import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion as Motion, AnimatePresence, useScroll, useTransform } from 'framer-motion';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Logo from '../components/Logo';
import CampaignCountdown from './campaigns/components/CampaignCountdown';
import SectionHeading from './campaigns/components/SectionHeading';
import MasonryGallery from '../components/MasonryGallery';
import HeroVideoWall from '../components/HeroVideoWall';
import HeroBrandMarquee from '../components/HeroBrandMarquee';
import HeroTapes from '../components/HeroTapes';
import { BRAND_LOGOS } from './data/brands';
import { StatItem } from '../components/Stats';
import { useCart } from '../hooks/useCart';
import useHeroWordFit from '../hooks/useHeroWordFit';
import { CRE8_APPS as PLAYGROUND, FEATURED_APPS, CRE8_EXTENSIONS } from './data/cre8Apps';
import { getFeaturedCampaigns } from './data/campaigns';
import AppCard from './components/AppCard';
import { smoothScrollTo, smoothScrollIntoView } from '../utils/smoothScroll';
import { RegistrationMark, CropMarks, IndexTag, CoordTag, LiveCoordTag, VerticalLabel, SectionCounter, FlowChain, HalftoneDots, ScannerLines, BarcodeMark, SectionFrame, GridOverlay, Crosshair, MetaBlock, TechnicalRule, TinyArrow, PageIndex, ThinRect, SystemMarker, GrainOverlay, Misregister } from '../components/swiss/SwissKit';
import AIVoiceAgent from './campaigns/AIVoiceAgent';
import InquiryModal from './booking/InquiryModal';
import RapidPrototyping from './campaigns/RapidPrototyping';
import ProcessPinnedSection from './ProcessPinnedSection';
import { alternateLangPath } from '../seo/routes';

import { 
  ArrowRight, 
  ArrowUpRight, 
  Menu, 
  X, 
  Zap, 
  Layers, 
  MessageSquare, 
  Quote,
  BarChart, 
  Target,
  Rocket,
  Code,
  Users, 
  User,
  Building,
  Calendar,
  AlertCircle,
  Info,
  Check,
  PenTool, 
  MousePointer2,
  Maximize2,
  Linkedin,
  Mail,
  Globe,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Hexagon,
  Layout,
  Smartphone,
  Box,
  Cpu,
  Mic,
  Clock,
  Database,
  Video,
  Film,
  Terminal,
  Sun,
  Moon,
  ShoppingBag,
  Play,
  Pause,
  Volume2,
  VolumeX,
  TrendingUp,
  ShieldCheck,
  PhoneCall,
  Headphones,
  Scissors,
  Home,
  UtensilsCrossed,
  Dumbbell,
  Bed,
  Flower2,
  Car,
  Stethoscope,
  Scale
} from 'lucide-react';

/* --------------------------------------------------------------------------------
   CONSTANTS & CONFIG
-------------------------------------------------------------------------------- */

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.12,
      delayChildren: 0.1
    }
  }
};

const fadeInLeft = {
  hidden: { opacity: 0, x: typeof window !== 'undefined' && window.innerWidth < 768 ? -20 : -60 },
  visible: { 
    opacity: 1, 
    x: 0,
    transition: { duration: 1.2, ease: [0.16, 1, 0.3, 1] }
  }
};

export const COLORS = {
  design: 'var(--design-primary)',
  ai: 'var(--ai-primary)',
  cre8guide: '#ef4444', // Red for Knowledge/Guide
  concierge: 'var(--concierge-primary)', // Theme-aware blue (lighter in dark mode)
  prototyping: 'var(--prototyping-primary)', // Theme-aware signal orange
  bg: 'var(--bg-color)',
  surface: 'var(--surface-color)',
  text: 'var(--text-color)',
};

// Bolds the "25+ years / 450+ projects / 60+ clients"-style credibility
// stats wherever they appear inside a hero description string, in either
// language — matches any "<number>+ <word>" pair (stopping before a
// trailing comma) and wraps it in font-black, leaving the rest of the
// sentence at its normal weight. Falls through untouched for descriptions
// that don't contain this pattern.
const highlightStatPhrases = (text) => {
  if (!text) return text;
  const parts = text.split(/(\d+\+\s+[^\s,]+)/g);
  if (parts.length === 1) return text;
  return parts.map((part, i) =>
    /^\d+\+\s+[^\s,]+$/.test(part)
      ? <strong key={i} className="font-black">{part}</strong>
      : <React.Fragment key={i}>{part}</React.Fragment>
  );
};

// Real, lang-prefixed paths for the main Design/AI/CRE8GUIDE tabs — the
// same convention already used for the 19 SEO solution subpages
// (/de|en/design/..., /de|en/ai/...).
export const TAB_PATHS = {
  design: { de: '/de/design', en: '/en/design' },
  ai: { de: '/de/ai', en: '/en/ai' },
  cre8guide: { de: '/de/cre8guide', en: '/en/cre8guide' },
  concierge: { de: '/de/concierge', en: '/en/concierge' },
  prototyping: { de: '/de/prototyping', en: '/en/prototyping' },
};

// The nav pill carries four tabs; at 375px only these shortened labels fit.
const TAB_LABELS_SHORT = {
  design: 'Design',
  ai: 'AI',
  concierge: 'Concierge',
  prototyping: 'Proto',
};

// Impressum renders as an overlay on top of this same LandingPage instance
// (App.jsx's view === 'impressum') — its real path is owned entirely by
// App.jsx, so the tab-driven effects below must never rewrite the address
// bar or canonical/hreflang tags while it's showing.
const isImpressumPath = () =>
  typeof window !== 'undefined' &&
  ['/de/impressum', '/en/impressum'].includes(window.location.pathname.replace(/\/$/, ''));

// Routes whose title/description App.jsx owns via PAGE_META. This component
// still renders behind them, so its per-tab metadata effect has to stand down —
// otherwise Impressum ends up carrying whichever tab title was active last
// (it was shipping the Rapid-Prototyping one), and CRE8APPS keeps the generic
// shell title. Both read to Google as duplicates of unrelated pages.
// Footer link to the current page's other-language URL — a plain <a>, so it is
// a crawlable path to the counterpart and passes link equity to it. Shared by
// this page's footer and SolutionPageTemplate's. Renders nothing on a path that
// has no alternate, rather than linking somewhere wrong.
const LANG_FLAGS = { de: '🇩🇪', en: '🇬🇧' };
// Page cross-fade on tab switches: fade out, swap the tab, fade back in.
const TAB_FADE_MS = 280;
const HERO_WORD_CLASS = 'absolute left-1/2 -translate-x-1/2 top-0 block origin-center whitespace-nowrap px-4 text-[1.08em] md:text-[1.4em] audiowide-regular font-normal';
// Tabs shown in the nav. Concierge is hidden for now; /concierge still works.
const NAV_TABS = ['design', 'ai', 'prototyping'];
// The "Current Campaign" (Concierge) banner section on the main page.
const SHOW_CAMPAIGN_BANNER = false;
const TAG_BASE =
  'text-[10px] font-bold uppercase tracking-[0.2em] px-3 py-1.5 border rounded-[4px] inline-flex items-center gap-2 transition-all duration-300';

export const LangAlternateLink = ({ lang, color }) => {
  const target = lang === 'de' ? 'en' : 'de';
  const href = typeof window === 'undefined'
    ? null
    : alternateLangPath(window.location.pathname, target);
  if (!href) return null;
  return (
    <div className="flex items-center gap-2">
      {/* Current language: a plain tag, not a link — nothing to navigate to. */}
      <span
        className={`${TAG_BASE} text-text-color border-text-color/40`}
        aria-current="true"
      >
        <span aria-hidden="true">{LANG_FLAGS[lang]}</span>
        {lang === 'de' ? 'Deutsch' : 'English'}
      </span>
      <a
        href={href}
        hrefLang={target}
        onClick={() => trackEvent('change_language', { language: target, source: 'footer' })}
        style={{ '--footer-hover-color': color }}
        className={`${TAG_BASE} text-text-muted border-border-color hover:text-[var(--footer-hover-color)] hover:border-[var(--footer-hover-color)]`}
      >
        <span aria-hidden="true">{LANG_FLAGS[target]}</span>
        {target === 'de' ? 'Deutsch' : 'English'}
      </a>
    </div>
  );
};

const PAGE_META_PATHS = ['/de/impressum', '/en/impressum', '/de/cre8apps', '/en/cre8apps'];
const ownsOwnMeta = () =>
  typeof window !== 'undefined' &&
  PAGE_META_PATHS.includes(window.location.pathname.replace(/\/$/, ''));

const getCampaignView = (campaignId) => {
  if (campaignId === 'vibe-coding') {
    return 'campaign-rapid-prototyping';
  }

  return `campaign-${campaignId}`;
};

export const trackEvent = (name, params = {}) => {
  if (typeof window !== 'undefined' && window.gtag) {
    // Only throttle automatic/scroll events like 'section_view'
    if (name === 'section_view') {
      const paramKey = JSON.stringify(params).replace(/[^a-zA-Z0-9]/g, '');
      const sessionKey = `tracked_${name}_${paramKey}`;
      
      if (!sessionStorage.getItem(sessionKey)) {
        window.gtag('event', name, params);
        sessionStorage.setItem(sessionKey, 'true');
      }
    } else {
      // For all manual clicks (buttons, tabs, etc.), fire every time
      window.gtag('event', name, params);
    }
  }
};

const GUIDE_ITEMS = [
  { id: 1, title: "CRE8GUIDE - Part 1/24 - Mindset eines Digital Designers", image: "https://cre8now.design/__guide/images/cre8guide-1.jpg", url: "https://cre8now.design/cre8guide-1.html" },
  { id: 2, title: "CRE8GUIDE - Part 2/24 - Psychologie im Design", image: "https://cre8now.design/__guide/images/cre8guide-2.jpg", url: "https://cre8now.design/cre8guide-2.html" },
  { id: 3, title: "CRE8GUIDE - Part 3/24 - Design Thinking", image: "https://cre8now.design/__guide/images/cre8guide-3.jpg", url: "https://cre8now.design/cre8guide-3.html" },
  { id: 4, title: "CRE8GUIDE - Part 4/24 - Die Macht der Farben", image: "https://cre8now.design/__guide/images/cre8guide-4.jpg", url: "https://cre8now.design/cre8guide-4.html" },
  { id: 5, title: "CRE8GUIDE - Part 5/24 - UX - Das Herzstück jedes Designs", image: "https://cre8now.design/__guide/images/cre8guide-5.jpg", url: "https://cre8now.design/cre8guide-5.html" },
  { id: 6, title: "CRE8GUIDE - Part 6/24 - UI - Das Gesicht der UX", image: "https://cre8now.design/__guide/images/cre8guide-6.jpg", url: "https://cre8now.design/cre8guide-6.html" },
  { id: 7, title: "CRE8GUIDE - Part 7/24 - IX - Interaktions Design", image: "https://cre8now.design/__guide/images/cre8guide-7.jpg", url: "https://cre8now.design/cre8guide-7.html" },
  { id: 8, title: "CRE8GUIDE - Part 8/24 - Responsive Design", image: "https://cre8now.design/__guide/images/cre8guide-8.jpg", url: "https://cre8now.design/cre8guide-8.html" },
  { id: 9, title: "CRE8GUIDE - Part 9/24 - Designsysteme für Konsistenz", image: "https://cre8now.design/__guide/images/cre8guide-9.jpg", url: "https://cre8now.design/cre8guide-9.html" },
  { id: 10, title: "CRE8GUIDE - Part 10/24 - Frontend vs. Backend", image: "https://cre8now.design/__guide/images/cre8guide-10.jpg", url: "https://cre8now.design/cre8guide-10.html" },
  { id: 11, title: "CRE8GUIDE - Part 11/24 - Barrierefreiheit im Digital Design", image: "https://cre8now.design/__guide/images/cre8guide-11.jpg", url: "https://cre8now.design/cre8guide-11.html" },
  { id: 12, title: "CRE8GUIDE - Part 12/24 - Informations-Architektur für klare Kommunikation", image: "https://cre8now.design/__guide/images/cre8guide-12.jpg", url: "https://cre8now.design/cre8guide-12.html" },
  { id: 13, title: "CRE8GUIDE - Part 13/24 - Visuelle Hierarchie", image: "https://cre8now.design/__guide/images/cre8guide-13.jpg", url: "https://cre8now.design/cre8guide-13.html" },
  { id: 14, title: "CRE8GUIDE - Part 14/24 - Mikrointeraktionen für den gewissen Unterschied", image: "https://cre8now.design/__guide/images/cre8guide-14.jpg", url: "https://cre8now.design/cre8guide-14.html" },
  { id: 15, title: "CRE8GUIDE - Part 15/24 - User Tests und Iteration", image: "https://cre8now.design/__guide/images/cre8guide-15.jpg", url: "https://cre8now.design/cre8guide-15.html" },
  { id: 16, title: "CRE8GUIDE - Part 16/24 - Typografie für digitale Medien", image: "https://cre8now.design/__guide/images/cre8guide-16.jpg", url: "https://cre8now.design/cre8guide-16.html" },
  { id: 17, title: "CRE8GUIDE - Part 17/24 - Mobile First Ansatz", image: "https://cre8now.design/__guide/images/cre8guide-17.jpg", url: "https://cre8now.design/cre8guide-17.html" },
  { id: 18, title: "CRE8GUIDE - Part 18/24 - Prototyping im Design-Prozess", image: "https://cre8now.design/__guide/images/cre8guide-18.jpg", url: "https://cre8now.design/cre8guide-18.html" },
  { id: 19, title: "CRE8GUIDE - Part 19/24 - UX Copywriting", image: "https://cre8now.design/__guide/images/cre8guide-19.jpg", url: "https://cre8now.design/cre8guide-19.html" },
  { id: 20, title: "CRE8GUIDE - Part 20/24 - Emotionen wecken mit Design", image: "https://cre8now.design/__guide/images/cre8guide-20.jpg", url: "https://cre8now.design/cre8guide-20.html" },
  { id: 21, title: "CRE8GUIDE - Part 21/24 - Agile Design Prozesse", image: "https://cre8now.design/__guide/images/cre8guide-21.jpg", url: "https://cre8now.design/cre8guide-21.html" },
  { id: 22, title: "CRE8GUIDE - Part 22/24 - Design für Conversion", image: "https://cre8now.design/__guide/images/cre8guide-22.jpg", url: "https://cre8now.design/cre8guide-22.html" },
  { id: 23, title: "CRE8GUIDE - Part 23/24 - Ethik im Digital Design", image: "https://cre8now.design/__guide/images/cre8guide-23.jpg", url: "https://cre8now.design/cre8guide-23.html" },
  { id: 24, title: "CRE8GUIDE - Part 24/24 - Digital Design für die Welt", image: "https://cre8now.design/__guide/images/cre8guide-24.jpg", url: "https://cre8now.design/cre8guide-24.html" },
];

const YOUTUBE_SHORTS = [
  { id: "VCDO6dFJIbw", title: "Zusammenarbeit schafft Innovation" },
  { id: "AQyCVBgEr6U", title: "Veränderung ist die einzige Konstante" },
  { id: "V9nmzfWxNtI", title: "Teilen stärkt die Gemeinschaft" },
  { id: "rU2d7GolNR4", title: "Kreativität ist Übungssache" },
  { id: "SrQB6sqaO68", title: "Lernen hört nie auf" },
  { id: "n5DfcinWvzI", title: "Kommunikation ist der Schlüssel" },
  { id: "NqhPGEm6S2c", title: "Sei offen für Wachstum" },
  { id: "-uaHtQo6tpM", title: "Ruhephasen steigern die Kreativität" },
  { id: "9af0QGkgwaw", title: "Selbstorganisation steigert Effizienz" },
  { id: "n96_Z0tnfLs", title: "UX beginnt mit Empathie" },
  { id: "ru41T5D_V9U", title: "Inspiration ist überall" },
  { id: "3ix1-ziWNgc", title: "Gutes Design ist unsichtbar" },
  { id: "HQ8wZEPMUf8", title: "Geduld bringt Ergebnisse" },
  { id: "ZzU5dbR0D2s", title: "Fehler sind wertvolle Lektionen" },
  { id: "ZY6tMaogNMM", title: "Weiterbildung ist Pflicht!" },
  { id: "Xo6eykXN2Zo", title: "Entscheidungen treffen ist eine Fähigkeit" },
  { id: "qXqzoBUVeMM", title: "Feedback ist ein Geschenk" },
  { id: "HuSbzbcqxx8", title: "Einfachheit ist der Schlüssel" },
  { id: "cTN3XqO_iJs", title: "Die Magie liegt in den Details" },
  { id: "lnboPzQKwKs", title: "Der Nutzer ist Massstab" },
  { id: "cRKVn9iGIe0", title: "Empathie öffnet Türen" }
];

const CAMPAIGNS = getFeaturedCampaigns({ Rocket, Code, Mic, Target });

export const TRANSLATIONS = {
  de: {
    nav: {
      design: "Design Studio",
      ai: "AI Lösungen",
      bookTermin: "Strategiegespräch",
      services: "Service",
      projects: "Lösungen",
      process: "Prozess",
      playground: "CRE8APPS",
      testimonials: "Testimonials",
      about: "Über uns",
      contact: "Kontakt",
      guide: "CRE8GUIDE",
      connect: "Connect",
      location: "Standort",
      remote: "Remote Weltweit",
      socials: "Socials",
      openForCollab: "Offen für neue Lösungen 2026",
      imprint: "IMPRESSUM",
      privacy: "DATENSCHUTZ"
    },
    cre8guide: {
      hero: {
        headline: ["WISSENS", "HUB"],
        words: ["WISSENSHUB", "AKADEMIE", "ABKÜRZUNGEN", "PERSPEKTIVE", "METHODIK", "EXZELLENZ"],
        description: "Dein Hub für Knowledge über Design & AI. Entdecke den Cre8Guide mit 24 Lektionen und lass dich von unseren täglichen Motivations-Shorts inspirieren.",
        cta: "Jetzt Entdecken"
      },
      academy: {
        label: "Cre8Guide Academy",
        title: "Design & AI",
        highlight: "CRE8GUIDE",
        description: "Konzentriertes Expertenwissen aus über 25 Jahren UX/UI Design & Digitaler Produktentwicklung."
      },
      onAir: {
        label: "On Air",
        title: "Motivation",
        highlight: "Shorts",
        description: "Tägliche Impulse für Kreativität, Mindset und persönliches Wachstum."
      },
      newsletter: {
        title: "Join the CRE8NOW Community",
        description: "Erhalte exklusive Insights, neue Cre8Guide Parts und KI-Updates direkt in dein Postfach.",
        placeholder: "Deine E-Mail Adresse",
        cta: "Jetzt anmelden"
      }
    },
    ai: {
      hero: {
        headline: ["Dein AI Studio für", "LÖSUNGEN"],
        words: ["LÖSUNGEN", "PIPELINES", "CONTENT", "WORKFLOWS", "AGENTEN", "STRATEGIE", "EVOLUTION", "SYSTEME"],
        description: "CRE8NOW hilft mittelständischen Unternehmen, wiederkehrende Prozesse zu automatisieren und operative Teams zu entlasten. Als führende AI-Agentur entwickelt CRE8NOW intelligente Automationen, KI-Content für Bild und Video, Content-Pipelines und Workflows sowie Trainings, die euer Wachstum auf Autopilot schalten.",
        wins: ["MEHR KUNDEN", "MEHR ZEIT", "MEHR UMSATZ", "WENIGER STRESS", "HAPPY CLIENTS", "HAPPY TEAMS"],
        marquee: ["ZUFRIEDENE TEAMS", "24/7 AUTOMATION", "STATE OF THE ART", "SCHNELLERE PROZESSE", "MESSBARER ROI", "WENIGER AUFWAND"],
        cta: "Kostenloses Erstgespräch"
      },
      services: {
        label: "KI Expertise",
        title: "KI Lösungen für",
        highlight: "AUTOMATION",
        description: "Als führende AI-Agentur aus Berlin liefert CRE8NOW schlüsselfertige KI-Lösungen und modernste Enterprise-AI-Modelle. CRE8NOW eliminiert Ineffizienzen durch intelligente KI-Automatisierung (Make, n8n) und entwickelt maßgeschneiderte LLM-Integrationen, RAG-Systeme und autonome AI Voice Agents, die messbaren ROI, maximale Skalierbarkeit und nachhaltige Wettbewerbsvorteile für euer Business sichern.",
        wins: ["MAXIMALER ROI", "24/7 AUTOMATION", "SKALIERBARE PROZESSE", "WETTBEWERBS-VORTEIL", "TOP KUNDEN-ERLEBNIS", "ZUKUNFTSSICHERHEIT"],
        items: [
          { title: "KI-Beratung", href: "/de/ai/ai-consulting", desc: "CRE8NOW analysiert eure bestehenden Prozesse und erstellt eine individuelle KI-Roadmap für erfolgreiche AI-Transformation.", tags: ["AI Audit", "KI-Strategie", "ROI Analyse", "Automatisierungspotenzial", "Digital Transformation", "Business-Growth"] },
          { title: "KI-Agenten", href: "/de/ai/ai-agents", desc: "Autonome AI-Agenten und intelligente Chatbots, die rund um die Uhr für euch arbeiten und Prozesse automatisieren.", tags: ["LLMs", "GPT", "Claude", "Gemini", "AI-Automatisierung", "24/7 Support", "Kosten-Effizienz", "Skalierbarkeit", "Smart Chatbots"] },
          { title: "Digital Concierge", href: "/de/ai/digital-concierge", desc: "Natürliche Sprachinteraktion und intelligente Voice-Agents / Telefonie-Agenten für modernen Kundenservice und AI-gestützten Vertrieb.", tags: ["Voice AI", "Conversational AI", "Telefonie-Automatisierung", "Lead-Qualifizierung", "Human-like Speech", "Sales-Booster"] },
          { title: "KI-Pipelines", href: "/de/ai/ai-pipelines", desc: "Intelligente AI-Pipelines und automatisierte Content-Generierung, die repetitive Aufgaben eliminieren und Effizienz steigern.", tags: ["Weavy", "Higgsfield", "n8n", "Make", "Zeit-Ersparnis", "Prozess-Optimierung", "Low-Code"] },
          { title: "AI Content", href: "/de/ai/ai-content", desc: "Generiert professionelle KI-Videos, AI-generierte Bilder und optimierte Texte in Rekordzeit mit modernsten AI-Tools.", tags: ["Generative KI", "AI-Content", "Brand-Konsistenz", "Schnelle Produktion", "Visual AI", "Marketing-Asset"] },
          { title: "KI-Workshops", href: "/de/ai/ai-workshops", desc: "Macht euer Team fit für die KI-Ära mit praxisorientierten AI-Workshops und Prompt Engineering Trainings.", tags: ["AI Training", "Prompt Engineering", "Team-Enablement", "KI-Mindset", "Knowledge-Transfer", "Innovation"] },
          { title: "KI-Film Produktion", href: "/de/ai/ai-film-production", desc: "Produziert cineastische AI-generierte Commercials und Social Media Content mit neuesten KI-Tools und AI-Workflows.", tags: ["AI Commercials", "KI-Videos", "Production-Speed", "Cineastischer Look", "Marketing-Boost", "Video-Strategy"] },
          { title: "Custom AI-Entwicklung", href: "/de/ai/custom-ai-development", desc: "Maßgeschneiderte KI-Lösungen und AI-Systeme, nahtlos integriert in eure bestehende Infrastruktur.", tags: ["Custom AI", "API Integration", "Unternehmensdaten", "Datenschutz-konform", "Individual-Software", "Deep-Tech"] },
          { title: "AI Voice & Image Cloning", href: "/de/ai/ai-voice-cloning", desc: "Professionelle Voice-Clones und Image-Clones für skalierbare Brand-Kommunikation mit künstlicher Intelligenz.", tags: ["Voice Clone", "AI Clone", "Personalisierung", "Digitale Zwillinge", "Brand-Voice", "Scalable-Media"] }
        ]
      },
      process: {
        label: "AI Implementierung",
        title: "Der effiziente KI ",
        highlight: "PROZESS",
        description: "Der CRE8NOW AI-Prozess verbindet technische Tiefe mit strategischer Klarheit. Ob KI-Produkt, AI-optimierte Website oder automatisierter Funnel: CRE8NOW entwickelt eure Lösung von Audit bis Launch und integriert sie nahtlos in euer digitales Ökosystem.",
        steps: [
          { step: "01", title: "Audit & Analyse", text: "CRE8NOW führt ein umfassendes technisches AI-Audit eurer bestehenden Infrastruktur durch, identifiziert Bottlenecks, bewertet das KI-Automatisierungspotenzial und analysiert optimale AI-Integration in eure Geschäftsprozesse." },
          { step: "02", title: "Planung & Strategie", text: "Basierend auf den AI-Audit-Ergebnissen entwickelt CRE8NOW eine dedizierte KI-Roadmap und maßgeschneiderte AI-Strategie, definiert die optimale Architektur der AI-Agenten und wählt die passenden LLMs und KI-Modelle für eure spezifischen Anforderungen aus." },
          { step: "03", title: "Bau & Entwicklung", text: "In dieser Phase erwachen die KI-Agenten zum Leben. CRE8NOW programmiert autonome AI-Workflows mit führenden Tools wie n8n oder Make und integriert modernste künstliche Intelligenz in eure Systeme." },
          { step: "04", title: "Setup & Start", text: "Die KI-Systeme und AI-Agenten werden nahtlos in eure Live-Umgebung integriert. CRE8NOW führt praxisorientierte AI-Workshops und Onboarding-Sessions für euer Team durch, um maximale Adoption sicherzustellen." },
          { step: "05", title: "Skalierung & Support", text: "Künstliche Intelligenz ist kein statisches Produkt. Kontinuierliche Optimierung ist essentiell: CRE8NOW überwacht die Performance der KI-Agenten, verfeinert die AI-Prompts kontinuierlich und skaliert eure AI-Lösungen für maximalen ROI." }
        ]
      }
    },
    design: {
      hero: {
        headline: ["Dein Design Studio für", "EXZELLENZ"],
        words: ["PRODUKTE", "LÖSUNGEN", "DESIGNS", "VISIONEN", "ERLEBNISSE", "SYSTEME"],
        description: "CRE8NOW hilft wachstumsstarken Marken, komplexe digitale Produkte verständlicher zu machen und mehr Besucher in Kunden zu verwandeln. Mit 25+ Jahren Erfahrung, 450+ Projekten und 60+ Kunden entwickelt CRE8NOW konversionsstarke UX/UI Designs, die euer Wachstum beschleunigen.",
        marquee: ["ZUFRIEDENE KUNDEN", "SCHNELLE PRODUKTE", "STATE OF THE ART", "PRÄZISES DESIGN", "MESSBARES WACHSTUM", "STARKE MARKEN"],
        cta: "Kostenloses Erstgespräch"
      },
      services: {
        label: "Design Expertise",
        title: "High Performance Design für",
        highlight: "CONVERSION",
        description: "CRE8NOW gestaltet digitale Produkte, die komplexe Anforderungen verständlicher und einfacher nutzbar machen. Als führende Design-Agentur für digitales Wachstum, UX/UI Design und Conversion-Rate-Optimierung (CRO) transformiert CRE8NOW komplexe Anforderungen in intuitive SaaS-Plattformen, responsive Webflow/Framer Websites und marktführende mobile Apps, die Vertrauen aufbauen und Besucher in loyale Kunden verwandeln. Durch modernste Figma Design-Systeme und psychologisch fundierte UX maximiert CRE8NOW euren digitalen Erfolg.",
        wins: ["MAXMALE CONVERSION", "STÄRKERE BRAND", "OPTIMALE UX", "SCHNELLERER LAUNCH", "TOP USER-TRUST", "BESSERE RETENTION"],
        items: [
          { title: "Consulting", href: "/de/design/consulting", desc: "Professionelle UX Audits und Design-Analysen identifizieren Schwachstellen und ungenutztes Optimierungspotenzial eurer digitalen Produkte.", tags: ["UX Expert Review", "Design-Strategie", "Conversion-Audit", "User-Insights", "Brand-Alignment", "Performance"] },
          { title: "UX Design", href: "/de/design/ux-design", desc: "CRE8NOW gestaltet digitale Produkte, die eure Nutzer wirklich lieben. Durch fundiertes Research entstehen intuitive und nahtlose Erlebnisse.", tags: ["User Research", "UX Personas", "Journey Mapping", "Problem-Solving", "User-Centric", "Behavior-Analysis"] },
          { title: "UI Design", href: "/de/design/ui-design", desc: "Visuelles Interface-Design, das eure Brand stärken, Vertrauen aufbauen und ein konsistentes Erscheinungsbild schaffen.", tags: ["Visual Design", "Interface Design", "Marken-Identität", "Design System", "Visual-Hierarchy"] },
          { title: "IX Design", href: "/de/design/ix-design", desc: "Durchdachte Micro-Interactions und interaktive Details, die digitale Produkte lebendig und intuitiv bedienbar machen.", tags: ["Micro-Interactions", "Motion Design", "Interactive", "Prototyping", "Feedback-States"] },
          { title: "Strategy", href: "/de/design/strategy", desc: "Ganzheitliche Konzeption, strategische User Journeys und präzise Informations-Architekturen für den optimalen Grundstein digitaler Produkte.", tags: ["User Journeys", "Konzeption", "Informations-Architektur", "Strategie", "Produkt-Vision"] },
          { title: "Prototyping", href: "/de/design/prototyping", desc: "Interaktive Prototypen und klickbare Wireframes für Web-Apps und native Apps für frühzeitiges User-Feedback und validierte UX-Konzepte.", tags: ["Web-Apps", "Native Apps", "Prototyping", "Nutzer-Tests", "MVP-Validierung", "Rapid-Dev"] },
          { title: "SEO / GEO", href: "/de/design/seo", desc: "Nachhaltige Suchmaschinenoptimierung (SEO) und zukunftsweisende Search Generative Experience (SGE / GEO) Strategien für maximale organische Sichtbarkeit.", tags: ["SEO", "GEO", "SGE", "Search Experience", "Organic Growth", "Google"] },
          { title: "Entwicklung", href: "/de/design/development", desc: "Schneller Launch in Wochen statt Monaten mit professioneller Webflow & Framer Entwicklung und modernen No-Code-Tools.", tags: ["Webflow", "No-Code", "Fast-to-Market", "Responsive Design", "SEO-ready", "Framer"] },
          { title: "Social Ads", href: "/de/design/social-ads", desc: "Konzeptionsstarke und performance-orientierte Social Media Ads, die eure Zielgruppe fesseln und die Klickraten maximieren.", tags: ["Meta", "TikTok", "Google", "Social Ads", "Performance Marketing", "Visual Design", "Motion Graphics", "Campaigns", "CTR Boost"] },
          { title: "Trainings", href: "/de/design/trainings", desc: "Praxisnahe kreative Workshops und Trainings für Figma, Webflow und moderne Design-Workflows für euer Team.", tags: ["Figma Training", "UX Workshop", "Creative Thinking", "Design-Sprint", "Team-Coaching", "Collaboration"] }
        ]
      },
      process: {
        label: "Design Prozess",
        title: "Optimierter Design ",
        highlight: "WORKFLOW",
        description: "Der CRE8NOW Design-Prozess bringt digitale Lösungen für eure Zielgruppe von der Idee zur Marktreife: nutzerzentriert, datengetrieben und mit klarem Fokus auf Conversion-Optimierung und nachhaltiges Wachstum.",
        steps: [
          { step: "01", title: "Analyse & Research", text: "CRE8NOW taucht tief in euer Business-Modell und eure Zielgruppe ein. Durch fundiertes User Research, Stakeholder-Interviews und umfassende Competitor-Analysen entsteht die datenbasierte Basis für alle strategischen Design-Entscheidungen." },
          { step: "02", title: "Konzept & Flow", text: "CRE8NOW übersetzt Research-Erkenntnisse in durchdachte Informationsarchitektur. Es entstehen detaillierte User Flows, strategische Wireframes und UX-Konzepte, die den logischen Aufbau eures digitalen Produkts definieren." },
          { step: "03", title: "Design & System", text: "Hier entsteht die visuelle Identität eures digitalen Produkts. CRE8NOW entwirft ein skalierbares Design-System mit wiederverwendbaren UI-Komponenten für maximale Konsistenz und effiziente Entwicklung." },
          { step: "04", title: "Bau & Start", text: "CRE8NOW übergibt nicht nur statische Design-Files, sondern implementiert das UX/UI Design professionell in Webflow oder Framer oder begleitet eure Development-Teams mit detaillierten Design Handoffs." },
          { step: "05", title: "Test & Optimierung", text: "Nach dem Launch ist vor der kontinuierlichen Optimierung. CRE8NOW nutzt User Analytics, Heatmaps und qualitatives User-Feedback, um das Produkt iterativ zu verbessern und Conversion-Rates zu steigern." }
        ]
      }
    },
    common: {
      portfolio: {
        label: "Portfolio",
        title: "Creative Design",
        highlight: "Lösungen",
        description: "Jedes Projekt hier wurde gebaut, um zu performen. CRE8NOW entwickelt konversionsstarke Landingpages, moderne Websites, digitale Produkte und KI-gestützte Lösungen, die messbare Ergebnisse liefern. Keine Konzept-Slides, sondern echte Live-Produkte für echtes Wachstum.",
        projects: [
          { title: "Demo Reel", category: "CRE8NOW", tags: ["UI Design", "Interaction Design", "Development"] },
          { title: "Helium Film & AI Production", category: "Film & AI", tags: ["UI Design", "Interaction Design", "Development"] },
          { title: "DRIIIV", category: "No Agency", tags: ["Interaction Design", "Development", "Consulting"] },
          { title: "Valet-Mobility", category: "Mobility", tags: ["UX Design", "UI Design", "Development", "Performance"] },
          { title: "Bewerbung AI", category: "Platform", tags: ["UX Design", "UI Design", "SEO"] },
          { title: "BlueLake Strategy", category: "Consulting", tags: ["UX Design", "Consulting", "Development", "SEO"] }
        ]
      },
      playground: {
        label: "CRE8APPS",
        title: "CRE8NOW Baut",
        highlight: "Digitale Produkte",
        description: "CRE8NOW entwickelt eigene digitale Produkte: von AI-Tools und Web-Apps bis zu Rapid Prototypes und skalierbaren Software-Produkten. CRE8NOW baut, was es Kunden empfiehlt: schnell marktreife Produkte mit echtem Business-Impact.",
        wins: ["RAPID PROTOTYPING", "PRODUCT DEVELOPMENT", "WEB & MOBILE APPS", "AI-POWERED", "FAST TO MARKET", "MADE IN BERLIN"],
        viewProject: "Projekt ansehen",
        items: [
          { title: "FigMagic", desc: "Automatisiert euren Figma Workflow mit KI-gestützten Komponenten und intelligenten Design-Tools.", tags: ["AI-Produkt", "Figma"] },
          { title: "Midprompts", desc: "Das ultimative AI Prompt Engineering Tool für Midjourney und generative KI-Bildgenerierung.", tags: ["KI-Tool", "AI-Medien"] },
          { title: "CRE8\nLab", desc: "Experimentelle KI-Agenten und AI-Workflows für kreative Taskforces und innovative Teams.", tags: ["AI-Forschung", "KI-Agenten"] },
          { title: "Audio\nBuster", desc: "Revolutionäre KI-Audio-Plattform für professionelles Sound-Design und intelligente Voice-Cloning-Workflows.", tags: ["AI-Audio", "KI-Medien"] },
          { title: "Brand\nEngine", desc: "Skalierbare Markenidentitäten in Sekunden durch künstliche Intelligenz und generative Assets.", tags: ["AI-Branding", "Design"] },
          { title: "Motion\nMint", desc: "Generative Motion-Designs und Web-Animationen für dynamische digitale Produkte.", tags: ["AI-Motion", "Interaktiv"] },
          { title: "Code\nCatalyst", desc: "KI-gestützte Code-Generierung und automatisiertes Refactoring für moderne Frontend-Stacks.", tags: ["AI-Dev", "Automatisierung"] },
          { title: "UX\nOracle", desc: "Intelligente UX-Analysen und Heatmap-Vorhersagen auf Basis von KI-gestützten Modellen.", tags: ["AI-UX", "Forschung"] }
        ]
      },
      about: {
        label: "Über den Gründer",
        bio: "Mit über 25 Jahren Erfahrung als UX/UI Designer und AI Artist verbindet Pascal Hoffmann kreative Exzellenz mit technologischer Innovation. Als Gründer von CRE8NOW führt er ein exzellentes Netzwerk aus spezialisierten Experten für Design und künstliche Intelligenz, um für jedes Projekt die optimale Taskforce aus AI-Agenten und menschlicher Genialität zu mobilisieren.",
        stats: [
          { label: "Jahre Erfahrung", value: "25+" },
          { label: "Marken", value: "40+" },
          { label: "Projekte", value: "450+" }
        ]
      },
      step: "Schritt",
      contact: {
        title: "ZEIT FÜR",
        highlight: "CRE8NOW",
        description: "Bereit für eine digitale Lösung, die eure Zielgruppe wirklich erreicht? Starte mit CRE8NOW dein nächstes digitales Projekt."
      },
      testimonials: [
        {
          quote: "Ein großes Lob an @Pascal. Das Design für dieses Release war absolut erstklassig – ohne dich hätten wir das Projekt nicht so erfolgreich starten können. Großartige Umsetzung, klare Richtung und ein extrem durchdachter Ansatz für die User Experience. Du hast die perfekte Balance zwischen Kreativität und Usabilität gefunden.",
          author: "Saif",
          role: "CTO BEI BEWERBUNG.AI"
        },
        {
          quote: "Die Zusammenarbeit mit CRE8NOW hat unsere Produktstrategie grundlegend verändert. Ihre KI-Expertise kombiniert mit Weltklasse-Design ist in der Branche selten zu finden. Die Ergebnisse haben alle Erwartungen übertroffen und lieferten bereits wenige Wochen nach dem Launch messbaren Mehrwert.",
          author: "Elena",
          role: "GRÜNDERIN VON NEXUS-TECH"
        },
        {
          quote: "Massives Lob an Pascal für die erstklassige Umsetzung der DRIIIV-Website. Schnell, flüssig und technisch makellos. Pascal war der stille Wächter unserer CI und hat uns immer wieder auf die richtige Spur gebracht, wenn wir uns in Details verloren haben. Kluge Entscheidungen, tolles Timing, null Drama. So muss sich digitale Zusammenarbeit anfühlen. Massiven Respekt.",
          author: "Robert Jung",
          role: "DRIIIV.COM / EX GLOBAL EXECUTIVE CREATIVE DIRECTOR PORSCHE"
        }
      ]
    }
  },
  en: {
    nav: {
      design: "Design Studio",
      ai: "AI Solutions",
      bookTermin: "Orientation Call",
      services: "Service",
      projects: "Solutions",
      process: "Process",
      playground: "CRE8APPS",
      testimonials: "Testimonials",
      about: "About",
      contact: "Contact",
      guide: "CRE8GUIDE",
      connect: "Connect",
      location: "Location",
      remote: "Remote Worldwide",
      socials: "Socials",
      openForCollab: "Open for new solutions 2026",
      imprint: "IMPRINT",
      privacy: "PRIVACY"
    },
    cre8guide: {
      hero: {
        headline: ["KNOWLEDGE", "HUB"],
        words: ["KNOWLEDGE", "ACADEMY", "SHORTCUTS", "PERSPECTIVE", "METHODOLOGY", "EXCELLENCE"],
        description: "Your hub for knowledge about Design & AI. Discover the Cre8Guide with 24 lessons and get inspired by our daily motivation shorts.",
        cta: "Explore Now"
      },
      academy: {
        label: "Cre8Guide Academy",
        title: "Design & AI",
        highlight: "CRE8GUIDE",
        description: "Condensed expert knowledge from over 25 years of UX/UI design & digital product development."
      },
      onAir: {
        label: "On Air",
        title: "Motivation",
        highlight: "Shorts",
        description: "Daily impulses for creativity, mindset, and personal growth."
      },
      newsletter: {
        title: "Join the CRE8NOW Community",
        description: "Get exclusive insights, new Cre8Guide parts, and AI updates directly to your inbox.",
        placeholder: "Your email address",
        cta: "Sign up now"
      }
    },
    ai: {
      hero: {
        headline: ["Your AI Studio for", "SOLUTIONS"],
        words: ["SOLUTIONS", "PIPELINES", "CONTENT", "WORKFLOWS", "AGENTS", "STRATEGY", "EVOLUTION", "SYSTEMS"],
        description: "CRE8NOW helps mid-market companies automate repetitive processes and reduce the operational workload on their teams. As a leading AI Agency, CRE8NOW builds intelligent automation, AI content for image and video, content pipelines and workflows, plus trainings that put your growth on autopilot.",
        marquee: ["HAPPY TEAMS", "24/7 AUTOMATION", "STATE OF THE ART", "FASTER WORKFLOWS", "MEASURABLE ROI", "LESS OPERATIONAL LOAD"],
        cta: "Free Consultation"
      },
      services: {
        label: "AI Expertise",
        title: "AI Solutions for",
        highlight: "AUTOMATION",
        description: "As Berlin’s premier AI Agency, CRE8NOW delivers cutting-edge artificial intelligence, autonomous workflow automation, and custom LLM solutions. CRE8NOW eliminates inefficiencies via advanced AI automation (Make, n8n), deploys intelligent voice agents, and designs custom RAG systems that secure measurable ROI, hyper-scalability, and lasting competitive edges.",
        wins: ["MAXIMUM ROI", "24/7 AUTOMATION", "SCALABLE PROCESSES", "COMPETITIVE EDGE", "TOP USER EXPERIENCE", "FUTURE PROOFING"],
        items: [
          { title: "AI Consulting", href: "/en/ai/ai-consulting", desc: "CRE8NOW analyzes your existing processes and creates an individual AI roadmap for successful artificial intelligence transformation.", tags: ["AI Audit", "AI Strategy", "ROI Analysis", "Automation Potential", "Digital Transformation", "Business-Growth"] },
          { title: "AI Agents", href: "/en/ai/ai-agents", desc: "Autonomous AI agents and intelligent chatbots that work 24/7 for you and automate your business processes.", tags: ["LLMs", "GPT", "Claude", "Gemini", "AI Automation", "24/7 Support", "Cost Efficiency", "Scalability", "Smart Chatbots"] },
          { title: "Digital Concierge", href: "/en/ai/digital-concierge", desc: "Natural language interaction and intelligent voice agents for modern customer service and AI-powered sales solutions.", tags: ["Voice AI", "Conversational AI", "Telephony Automation", "Lead Qualification", "Human-like Speech", "Sales-Booster"] },
          { title: "AI Pipelines", href: "/en/ai/ai-pipelines", desc: "Intelligent AI pipelines and automated content generation that eliminate repetitive tasks and increase efficiency.", tags: ["Weavy", "Higgsfield", "n8n", "Make", "Time Savings", "Process Optimization", "Low-Code"] },
          { title: "AI Content", href: "/en/ai/ai-content", desc: "Generate professional AI videos, AI-generated images, and optimized content in record time with state-of-the-art AI tools.", tags: ["Generative AI", "AI Content", "Brand Consistency", "Rapid Production", "Visual AI", "Marketing-Asset"] },
          { title: "AI Workshops", href: "/en/ai/ai-workshops", desc: "Prepare your team for the AI era with hands-on AI workshops and prompt engineering training sessions.", tags: ["AI Training", "Prompt Engineering", "Team Enablement", "AI Mindset", "Knowledge Transfer", "Innovation"] },
          { title: "AI Film Production", href: "/en/ai/ai-film-production", desc: "Produce cinematic AI-generated commercials and social media content with latest AI tools and intelligent workflows.", tags: ["AI Commercials", "AI Videos", "Production Speed", "Cinematic Look", "Marketing Boost", "Video-Strategy"] },
          { title: "Custom AI Development", href: "/en/ai/custom-ai-development", desc: "Tailored AI solutions and intelligent systems, seamlessly integrated into your existing infrastructure.", tags: ["Custom AI", "API Integration", "Proprietary Data", "GDPR Compliant", "Bespoke Software", "Deep-Tech"] },
          { title: "AI Voice & Image Cloning", href: "/en/ai/ai-voice-cloning", desc: "Professional voice clones and image clones for scalable brand communication powered by artificial intelligence.", tags: ["Voice Clone", "AI Clone", "Personalization", "Digital Twins", "Brand Voice", "Scalable-Media"] }
        ]
      },
      process: {
        label: "AI Implementation",
        title: "The Efficient AI ",
        highlight: "PROCESS",
        description: "The CRE8NOW AI process combines technical depth with strategic clarity. Whether it's an AI product, an AI-optimized website, or an automated funnel: CRE8NOW takes your solution from audit to launch and integrates it seamlessly into your digital ecosystem.",
        steps: [
          { step: "01", title: "Audit & Analysis", text: "CRE8NOW conducts a comprehensive technical AI audit of your existing infrastructure, identifies bottlenecks, evaluates AI automation potential, and analyzes optimal AI integration into your business processes." },
          { step: "02", title: "Strategy & Plan", text: "Based on the AI audit results, CRE8NOW develops a dedicated AI roadmap and tailored artificial intelligence strategy, defines the optimal architecture for AI agents, and selects the right LLMs and AI models for your specific requirements." },
          { step: "03", title: "Build & Develop", text: "In this phase, AI agents come to life. CRE8NOW programs autonomous AI workflows with leading tools like n8n or Make and integrates state-of-the-art artificial intelligence into your systems." },
          { step: "04", title: "Setup & Launch", text: "AI systems and intelligent agents are seamlessly integrated into your live environment. CRE8NOW conducts hands-on AI workshops and onboarding sessions for your team to ensure maximum adoption." },
          { step: "05", title: "Scale & Support", text: "Artificial intelligence is not a static product. Continuous optimization is essential: CRE8NOW monitors AI agent performance, continuously refines AI prompts, and scales your AI solutions for maximum ROI." }
        ]
      }
    },
    design: {
      hero: {
        headline: ["Your Design Studio for", "EXCELLENCE"],
        words: ["EXCELLENCE", "SOLUTIONS", "DESIGNS", "VISIONS", "EXPERIENCES", "SYSTEMS"],
        description: "CRE8NOW helps growth-focused brands make complex digital products easier to use and turn more visitors into customers. With 25+ years of experience, 450+ projects, and 60+ clients, CRE8NOW delivers high-converting UX/UI design that accelerates your growth.",
        marquee: ["HAPPY CLIENTS", "FAST PRODUCTS", "STATE OF THE ART", "PRECISION DESIGN", "MEASURABLE GROWTH", "STRONGER BRANDS"],
        cta: "Free Consultation"
      },
      services: {
        label: "Design Expertise",
        title: "High Performance Design for",
        highlight: "CONVERSION",
        description: "CRE8NOW designs digital products that make complex requirements easier to understand and use. As a leading Design Agency for digital growth, elite UX/UI design, and Conversion Rate Optimization (CRO), CRE8NOW transforms complex requirements into intuitive SaaS interfaces, responsive Webflow/Framer websites, and market-dominating mobile apps that build trust and convert visitors. Leveraging state-of-the-art Figma design systems and psychology-driven UX, CRE8NOW maximizes your digital success.",
        wins: ["MAXIMUM CONVERSION", "STRONGER BRAND", "OPTIMAL UX", "FASTER LAUNCH", "TOP USER TRUST", "BETTER RETENTION"],
        items: [
          { title: "Consulting", href: "/en/design/consulting", desc: "Professional UX audits and design analyses identify weaknesses and untapped optimization potential in your digital products.", tags: ["UX Expert Review", "Design Strategy", "Conversion Audit", "User Insights", "Brand Alignment", "Performance"] },
          { title: "UX Design", href: "/en/design/ux-design", desc: "CRE8NOW designs digital products that your users truly love. Based on deep user research, CRE8NOW builds intuitive and seamless experiences.", tags: ["User Research", "UX Personas", "Journey Mapping", "Problem Solving", "User Centric", "Behavior-Analysis"] },
          { title: "UI Design", href: "/en/design/ui-design", desc: "Visual interface design that strengthens your brand, builds trust, and creates a consistent look and feel.", tags: ["Visual Design", "Interface Design", "Brand Identity", "Design System", "Visual-Hierarchy"] },
          { title: "IX Design", href: "/en/design/ix-design", desc: "Considered micro-interactions and interactive details that make digital products feel alive and intuitive to use.", tags: ["Micro-Interactions", "Motion Design", "Interactive", "Prototyping", "Feedback-States"] },
          { title: "Strategy", href: "/en/design/strategy", desc: "Holistic planning, strategic user journeys, and precise information architecture designed to establish a solid foundation for digital products.", tags: ["User Journeys", "Concept", "Information Architecture", "Strategy", "Product Vision"] },
          { title: "Prototyping", href: "/en/design/prototyping", desc: "Interactive prototypes and clickable wireframes for web apps and native apps to enable early user feedback and validate UX concepts.", tags: ["Web Apps", "Native Apps", "Prototyping", "User Testing", "MVP Validation", "Rapid-Dev"] },
          { title: "SEO / GEO", href: "/en/design/seo", desc: "Sustainable search engine optimization (SEO) and next-generation Search Generative Experience (SGE / GEO) strategies for maximum organic visibility.", tags: ["SEO", "GEO", "SGE", "Search Experience", "Organic Growth", "Google"] },
          { title: "Development", href: "/en/design/development", desc: "Rapid launch in weeks instead of months with professional Webflow & Framer development and modern no-code tools.", tags: ["Webflow", "No-Code", "Fast-to-Market", "Responsive Design", "SEO Ready", "Framer"] },
          { title: "Social Ads", href: "/en/design/social-ads", desc: "High-converting, performance-driven social media ads designed to captivate your audience and maximize click-through rates.", tags: ["Meta", "TikTok", "Google", "Social Ads", "Performance Marketing", "Visual Design", "Motion Graphics", "Campaigns", "CTR Boost"] },
          { title: "Trainings", href: "/en/design/trainings", desc: "Hands-on creative workshops and training sessions covering Figma, Webflow, and modern design workflows for your team.", tags: ["Figma Training", "UX Workshop", "Creative Thinking", "Design Sprint", "Team Coaching", "Collaboration"] }
        ]
      },
      process: {
        label: "Design Process",
        title: "Optimized Design",
        highlight: "WORKFLOW",
        description: "The CRE8NOW design process takes digital solutions for your target audience from concept to market: user-centered, data-driven, with a clear focus on conversion optimization and sustainable growth.",
        steps: [
          { step: "01", title: "Analyse & Research", text: "CRE8NOW dives deep into your business model and target audience. Through solid user research, stakeholder interviews, and comprehensive competitor analyses, CRE8NOW lays the data-driven foundation for all strategic design decisions." },
          { step: "02", title: "Concept & Flow", text: "CRE8NOW translates research insights into thoughtful information architecture. Detailed user flows, strategic wireframes, and UX concepts emerge that define the logical structure of your digital product." },
          { step: "03", title: "Design & System", text: "This is where the visual identity of your digital product is born. CRE8NOW designs a scalable design system with reusable UI components for maximum consistency and efficient development." },
          { step: "04", title: "Build & Launch", text: "CRE8NOW doesn't just hand over static design files. It professionally implements the UX/UI design in Webflow or Framer, or supports your development teams with detailed design handoffs." },
          { step: "05", title: "Test & Optimize", text: "After launch is before continuous optimization. CRE8NOW uses user analytics, heatmaps, and qualitative user feedback to iteratively improve the product and increase conversion rates." }
        ]
      }
    },
    common: {
      portfolio: {
        label: "Portfolio",
        title: "Creative Design",
        highlight: "Solutions",
        description: "Every project you see here was built to perform. CRE8NOW delivers high-converting landing pages, modern websites, custom digital products, and AI-powered solutions that drive measurable results. These aren't concept slides. These are live products built for real growth.",
        projects: [
          { title: "Demo Reel", category: "CRE8NOW", tags: ["UI Design", "Interaction Design", "Development"] },
          { title: "Helium Film & AI Production", category: "Film & AI", tags: ["UI Design", "Interaction Design", "Development"] },
          { title: "DRIIIV", category: "No Agency", tags: ["Interaction Design", "Development", "Consulting"] },
          { title: "Valet-Mobility", category: "Mobility", tags: ["UX Design", "UI Design", "Development", "Performance"] },
          { title: "Bewerbung AI", category: "Platform", tags: ["UX Design", "UI Design", "SEO"] },
          { title: "BlueLake Strategy", category: "Consulting", tags: ["UX Design", "Consulting", "Development", "SEO"] }
        ]
      },
      playground: {
        label: "CRE8APPS",
        title: "CRE8NOW Builds",
        highlight: "Digital Products",
        description: "CRE8NOW builds its own digital products: from AI tools and web apps to rapid prototypes and scalable software products. CRE8NOW builds what it recommends to clients: products that reach the market fast and create real business impact.",
        wins: ["RAPID PROTOTYPING", "PRODUCT DEVELOPMENT", "WEB & MOBILE APPS", "AI-POWERED", "FAST TO MARKET", "MADE IN BERLIN"],
        viewProject: "View Project",
        items: [
          { title: "FigMagic", desc: "Automate your Figma workflow with AI-powered components and intelligent design tools.", tags: ["AI Product", "Figma"] },
          { title: "Midprompts", desc: "The ultimate AI prompt engineering tool for Midjourney and generative AI image generation.", tags: ["AI Tool", "AI Media"] },
          { title: "CRE8\nLab", desc: "Experimental AI agents and intelligent workflows for creative taskforces and innovative teams.", tags: ["AI Research", "AI Agents"] },
          { title: "Audio\nBuster", desc: "Revolutionary AI audio platform for professional sound design and intelligent voice cloning workflows.", tags: ["AI Audio", "AI Media"] },
          { title: "Brand\nEngine", desc: "Scalable brand identities in seconds through AI and generative assets.", tags: ["AI Branding", "Design"] },
          { title: "Motion\nMint", desc: "Generative motion designs and web animations for dynamic digital products.", tags: ["AI Motion", "Interactive"] },
          { title: "Code\nCatalyst", desc: "AI-powered code generation and automated refactoring for modern frontend stacks.", tags: ["AI Dev", "Automation"] },
          { title: "UX\nOracle", desc: "Intelligent UX analysis and heatmap predictions based on AI-powered models.", tags: ["AI UX", "Research"] }
        ]
      },
      about: {
        label: "Founder & Creative Director",
        bio: "With over 25 years of experience as a UX/UI designer and AI artist, Pascal Hoffmann combines creative excellence with technological innovation. As the founder of CRE8NOW, he leads an elite network of specialists in Design and AI, mobilizing the perfect task force of AI agents and human brilliance for every project.",
        stats: [
          { label: "Years Experience", value: "25+" },
          { label: "Brands", value: "40+" },
          { label: "Projects", value: "450+" }
        ]
      },
      step: "Step",
      contact: {
        title: "TIME FOR",
        highlight: "CRE8NOW",
        description: "Ready for a digital solution that actually reaches your target audience? Start your next digital project with CRE8NOW."
      },
      testimonials: [
        {
          quote: "I want to give a special shoutout to @Pascal. The design work on this release was next-level seriously couldn't have kicked things off without you. Amazing execution, great direction, and a super thoughtful approach to the user experience. You nailed the balance between creativity and usability really appreciate the craft",
          author: "Saif",
          role: "CTO AT BEWERBUNG.AI"
        },
        {
          quote: "Working with CRE8NOW transformed our product strategy. Their AI expertise combined with world-class design is a rare find in the industry. The results exceeded all expectations and delivered tangible business value within weeks of launch.",
          author: "Elena",
          role: "FOUNDER AT NEXUS-TECH"
        },
        {
          quote: "Huge shoutout to Pascal for absolutely nailing the DRIIIV website. Fast, smooth, and technically flawless. Pascal wasn’t just executing; he was the quiet guardian of our CI, pulling us back on track whenever we overthought things. Smart calls, great timing, zero drama. This is how digital work should feel. Massive respect.",
          author: "Robert Jung",
          role: "DRIIIV.COM / EX GLOBAL EXECUTIVE CREATIVE DIRECTOR PORSCHE"
        }
      ]
    }
  }
};

const ICONS = {
  Hexagon, Cpu, Mic, Database, Video, Users, Film, Code, Smartphone, Layout, MousePointer2, Layers, Globe, Box, PenTool
};

const FOUNDER = {
  name: "Pascal Hoffmann",
  role: "Founder & Creative Director",
  bio: "Mit über 25 Jahren Erfahrung als UX/UI Designer und AI Artist verbindet Pascal Hoffmann kreative Exzellenz mit technologischer Innovation. Als Gründer von CRE8NOW führt er ein exzellentes Netzwerk aus spezialisierten Experten für Design und künstliche Intelligenz, um für jedes Projekt die optimale Taskforce aus AI-Agenten und menschlicher Genialität zu mobilisieren.",
  stats: [
    { label: "Years Experience", value: "25+" },
    { label: "Brands", value: "40+" },
    { label: "Projects", value: "450+" }
  ]
};

const CLIENTS = ["SAMSUNG", "MERCEDES-BENZ", "MICROSOFT", "AUDI", "LUFTHANSA", "NIKON"];

// Brand roster lives with its marquee; re-exported for existing importers.
export { BRAND_LOGOS };

const TOOL_LOGOS = Array.from({ length: 12 }, (_, i) => ({
  src: `/assets/tools/cre8now-aitools-${String(i + 1).padStart(5, '0')}.png`,
  alt: `AI Tool ${i + 1}`
}));

export const SplashScreen = ({ color, theme, activeTab }) => {
  const posterAccent = activeTab === 'ai'
    ? 'var(--cre8-magenta)'
    : activeTab === 'concierge'
      ? 'var(--cre8-blue)'
      : activeTab === 'prototyping'
        ? 'var(--cre8-violet)'
        : 'var(--cre8-yellow)';

  return (
    <Motion.div
      initial={{ opacity: 1 }}
      exit={{
        opacity: 0,
        transition: { duration: 0.8, ease: "easeInOut" }
      }}
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-bg-color overflow-hidden h-[100dvh]"
    >
      {/* Background Glow */}
      <Motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 0.15 }}
        transition={{ duration: 2, ease: "easeOut" }}
        className="absolute w-[80vw] h-[80vw] rounded-full blur-[120px]"
        style={{ background: `radial-gradient(circle, ${color} 0%, transparent 70%)` }}
      />

      {/* Swiss micrographic frame — same system as the hero, but all four
          crop-mark corners (top and bottom) since there's no nav or marquee
          to clear here. */}
      <div className="hidden md:block absolute inset-0 z-10 pointer-events-none text-text-color">
        <CropMarks inset={20} color={posterAccent} />
        <div className="absolute top-6 left-6 flex items-center gap-4">
          <RegistrationMark size={16} style={{ color: posterAccent }} className="opacity-70" />
          <IndexTag code="CRE8NOW" num="BOOT" color={posterAccent} />
        </div>
        <div className="absolute top-6 right-6">
          <LiveCoordTag />
        </div>
        <div className="absolute bottom-6 left-6">
          <span className="font-mono-swiss text-[9px] tracking-[0.2em] opacity-50">SYSTEM / LOADING</span>
        </div>
        <div className="absolute bottom-6 right-6">
          <span className="font-mono-swiss text-[9px] tracking-[0.2em] opacity-50" style={{ color: posterAccent }}>
            MODE / {activeTab === 'ai' ? 'AI' : activeTab === 'concierge' ? 'CONCIERGE' : activeTab === 'prototyping' ? 'PROTOTYPING' : 'DESIGN'}
          </span>
        </div>
        <VerticalLabel text="CRE8NOW / SYSTEM" side="left" offset={24} />
        <VerticalLabel text="INITIALIZING" side="right" offset={24} color={posterAccent} />
      </div>

      <div className="relative z-10 flex flex-col items-center">
        <Motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 2, ease: [0.16, 1, 0.3, 1] }}
          className="mb-8"
        >
          <Logo theme={theme} activeTab={activeTab} forceFull className="w-[150px] md:w-[200px] h-auto" />
        </Motion.div>
      </div>
      
      <div className="absolute bottom-12 left-1/2 -translate-x-1/2 flex flex-col items-center gap-6">
        <Motion.div
          initial={{ width: 0 }}
          animate={{ width: 100 }}
          transition={{ duration: 1.5, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
          className="h-px bg-border-color relative overflow-hidden"
        >
          <Motion.div
            initial={{ x: '-100%' }}
            animate={{ x: '100%' }}
            transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
            className="absolute inset-0 h-full w-full"
            style={{ background: `linear-gradient(90deg, transparent, ${color}, transparent)` }}
          />
        </Motion.div>

        <Motion.h6
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 0.4, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="text-[10px] font-bold uppercase tracking-[0.4em] whitespace-nowrap"
        >
          Initializing CRE8NOW
        </Motion.h6>
      </div>
    </Motion.div>
  );
};

const PROJECTS = [
  { title: "Demo Reel", category: "CRE8NOW", year: "2025", image: "https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?auto=format&fit=crop&q=80&w=1200", video: "/assets/videos/cre8now-work_1.mp4" },
  { title: "Helium Film & AI Production", category: "Film & AI", year: "2025", image: "https://images.unsplash.com/photo-1633356122544-f134324a6cee?auto=format&fit=crop&q=80&w=1200", video: "/assets/videos/cre8now-helium.mp4" },
  { title: "DRIIIV", category: "No Agency", year: "2024", image: "https://images.unsplash.com/photo-1555066931-4365d14bab8c?auto=format&fit=crop&q=80&w=1200", video: "/assets/videos/cre8now-driiiv.mp4" },
  { title: "Valet-Mobility", category: "Mobility Platform", year: "2025", image: "https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?auto=format&fit=crop&q=80&w=1200", video: "/assets/videos/valet.mp4" },
  { title: "Bewerbung AI", category: "Bewerbungs Platform", year: "2025", image: "https://images.unsplash.com/photo-1620712943543-bcc4607c9960?auto=format&fit=crop&q=80&w=1200", video: "/assets/videos/bewerbung.ai.mp4" },
  { title: "BlueLake Strategy", category: "Unternehmensberatung", year: "2024", image: "https://images.unsplash.com/photo-1541462608141-675f15914730?auto=format&fit=crop&q=80&w=1200", video: "/assets/videos/bluelake-strategy.mp4" }
];

const BOOKING_CONTENT = {
  de: {
    title: 'Strategiegespräch buchen',
    subtitle: 'Teile CRE8NOW dein Vorhaben mit. CRE8NOW stellt die passende Taskforce aus Designern & KI-Experten zusammen.',
    steps: ['Ziele', 'Painpoints', 'Details', 'Kontakt'],
    labels: {
      name: 'Name',
      email: 'E-Mail',
      company: 'Unternehmen',
      website: 'Website',
      details: 'Zusätzliche Infos',
      reasons: 'Was möchtest du erreichen?',
      painpoints: 'Aktuelle Herausforderungen?',
      focus: 'Welche Lösungen interessieren dich?',
      industry: 'Branche / Bereich?',
      timeline: 'Gewünschter Start',
      budget: 'Budgetrahmen'
    },
    placeholders: {
      name: 'Vor- und Nachname',
      email: 'name@unternehmen.de',
      company: 'Firmenname',
      website: 'https://...',
      details: 'Ziele, Anforderungen oder aktuelle Herausforderungen.'
    },
    industries: ['E-Commerce', 'SaaS / Tech', 'Finanz / Legal', 'Health / Med', 'Real Estate', 'Education', 'Lifestyle / Gastro', 'Anderes'],
    goals: {
      design: ['UX Design', 'Landingpage', 'App Design', 'Design System', 'Redesign', 'Rapid Prototype', 'Web-App', 'Native App', 'Web Development', 'Social Media Design', 'Motion Graphics'],
      ai: ['KI-Strategie', 'AI-Agenten', 'Voice AI', 'Automation', 'Gen-AI Content', 'LLM Training'],
      consulting: ['UX/UI Audit', 'Design Audit', 'Performance Audit', 'Infrastruktur Audit', 'KI Roadmaps', 'Prozess-Analyse', 'Scaling Strategie'],
      workshops: ['Team Onboarding', 'Prompt Engineering', 'Figma Training', 'Webflow Training', 'AI Workflow Setup', 'Design Thinking', 'Best-Practice-Training']
    },
    painpoints: {
      design: ['Wenig Conversions', 'Veralteter Look', 'Hoher Drop-off', 'Inkonsistenz', 'Slow Handoff', 'Mobile UX'],
      ai: ['Teamüberlastung', 'Erreichbarkeit', 'Support Team Entlastung', 'Antwortzeit Leads', 'Kundenzufriedenheit', 'Verpasste Chancen'],
      consulting: ['Fehlendes KI-Wissen', 'Hohe Fehlerraten', 'Falsche Tools / Tech', 'Legacy Code / System', 'Skalierungs-Angst', 'Budget Verschwendung'],
      workshops: ['Mitarbeiter-Vorbehalte', 'Kein Praxisbezug', 'Zeitverschwendung', 'Ineffiziente Workflows', 'Technologie-Überforderung', 'Fehlender Standard']
    },
    timelineOptions: [
      { id: 'asap', label: 'Sofort' },
      { id: 'month', label: '30 Tage' },
      { id: 'quarter', label: 'Quartal' },
      { id: 'open', label: 'Offen' }
    ],
    budgetOptions: [
      { id: '5k', label: 'Bis 5k €' },
      { id: '15k', label: '5k - 15k €' },
      { id: '30k', label: '15k - 30k €' },
      { id: '30kplus', label: '30k+ €' }
    ],
    buttons: {
      cancel: 'Abbrechen',
      back: 'Zurück',
      next: 'Weiter',
      send: 'Anfrage senden',
      sending: 'Wird gesendet...',
      close: 'Schließen'
    },
    validation: {
      name: 'Name erforderlich',
      email: 'E-Mail ungültig',
      company: 'Unternehmen erforderlich',
      website: 'Website erforderlich',
      industry: 'Bitte Branche wählen',
      focus: 'Bitte Bereich wählen',
      goals: 'Bitte Ziele wählen',
      painpoints: 'Bitte Painpoints wählen',
      timeline: 'Bitte Start wählen',
      budget: 'Bitte Budget wählen'
    },
    confirmation: {
      title: 'Anfrage gesendet',
      description: 'CRE8NOW hat deine Details erhalten und meldet sich in Kürze.'
    },
    tooltips: {
      focus: "Wähle die Bereiche, in denen CRE8NOW dich unterstützen kann. Du kannst auch mehrere wählen.",
      industry: "Hilft CRE8NOW, die Beratung auf deine Branche & spezifischen Anforderungen vorzubereiten.",
      goals: "Damit konfiguriert CRE8NOW die primären Ziele für dein Projekt.",
      painpoints: "Zeigt CRE8NOW, wo die größten Hebel für Optimierungen liegen.",
      timeline: "Hilft CRE8NOW bei der Ressourcenplanung für dein Projekt.",
      budget: "Wichtig für die Auswahl der passenden Strategie und Umsetzungstiefe."
    }
  },
  en: {
    title: 'Book Orientation Call',
    subtitle: 'Tell CRE8NOW about your project. CRE8NOW will assemble the right task force of designers & AI experts.',
    steps: ['Goals', 'Challenges', 'Details', 'Contact'],
    labels: {
      name: 'Name',
      email: 'Email',
      company: 'Company',
      website: 'Website',
      details: 'Additional details',
      reasons: 'What do you want to achieve?',
      painpoints: 'Actual challenges?',
      focus: 'Which solutions are you interested in?',
      industry: 'Industry / Area?',
      timeline: 'Preferred start',
      budget: 'Budget range'
    },
    placeholders: {
      name: 'Full name',
      email: 'name@company.com',
      company: 'Company name',
      website: 'https://...',
      details: 'Goals, requirements, or current challenges.'
    },
    industries: ['E-Commerce', 'SaaS / Tech', 'Finance / Legal', 'Health / Med', 'Real Estate', 'Education', 'Lifestyle / Gastro', 'Other'],
    goals: {
      design: ['UX Design', 'Landingpage', 'App Design', 'Design System', 'Redesign', 'Rapid Prototype', 'Web-App', 'Native App', 'Web Development', 'Social Media Design', 'Motion Graphics'],
      ai: ['AI Strategy', 'AI Agents', 'Voice AI', 'Automation', 'Gen-AI Content', 'LLM Training'],
      consulting: ['UX/UI Audit', 'Design Audit', 'Performance Audit', 'Infrastructure Audit', 'AI Roadmaps', 'Process Analysis', 'Scaling Strategy'],
      workshops: ['Team Onboarding', 'Prompt Engineering', 'Figma Training', 'Webflow Training', 'AI Workflow Setup', 'Design Thinking', 'Best Practices Training']
    },
    painpoints: {
      design: ['Low Conversions', 'Outdated Look', 'High Drop-off', 'Inconsistency', 'Slow Handoff', 'Mobile UX'],
      ai: ['Team Overload', 'Reachability', 'Support Team Relief', 'Lead Response Time', 'Customer Satisfaction', 'Missed Opportunities'],
      consulting: ['No Internal AI Expertise', 'High Error Rates', 'Wrong Tools / Stack', 'Legacy Code / Systems', 'Fear of Scaling', 'Wasted Budgets'],
      workshops: ['Staff AI Hesitancy', 'No Practical Relevance', 'Time Wasted', 'Inefficient Workflows', 'Tech Overload', 'No Setup Standard']
    },
    timelineOptions: [
      { id: 'asap', label: 'ASAP' },
      { id: 'month', label: '30 Days' },
      { id: 'quarter', label: 'Quarter' },
      { id: 'open', label: 'Open' }
    ],
    budgetOptions: [
      { id: '5k', label: 'Up to €5k' },
      { id: '15k', label: '€5k - €15k' },
      { id: '30k', label: '€15k - €30k' },
      { id: '30kplus', label: '€30k+' }
    ],
    buttons: {
      cancel: 'Cancel',
      back: 'Back',
      next: 'Next',
      send: 'Send Inquiry',
      sending: 'Sending...',
      close: 'Close'
    },
    validation: {
      name: 'Name required',
      email: 'Invalid Email',
      company: 'Company required',
      website: 'Website required',
      industry: 'Select industry',
      focus: 'Select area',
      goals: 'Select goals',
      painpoints: 'Select challenges',
      timeline: 'Select start',
      budget: 'Select budget'
    },
    confirmation: {
      title: 'Inquiry Sent',
      description: 'CRE8NOW received your details and will get back to you shortly.'
    },
    tooltips: {
      focus: "Select the areas where CRE8NOW can support you. You can choose multiple.",
      industry: "Helps CRE8NOW prepare the consultation for your specific industry requirements.",
      goals: "Lets CRE8NOW configure the primary objectives for your project.",
      painpoints: "Shows CRE8NOW where the biggest leverage for optimization lies.",
      timeline: "Helps CRE8NOW with resource planning for your project.",
      budget: "Important for selecting the right strategy and implementation depth."
    }
  }
};

export const getDefaultBookingFocus = (activeTab) => (
  ['design', 'ai', 'consulting', 'workshops'].includes(activeTab) ? [activeTab] : []
);

const buildBookingSubmission = ({ lang, form, copy }) => {
  const focusLabels = form.focus.map((id) => {
    if (id === 'design') return 'Design Solutions';
    if (id === 'ai') return 'AI Solutions';
    if (id === 'consulting') return 'Consulting';
    if (id === 'workshops') return 'Workshops';
    return id;
  });
  const timelineLabel = copy.timelineOptions.find((option) => option.id === form.timeline)?.label || form.timeline;
  const budgetLabel = copy.budgetOptions.find((option) => option.id === form.budget)?.label || form.budget;

  return {
    lang,
    subject: lang === 'de'
      ? `Projekt-Anfrage | ${form.focus.join(' & ').toUpperCase()}${form.company ? ` | ${form.company}` : ''}`
      : `Project Inquiry | ${form.focus.join(' & ').toUpperCase()}${form.company ? ` | ${form.company}` : ''}`,
    name: form.name,
    email: form.email,
    company: form.company || '-',
    website: form.website || '-',
    industry: form.industry || '-',
    focus: focusLabels.length ? focusLabels.join(', ') : '-',
    goals: form.reasons.length ? form.reasons.join(', ') : '-',
    painpoints: form.painpoints.length ? form.painpoints.join(', ') : '-',
    timeline: timelineLabel || '-',
    budget: budgetLabel || '-',
    details: form.details || '-',
    type: 'campaign_inquiry',
    origin: window.location.origin
  };
};

/* --------------------------------------------------------------------------------
   COMPONENTS
-------------------------------------------------------------------------------- */


// Vertical Swiss-styled service bar — a stack of full-width rows rather than
// a card grid. Each row is index + title + copy + icon; the fill enters from
// whichever edge the cursor crossed — moving down through the list, the next
// row's fill drops in from the top; moving up, it rises in from the bottom —
// matching the direction you're actually travelling through the list.
// Index/icon/arrow read in the accent color at rest, contrast color on hover.
export const ServiceItem = ({ service, i, total, color, isMobile, currentIcons, theme, hoverTextColor }) => {
  const ServiceIcon = currentIcons[i] || Hexagon;
  const fillRef = useRef(null);

  const rowVars = { '--svc-accent': color, '--svc-hover-text': hoverTextColor || '#ffffff' };
  const accentClass = '[color:var(--svc-accent)] group-hover:[color:var(--svc-hover-text)]';
  const inkClass = 'group-hover:[color:var(--svc-hover-text)]';

  // Jump the fill to its start edge with transitions off, force a reflow,
  // then re-enable transitions and animate to the target — otherwise the
  // browser batches both writes and the "jump" never renders.
  const placeAt = (edge) => {
    const el = fillRef.current;
    if (!el) return;
    el.style.transition = 'none';
    el.style.transform = edge === 'top' ? 'translateY(-100%)' : 'translateY(100%)';
    void el.offsetHeight;
    el.style.transition = '';
  };

  const edgeFor = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    return (e.clientY - rect.top) < rect.height / 2 ? 'top' : 'bottom';
  };

  const handleEnter = (e) => {
    placeAt(edgeFor(e));
    requestAnimationFrame(() => {
      if (fillRef.current) fillRef.current.style.transform = 'translateY(0%)';
    });
  };
  const handleLeave = (e) => {
    const el = fillRef.current;
    if (!el) return;
    el.style.transform = edgeFor(e) === 'top' ? 'translateY(-100%)' : 'translateY(100%)';
  };

  // Items that link to a dedicated SEO subpage (e.g. UX Design → /design/ux-design)
  // render as a real anchor — a genuine crawlable link, not a JS-only handler —
  // everything else stays a plain non-navigating row.
  const RowTag = service.href ? Motion.a : Motion.div;
  const rowProps = service.href ? { href: service.href } : {};

  return (
    <RowTag
      {...rowProps}
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: isMobile ? 0 : Math.min(i, 6) * 0.04 }}
      viewport={{ once: false, amount: 0.15 }}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      style={rowVars}
      className="group relative border-b border-border-color overflow-hidden gpu-accelerated cursor-zoom block"
    >
      {/* Fill enters/exits from whichever edge the cursor crossed. */}
      <div
        ref={fillRef}
        className="absolute inset-0 transition-transform duration-500 ease-[cubic-bezier(0.83,0,0.17,1)]"
        style={{ backgroundColor: color, transform: 'translateY(-100%)' }}
      />


      <div className="relative z-10 px-6 py-8 md:px-10 md:py-9 flex flex-col md:flex-row md:items-center gap-4 md:gap-10">
        <div className="flex items-center gap-4 md:w-[92px] flex-shrink-0">
          <span className={`font-mono-swiss text-[10px] transition-colors duration-200 ease-out tabular-nums ${accentClass}`}>
            {String(i + 1).padStart(2, '0')} / {String(total).padStart(2, '0')}
          </span>
        </div>

        <h4 className={`font-black leading-[0.95] tracking-tight text-[clamp(22px,3.2vw,40px)] md:w-[300px] lg:w-[360px] flex-shrink-0 text-text-color transition-colors duration-200 ease-out ${inkClass}`}>
          {service.title}
        </h4>

        <div className="flex-1 min-w-0">
          <p className={`text-text-muted leading-relaxed font-light transition-[color,opacity] duration-200 ease-out max-w-2xl group-hover:opacity-90 ${inkClass}`}>
            {service.desc}
          </p>
          <div className="flex flex-wrap gap-2 mt-4">
            {service.tags?.map(tag => (
              <span
                key={tag}
                className={`text-[9px] font-bold uppercase tracking-[0.2em] px-3 py-1 border border-border-color group-hover:border-current/30 rounded-none text-text-muted transition-[color,opacity,border-color] duration-200 ease-out group-hover:opacity-80 ${inkClass}`}
              >
                {tag}
              </span>
            ))}
          </div>
        </div>

        <div className="hidden md:flex items-center gap-6 flex-shrink-0 self-start md:self-center">
          <ServiceIcon size={44} strokeWidth={1.5} className={`transition-colors duration-200 ease-out ${accentClass}`} />
          <ArrowUpRight
            size={22}
            className={`opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-500 ${accentClass}`}
          />
        </div>
      </div>
    </RowTag>
  );
};

const PlaygroundItem = ({ item, theme, color, lang, setExpandedVideo }) => {
  const isLight = theme === 'light';
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <Motion.div 
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      viewport={{ once: true, amount: 0.1 }}
      className="relative w-full overflow-hidden bg-bg-color group flex flex-col"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div 
        className="w-full relative overflow-hidden cursor-pointer aspect-video rounded-[4px]"
        onClick={() => item.video && setExpandedVideo(item.video)}
      >
        <div className="absolute inset-0 w-full h-full">
          {item.video && isHovered ? (
            <video
              src={item.video}
              autoPlay
              loop
              muted
              playsInline
              className="w-full h-full object-cover transition-all duration-1000 group-hover:scale-110"
            />
          ) : (
            <img 
              src={item.image} 
              alt={`CRE8NOW: ${item.title}, Design & AI Projekt`}
              loading="lazy"
              className="w-full h-full object-cover transition-all duration-1000 group-hover:scale-110"
            />
          )}
        </div>
        
        {item.status === 'in-progress' && (
          <div className="absolute top-4 right-4 z-20">
            <span 
              className="backdrop-blur-md text-bg-color text-[10px] font-black uppercase tracking-widest px-3 py-1 rounded-[2px]"
              style={{ backgroundColor: color }}
            >
              {lang === 'de' ? 'In Arbeit' : 'In Progress'}
            </span>
          </div>
        )}
      </div>

      <div className="py-8 md:py-10 flex flex-col bg-bg-color flex-1">
        <h6 className={`mb-4 ${isLight ? 'text-text-color/70' : 'text-text-muted/60'}`}>
          {item.tags.join(' · ')}
        </h6>
        
        <h3 className={`font-black uppercase leading-[1.1] tracking-tight mb-2 audiowide-regular ${isLight ? 'text-text-color' : 'text-white'}`}>
          {item.title}
        </h3>
        <h6 className="mb-6 opacity-40">
          {item.subtitle}
        </h6>

        <p className={`font-light leading-relaxed mb-8 flex-1 ${isLight ? 'text-text-muted' : 'text-white/60'}`}>
          {item.desc}
        </p>

        <div>
          <button 
            onClick={() => {
              if (item.video) setExpandedVideo(item.video);
              // In production we would open a project page or waitlist
            }}
            className="group/btn relative px-6 py-3.5 md:px-8 md:py-4 font-black uppercase tracking-widest text-[10px] transition-all duration-500 overflow-hidden bg-text-color text-bg-color rounded-[4px] self-start inline-flex items-center"
          >
            <div 
              className="absolute inset-0 translate-y-full group-hover/btn:translate-y-0 transition-transform duration-500" 
              style={{ backgroundColor: color }}
            />
            <span className={`relative z-10 flex items-center transition-colors duration-500 ${color === 'var(--ai-primary)' ? 'group-hover/btn:text-white' : ''}`}>
              {lang === 'de' ? 'Projekt starten' : 'Start Project'}
              <span className="w-0 opacity-0 group-hover/btn:w-5 group-hover/btn:ml-3 group-hover/btn:opacity-100 transition-all duration-500 overflow-hidden flex items-center">
                <ArrowRight size={14} />
              </span>
            </span>
          </button>
        </div>
      </div>
    </Motion.div>
  );
};

const AboutSectionContent = ({ color, t, theme }) => {
  const ref = useRef(null);
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  const { scrollYProgress } = useScroll({
    target: isMobile ? null : ref,
    offset: ["start end", "end start"],
  });

  const imgY = useTransform(scrollYProgress, [0, 1], ["-15%", "15%"]);
  const textY = useTransform(scrollYProgress, [0, 1], [50, -50]);

  const isDark = theme === 'dark';

  return (
    <div ref={ref} className="relative w-full min-h-screen flex items-center overflow-hidden py-32 md:py-0">
      {/* FULL WIDTH BACKGROUND IMAGE WITH PARALLAX */}
      <div className="absolute inset-0 w-full h-[130%] -top-[15%] z-0">
        <Motion.div 
          style={{ y: isMobile ? 0 : imgY }}
          className="w-full h-full"
        >
          <img 
            src={isDark ? "/assets/me_black.jpg" : "/assets/me_white.png"} 
            alt={`${FOUNDER.name}, ${FOUNDER.role} bei CRE8NOW Berlin`}
            fetchPriority="high"
            className="w-full h-full object-cover object-center md:object-[center_20%]"
          />
        </Motion.div>
        {/* Adaptive overlay for text legibility */}
        <div 
          className={`absolute inset-0 bg-bg-color opacity-80 ${isDark ? 'mix-blend-screen' : 'mix-blend-multiply'}`} 
        />
      </div>

      <div className="relative z-10 w-full px-[8vw]">
        <Motion.div 
          style={{ y: textY }}
          initial={{ opacity: 0, x: -50 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-xl lg:max-w-2xl"
        >
          <div className="flex items-center gap-4 mb-6 md:mb-8">
            <div className="w-8 h-px" style={{ backgroundColor: color }} />
            <h6 className="text-text-muted">{t.common.about.label}</h6>
          </div>
          
          <h2 
            className="font-serif font-black uppercase leading-[0.9] mb-6 md:mb-8 tracking-tighter text-text-color perspective-1000"
          >
            {FOUNDER.name}
          </h2>
          
          <p className="text-text-muted font-light leading-relaxed mb-10 max-w-lg">
            {t.common.about.bio}
          </p>
          
          <div className="grid grid-cols-3 gap-6 md:gap-10 border-t border-border-color pt-8">
            {t.common.about.stats.map(stat => (
              <StatItem key={stat.label} value={stat.value} label={stat.label} color={color} size="clamp(30px, 3.2vw, 54px)" />
            ))}
          </div>
        </Motion.div>
      </div>
    </div>
  );
};

// Single logo cell — its own scroll progress (not the section's) drives a
// continuous scrub: dim/grey while off-screen below, full color while
// centered, dim/grey again as it exits above. That's the actual "in and
// out" effect — whileInView only fires once per threshold crossing, this
// reads scroll position every frame instead. Opacity/color only, nothing
// moves — an earlier version also drifted the cell vertically, but that
// positional motion is what was causing clipping/overlap with neighboring
// rows in a fixed grid, so it's gone; the cell stays put and only fades.
// Corners use the same bracket mark as CropMarks elsewhere on the site,
// on all four sides, instead of ruled border lines.
const LogoWallItem = ({ logo, idx, theme, color }) => {
  const itemRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: itemRef, offset: ['start end', 'end start'] });
  const opacity = useTransform(scrollYProgress, [0, 0.3, 0.7, 1], [0.12, 1, 1, 0.12]);
  const grayscaleAmt = useTransform(scrollYProgress, [0, 0.3, 0.7, 1], [1, 0, 0, 1]);
  const filter = useTransform(grayscaleAmt, (v) => `grayscale(${v})`);
  // Same scrub as opacity above, on the same breakpoints — a slight zoom
  // in as each mark reaches full opacity, back out as it dims again.
  const scale = useTransform(scrollYProgress, [0, 0.3, 0.7, 1], [0.92, 1, 1, 0.92]);
  const corner = 'absolute w-[10px] h-[10px] opacity-50';

  return (
    <Motion.div
      ref={itemRef}
      style={{ opacity, scale, filter: logo.more ? undefined : filter }}
      className="relative flex-none w-1/2 md:w-1/4 aspect-[3/2] flex items-center justify-center p-6 md:p-8 group"
    >
      {/* Same corner-bracket mark used everywhere else in the Swiss frame
          system (hero, section corners) — all four this time. */}
      <span className={`${corner} top-0 left-0 border-t border-l`} style={{ borderColor: color }} />
      <span className={`${corner} top-0 right-0 border-t border-r`} style={{ borderColor: color }} />
      <span className={`${corner} bottom-0 left-0 border-b border-l`} style={{ borderColor: color }} />
      <span className={`${corner} bottom-0 right-0 border-b border-r`} style={{ borderColor: color }} />
      <span className="absolute top-3 left-3 font-mono-swiss text-[8px] opacity-30 tabular-nums">
        {String(idx + 1).padStart(2, '0')}
      </span>
      {logo.more ? (
        // Closing tile — the grid's own "and N more" summary instead of a
        // 20th logo, filling the one empty slot the 19 real marks leave.
        <div className="flex flex-col items-center justify-center text-center">
          <span className="font-black audiowide-regular text-[clamp(36px,5vw,64px)] leading-none text-text-color opacity-60">
            {logo.count}+
          </span>
          <span className="font-mono-swiss text-[9px] tracking-[0.25em] uppercase opacity-50 mt-2">
            {logo.label}
          </span>
        </div>
      ) : (
        <img
          src={logo.src}
          alt={logo.alt}
          loading="lazy"
          width={200}
          height={80}
          className={`max-h-24 md:max-h-36 w-auto object-contain opacity-70 group-hover:opacity-100 transition-opacity duration-500 ${theme === 'dark' ? '' : 'brightness-0'}`}
        />
      )}
    </Motion.div>
  );
};

// Logo wall — a Swiss module grid of client marks that reacts to scroll:
// each logo scrubs from dim/greyscale/slightly-scaled-down to full color/
// opacity/scale as it scrolls into view, and back out again (see
// LogoWallItem) — the grid stays fixed in the layout, only each tile's own
// opacity/color/scale animate in place.
const LogoWallSection = ({ color, lang, theme }) => {
  const ref = useRef(null);

  const cols = 4;
  // The 19 real marks leave one empty slot in the last row (5 rows x 4
  // cols = 20) — fill it with a closing "+N more" tile instead of a 20th
  // logo.
  const items = [...BRAND_LOGOS, { more: true, count: 50, label: lang === 'de' ? 'Weitere Marken' : 'More Brands' }];
  const rows = [];
  for (let i = 0; i < items.length; i += cols) rows.push(items.slice(i, i + cols));

  return (
    <section id="clients" ref={ref} data-gsap-section className="relative w-full py-24 md:py-32 bg-bg-color border-y border-border-color overflow-hidden">
      <SectionFrame code="C" num="005" color={color} verticalLabelRight="MODE / CLIENTS" />
      <div className="w-full px-[8vw] mb-16 md:mb-20">
        <SectionHeading
          label={lang === 'de' ? 'CRE8NOW / Kunden' : 'CRE8NOW / Clients'}
          title={lang === 'de' ? '25 Jahre. 450+ Projekte.' : '25 Years. 450+ Projects.'}
          highlight={lang === 'de' ? '60+ MARKEN' : '60+ BRANDS'}
          description={lang === 'de'
            ? 'CRE8NOW entwickelt seit über 25 Jahren digitale Produkte für internationale Marken: von Design-Systemen bis zu KI-gestützten Automationen.'
            : 'For over 25 years, CRE8NOW has built digital products for international brands: from design systems to AI-powered automation.'}
          color={color}
          centered={true}
          theme={theme}
        />
      </div>

      <div className="relative w-full px-8 md:px-20">
        {/* Outer frame corners for the grid block itself — Swiss crop
            marks instead of a ruled border box. */}
        <CropMarks inset={0} color={color} className="hidden md:block" />
        {rows.map((row, ri) => (
          <div key={ri} className="flex flex-wrap">
            {row.map((logo, ci) => (
              <LogoWallItem
                key={logo.src || 'more'}
                logo={logo}
                idx={ri * cols + ci}
                theme={theme}
                color={color}
              />
            ))}
          </div>
        ))}
      </div>
    </section>
  );
};

const TestimonialSection = ({ color, t, lang, theme }) => {
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const [progress, setProgress] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isAuto, setIsAuto] = useState(true);
  const testimonials = t.common.testimonials;
  const ref = useRef(null);

  const nextSlide = (auto = false) => {
    setIsAuto(auto);
    setDirection(1);
    setProgress(0);
    setIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevSlide = (auto = false) => {
    setIsAuto(auto);
    setDirection(-1);
    setProgress(0);
    setIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  // Handle auto-advance with progress state
  useEffect(() => {
    let interval;
    if (!isPaused) {
      interval = setInterval(() => {
        setProgress((prev) => {
          if (prev >= 100) {
            nextSlide(true);
            return 0;
          }
          return prev + 0.5; // Controls speed, 0.5% every 50ms = 10s total
        });
      }, 50);
    }
    return () => clearInterval(interval);
  }, [isPaused, testimonials.length]);

  const variants = {
    initial: (direction) => ({
      opacity: 0,
      x: direction > 0 ? '100%' : direction < 0 ? '-100%' : 0,
      filter: 'blur(10px)',
      scale: 0.98
    }),
    animate: {
      opacity: 1,
      x: 0,
      filter: 'blur(0px)',
      scale: 1
    },
    exit: (direction) => ({
      opacity: 0,
      x: direction > 0 ? '-100%' : direction < 0 ? '100%' : 0,
      filter: 'blur(10px)',
      scale: 0.98
    })
  };

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"]
  });

  const bgY = useTransform(scrollYProgress, [0, 1], [-150, 150]);

  return (
    <section id="testimonials" ref={ref} className="relative min-h-[70vh] py-24 md:py-32 bg-surface-color overflow-hidden border-y border-border-color gpu-accelerated">
      <CropMarks insetTop={24} insetBottom={24} insetX={24} color={color} className="hidden md:block opacity-40" />

      <Motion.div
        style={{ y: bgY }}
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none select-none opacity-[0.04] hidden md:block"
      >
        <Quote size={800} strokeWidth={0.5} style={{ color }} />
      </Motion.div>

      <div className="w-full px-6 md:px-12 relative z-10 flex flex-col items-center">
        <div className="relative w-full flex items-center justify-center min-h-[400px] md:min-h-[350px]">
          {/* ARROW LEFT */}
          <button 
            onClick={() => prevSlide(false)}
            className="absolute left-0 md:left-10 z-20 p-4 rounded-full border border-border-color/20 bg-surface-color/50 backdrop-blur-sm hover:scale-110 active:scale-95 transition-all text-text-color/50 hover:text-text-color hidden md:flex"
            aria-label="Previous testimonial"
          >
            <ChevronLeft size={24} />
          </button>

          <AnimatePresence mode="wait" custom={direction}>
            <Motion.div
              key={index}
              custom={direction}
              variants={variants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={{ 
                duration: 1, 
                ease: isAuto ? [0.83, 0, 0.17, 1] : [0.22, 1, 0.36, 1]
              }}
              className="w-full flex flex-col items-center text-center cursor-grab active:cursor-grabbing"
              drag="x"
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={0.2}
              onDragEnd={(_, info) => {
                const swipeThreshold = 50;
                if (info.offset.x < -swipeThreshold) nextSlide(false);
                else if (info.offset.x > swipeThreshold) prevSlide(false);
              }}
              onMouseEnter={() => setIsPaused(true)}
              onMouseLeave={() => setIsPaused(false)}
              onTouchStart={() => setIsPaused(true)}
              onTouchEnd={() => setIsPaused(false)}
            >
              <blockquote className="font-serif font-medium italic leading-[1.3] text-text-color mb-12 w-[85vw] md:w-[60vw] mx-auto tracking-tight select-none">
                "{testimonials[index].quote}"
              </blockquote>
              
              <div className="flex flex-col items-center select-none">
                <p className="font-bold uppercase tracking-[0.3em] text-text-color mb-2">{testimonials[index].author}</p>
                <div className="h-px w-8 bg-border-color mb-3" />
                <h6 className="opacity-60" style={{ color }}>{testimonials[index].role}</h6>
              </div>
            </Motion.div>
          </AnimatePresence>

          {/* ARROW RIGHT */}
          <button 
            onClick={() => nextSlide(false)}
            className="absolute right-0 md:right-10 z-20 p-4 rounded-full border border-border-color/20 bg-surface-color/50 backdrop-blur-sm hover:scale-110 active:scale-95 transition-all text-text-color/50 hover:text-text-color hidden md:flex"
            aria-label="Next testimonial"
          >
            <ChevronRight size={24} />
          </button>
        </div>

        {/* SLIDER DOTS */}
        <div className="flex items-center gap-6 mt-16 md:mt-24">
          <SectionCounter current={index + 1} total={testimonials.length} className="opacity-50" />
          <div className="flex gap-4">
          {testimonials.map((_, i) => (
            <button
              key={i}
              onClick={() => {
                setDirection(i > index ? 1 : -1);
                setIsAuto(true); // Keep sophisticated ease for dot navigation
                setIndex(i);
                setProgress(0);
              }}
              aria-label={lang === 'de' ? `Gehe zu Testimonial ${i + 1}` : `Go to testimonial ${i + 1}`}
              className="group py-6 flex items-center justify-center transition-all duration-300"
            >
              <div 
                className={`h-[3px] rounded-full transition-all duration-700 overflow-hidden relative ${i === index ? 'w-[100px] bg-text-color/10' : 'w-10 bg-text-color/30 group-hover:bg-text-color/60'}`}
              >
                {i === index && (
                  <Motion.div 
                    className="absolute inset-0 h-full"
                    style={{ 
                      width: `${progress}%`,
                      backgroundColor: color 
                    }}
                  />
                )}
              </div>
            </button>
          ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export const Nav = ({ activeTab, setActiveTab, setView, color, lang, setLang, t, theme, setTheme, onOpenBookingModal, heroMagenta, videoHero = false }) => {
  const { cartItems, setIsCartOpen } = useCart();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [hoveredItem, setHoveredItem] = useState(null);
  const [activeSection, setActiveSection] = useState("");

  // Active-tab indicator: Design uses the real --design-primary token (so
  // it flips magenta/yellow with theme, same as everywhere else Design's
  // accent appears), AI stays fixed magenta, Concierge is blue. Label
  // color has to flip with it — white reads fine on magenta/blue, not on
  // the yellow dark-mode Design indicator.
  const navIndicatorColor = activeTab === 'concierge'
    ? 'var(--cre8-blue)'
    : activeTab === 'prototyping'
      ? 'var(--prototyping-primary)'
      : activeTab === 'design' ? 'var(--design-primary)' : 'var(--cre8-magenta)';
  const navIndicatorText = activeTab === 'design' && theme === 'dark' ? '#1a1a1a' : '#ffffff';

  // While the (full-bleed, solid-color) hero is still in view, the logo and
  // hamburger lose their opaque background chips and read white/yellow
  // directly against the hero instead — once scrolled past, they go back to
  // the normal opaque bg-bg-color chip.
  const [isOverHero, setIsOverHero] = useState(true);
  useEffect(() => {
    const checkHero = () => {
      const threshold = (typeof window !== 'undefined' ? window.innerHeight : 800) * 0.85;
      setIsOverHero(window.scrollY < threshold);
    };
    checkHero();
    window.addEventListener('scroll', checkHero, { passive: true });
    window.addEventListener('resize', checkHero);
    return () => {
      window.removeEventListener('scroll', checkHero);
      window.removeEventListener('resize', checkHero);
    };
  }, []);
  const heroChrome = isOverHero && activeTab !== 'cre8guide';
  // Same rule the hero itself uses to pick magenta vs. yellow
  // (isHeroMagenta further down this file) — heroChrome's accent/CTA
  // colors were hardcoded assuming the hero under it is always magenta,
  // which breaks (invisible yellow-on-yellow marks) the one case where
  // it isn't: Design tab + dark theme, where the hero itself is yellow.
  // heroMagenta lets a page whose hero is always magenta (case studies,
  // filed under the Design tab) say so, instead of the tab/theme guess.
  const isNavHeroMagenta = heroMagenta ?? (activeTab === 'ai' || activeTab === 'cre8guide' || !(activeTab === 'design' && theme === 'dark'));
  // The main tab pages' heroes are dark video walls (HeroVideoWall): chrome
  // over them is white, accented with the hero's own accent color. Only the
  // landing page sets videoHero — subpages keep their poster heroes (yellow
  // in Design + dark), so they keep the dark logo/CTA chrome below.
  const isNavHeroDark = videoHero && ['design', 'ai', 'prototyping', 'concierge'].includes(activeTab);
  const heroChromeAccent = isNavHeroDark
    ? (activeTab === 'prototyping' ? '#8B5CF6'
      : (activeTab === 'design' && theme === 'dark') || activeTab === 'concierge' ? '#EFF305' : '#F41465')
    : (isNavHeroMagenta ? '#EFF305' : '#F41465');
  const isHeroChromeLight = isNavHeroDark || isNavHeroMagenta;
  // Light mode over the hero: labels on the white tab bar and the white CTA
  // take the hero's own background color — the same white-box/colored-label
  // treatment as the hero's primary button. In light mode the indicator
  // color is exactly that hero color (magenta, Concierge blue, Prototyping
  // violet), so it doubles as the label color.
  // The dark video-wall heroes keep the regular (scrolled) label colors.
  const heroLabelColor = heroChrome && !isMenuOpen && theme !== 'dark' && !isNavHeroDark ? navIndicatorColor : null;

  // Manually measured tab indicator positions — avoids framer-motion layoutId
  // shared-layout FLIP animations picking up spurious vertical deltas caused
  // by fixed-position rect measurement timing during scroll.
  const mobileTabRefs = useRef({});
  const desktopTabRefs = useRef({});
  const [mobileIndicator, setMobileIndicator] = useState({ left: 0, width: 0 });
  const [desktopIndicator, setDesktopIndicator] = useState({ left: 0, width: 0 });

  useEffect(() => {
    const measure = () => {
      const mobileEl = mobileTabRefs.current[activeTab];
      if (mobileEl) {
        setMobileIndicator({ left: mobileEl.offsetLeft, width: mobileEl.offsetWidth });
      }
      const desktopEl = desktopTabRefs.current[activeTab];
      if (desktopEl) {
        setDesktopIndicator({ left: desktopEl.offsetLeft, width: desktopEl.offsetWidth });
      }
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [activeTab, lang]);

  // Clear active section when tab changes to avoid highlighting old sections
  useEffect(() => {
    setActiveSection("");
  }, [activeTab]);

  const updateSectionParam = (href) => {
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      url.searchParams.delete('section');
      url.hash = href.replace('#', '');
      window.history.pushState({}, '', url.toString());
    }
  };

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleScroll = () => {
      // 1. Detect if we are in the Hero area
      if (window.scrollY < 300) {
        setActiveSection("");
        return;
      }

      // 2. Dynamic threshold
      const threshold = window.innerHeight * 0.3;
      
      const targets = activeTab === 'concierge'
        ? ["#process", "#target-groups", "#calculator", "#pricing", "#faq", "#contact"]
        : activeTab === 'prototyping'
        ? ["#pains", "#wins", "#apps", "#goals", "#process", "#configurator", "#faq"]
        : [
            activeTab === 'ai' ? "#ai-showcase" : "#projects",
            "#services",
            "#process",
            "#testimonials",
            "#playground",
            "#contact"
          ];

      let currentSection = "";
      
      // Find the bottom-most section that has passed the threshold
      for (const selector of targets) {
        const element = document.querySelector(selector);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= threshold) {
            currentSection = selector;
          }
        }
      }

      // 3. Page bottom fallback
      if (window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 100) {
        currentSection = "#contact";
      }

      if (currentSection !== activeSection) {
        if (currentSection) {
          trackEvent('section_view', { 
            section: currentSection.replace('#', ''),
            tab: activeTab 
          });
        }
        setActiveSection(currentSection);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    // Run initially to capture current position
    setTimeout(handleScroll, 100);

    return () => window.removeEventListener('scroll', handleScroll);
  }, [activeTab]);

  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
      if (window.__lenis) {
        window.__lenis.stop();
      }
    } else {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
      if (window.__lenis) {
        window.__lenis.start();
      }
    }
    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
      if (window.__lenis) {
        window.__lenis.start();
      }
    };
  }, [isMenuOpen]);

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  const getActiveBtnStyles = (isActive) => {
    const isDesignDark = activeTab === 'design' && theme === 'dark';
    if (isActive) {
      return {
        backgroundColor: color,
        color: isDesignDark ? '#000000' : '#ffffff',
        borderColor: color
      };
    }
    return {};
  };

  const menuItems = activeTab === 'prototyping'
    ? [
        { label: lang === 'de' ? 'Probleme' : 'Friction', href: "#pains" },
        { label: lang === 'de' ? 'Ergebnis' : 'Outcome', href: "#wins" },
        { label: 'CRE8APPS', href: "#apps" },
        { label: lang === 'de' ? 'Ziele' : 'Goals', href: "#goals" },
        { label: lang === 'de' ? 'Ablauf' : 'Process', href: "#process" },
        { label: lang === 'de' ? 'Konfigurator' : 'Configurator', href: "#configurator" },
        { label: 'FAQ', href: "#faq" }
      ]
    : activeTab === 'concierge'
    ? [
        { label: lang === 'de' ? 'Methode' : 'Method', href: "#process" },
        { label: lang === 'de' ? 'Zielgruppen' : 'Target Groups', href: "#target-groups" },
        { label: lang === 'de' ? 'Kalkulator' : 'Calculator', href: "#calculator" },
        { label: lang === 'de' ? 'Preise' : 'Pricing', href: "#pricing" },
        { label: 'FAQ', href: "#faq" },
        { label: lang === 'de' ? 'Kontakt' : 'Contact', href: "#contact" }
      ]
    : [
        { label: t.nav.projects, href: activeTab === 'ai' ? "#ai-showcase" : "#projects" },
        { label: t.nav.services, href: "#services" },
        { label: t.nav.process, href: "#process" },
        { label: t.nav.testimonials, href: "#testimonials" },
        { label: t.nav.playground, href: "#playground" },
        { label: t.nav.contact, href: "#contact" }
      ];

  return (
    <>
      <Motion.nav 
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className={`fixed top-0 left-0 right-0 z-[100] transition-all duration-500 bg-transparent py-2 md:py-3`}
      >
        <div className="w-full px-2 md:px-3 flex items-center gap-2 md:gap-3 relative">
          <div className="flex-1 flex justify-start items-center">
            <button 
              onClick={() => {
                trackEvent('logo_click', { tab: activeTab });
                if (activeTab === 'concierge') {
                  setView('home');
                  setActiveTab('design');
                } else {
                  window.location.reload();
                }
              }}
              className={`flex items-center gap-1 cursor-pointer rounded-[4px] transition-all duration-500 h-11 px-1.5 md:h-14 md:px-4 ${heroChrome ? '' : 'bg-bg-color backdrop-blur-md'}`}
            >
              {/* Desktop: full wordmark lockup. While the menu is open, it
                  drops the hero override and falls back to Logo's own
                  theme/tab-aware colors (white/dark text, yellow/magenta
                  accent) so it matches the now theme-reactive flyout
                  background — takes priority over the heroChrome override. */}
              <Logo
                theme={theme}
                activeTab={activeTab === 'concierge' ? 'ai' : activeTab}
                className={`hidden md:block transition-all duration-500 w-auto h-8`}
                overrideWhitePart={isMenuOpen ? undefined : (heroChrome ? (isHeroChromeLight ? '#ffffff' : '#1a1a1a') : undefined)}
                overrideAccentColor={isMenuOpen ? undefined : (heroChrome ? heroChromeAccent : undefined)}
              />
              {/* Mobile: icon mark only — the full wordmark doesn't fit the compact
                  mobile nav. CSS mask lets the same monochrome asset take any accent
                  color (yellow over the hero, tab/theme accent otherwise) without
                  needing separate colored files. */}
              <span
                role="img"
                aria-label="CRE8NOW"
                className="block md:hidden w-7 h-7 transition-colors duration-500"
                style={{
                  backgroundColor: (heroChrome && !isMenuOpen)
                    ? heroChromeAccent
                    : (activeTab === 'design' || activeTab === 'cre8guide')
                      ? (theme === 'dark' ? '#EFF305' : '#F41465')
                      : '#F41465',
                  WebkitMaskImage: 'url(/assets/Icon%20white.svg)',
                  maskImage: 'url(/assets/Icon%20white.svg)',
                  WebkitMaskSize: 'contain',
                  maskSize: 'contain',
                  WebkitMaskRepeat: 'no-repeat',
                  maskRepeat: 'no-repeat',
                  WebkitMaskPosition: 'center',
                  maskPosition: 'center',
                }}
              />
            </button>
          </div>

          <div className="md:hidden absolute inset-0 flex justify-center items-center pointer-events-none">
            <div className={`pointer-events-auto relative flex bg-bg-color rounded-[9px] p-[3px] backdrop-blur-md gap-1 transition-all duration-500 h-9 border border-text-color/10 overflow-hidden`}>
            <Motion.div
              /* Inset must track the container's padding, or the pill sits
                 proud of the bar at the new height. */
              className="absolute top-[3px] bottom-[3px] left-0 rounded-[7px] pointer-events-none"
              style={{ backgroundColor: navIndicatorColor }}
              animate={{ x: mobileIndicator.left, width: mobileIndicator.width }}
              transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
            />
            {NAV_TABS.map((tab) => (
              <a
                key={tab}
                href={TAB_PATHS[tab][lang]}
                ref={(el) => { mobileTabRefs.current[tab] = el; }}
                onClick={(e) => {
                  e.preventDefault();
                  if (tab === 'concierge' || tab === 'prototyping') {
                    trackEvent(tab === 'concierge' ? 'campaign_header_click_mobile' : 'prototyping_header_click_mobile');
                    setActiveTab(tab);
                    smoothScrollTo(0);
                    setIsMenuOpen(false);
                    return;
                  }
                  trackEvent('tab_click', { tab });
                  setActiveTab(tab);
                  smoothScrollTo(0);
                  setIsMenuOpen(false);
                }}
                className={`px-2.5 md:px-6 rounded-[7px] ui-text font-black uppercase transition-all duration-300 relative flex items-center justify-center cursor-pointer ${activeTab === tab ? '' : 'text-text-color'}`}
                style={activeTab === tab ? { color: navIndicatorText } : heroLabelColor ? { color: heroLabelColor } : undefined}
              >
                <span className="relative z-10 inline-block">
                  <span className="md:hidden">{TAB_LABELS_SHORT[tab] || t.nav.design}</span>
                  <span className="hidden md:block">{tab === 'ai' ? t.nav.ai : (tab === 'concierge' ? 'Concierge' : tab === 'prototyping' ? 'Prototyping' : t.nav.design)}</span>
                </span>
              </a>
            ))}
            </div>
          </div>

          {/* Desktop tabs - in flow */}
          <div className={`hidden md:flex relative bg-bg-color rounded-[4px] p-1 backdrop-blur-md gap-1 transition-all duration-500 h-11 border border-text-color/10 overflow-hidden`}>
            <Motion.div
              className="absolute top-1 bottom-1 left-0 rounded-[4px] pointer-events-none"
              style={{ backgroundColor: navIndicatorColor }}
              animate={{ x: desktopIndicator.left, width: desktopIndicator.width }}
              transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
            />
            {NAV_TABS.map((tab) => (
              <a
                key={tab}
                href={TAB_PATHS[tab][lang]}
                ref={(el) => { desktopTabRefs.current[tab] = el; }}
                onClick={(e) => {
                  e.preventDefault();
                  if (tab === 'concierge' || tab === 'prototyping') {
                    trackEvent(tab === 'concierge' ? 'campaign_header_click' : 'prototyping_header_click');
                    setActiveTab(tab);
                    smoothScrollTo(0);
                    setIsMenuOpen(false);
                    return;
                  }
                  trackEvent('tab_click', { tab });
                  setActiveTab(tab);
                  smoothScrollTo(0);
                  setIsMenuOpen(false);
                }}
                className={`px-6 rounded-[4px] ui-text font-black uppercase transition-all duration-300 relative flex items-center justify-center cursor-pointer ${activeTab === tab ? '' : 'text-text-color'}`}
                style={activeTab === tab ? { color: navIndicatorText } : heroLabelColor ? { color: heroLabelColor } : undefined}
              >
                <span className="relative z-10 inline-block">
                  {tab === 'ai' ? t.nav.ai : (tab === 'concierge' ? 'Concierge' : tab === 'prototyping' ? 'Prototyping' : t.nav.design)}
                </span>
              </a>
            ))}
          </div>

          <div className="flex-1 flex justify-end items-center gap-2 md:gap-3">
            {/* Theme Toggle — only in flyout menu */}

            {/* Orientation Call CTA — hidden while the menu is open, since
                the flyout already has its own copy of this same button
                (location: 'flyout_menu' below); showing both is redundant. */}
            {!isMenuOpen && (
              <Motion.button
                onClick={() => {
                  trackEvent('orientation_call_click', { location: 'header' });
                  onOpenBookingModal();
                }}
                whileHover={{
                  scale: 1.05,
                  backgroundColor: color,
                  color: (activeTab === 'design' && theme === 'dark') ? '#1a1a1a' : '#ffffff'
                }}
                whileTap={{ scale: 0.95 }}
                className={`hidden md:flex items-center justify-center ui-text font-black uppercase px-3 md:px-6 rounded-[4px] transition-all duration-500 h-9 md:h-11 ${heroChrome ? (isHeroChromeLight ? 'bg-white text-[#1a1a1a]' : 'bg-[#1a1a1a] text-white') : 'bg-text-color text-bg-color'}`}
                style={heroLabelColor ? { color: heroLabelColor } : undefined}
              >
                <span className="inline-block">{activeTab === 'concierge' ? (lang === 'de' ? 'Demo buchen' : 'Book Demo') : activeTab === 'prototyping' ? (lang === 'de' ? 'Scope-Call' : 'Scope Call') : t.nav.bookTermin}</span>
              </Motion.button>
            )}



            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label={lang === 'de' ? 'Menü öffnen' : 'Open Menu'}
              className={`rounded-[4px] flex flex-col items-center justify-center group transition-all duration-500 overflow-hidden relative w-11 h-11 md:w-14 md:h-14 ${heroChrome && !isMenuOpen ? '' : 'bg-bg-color backdrop-blur-md'}`}
            >
              <Motion.div
                animate={{ rotate: isMenuOpen ? 360 : 0 }}
                transition={{ duration: 1, ease: [0.83, 0, 0.17, 1] }}
                className="relative w-5 h-5 flex flex-col items-center justify-center"
              >
                <Motion.span
                  animate={isMenuOpen ? { rotate: 45, y: 0.5 } : { rotate: 0, y: -3 }}
                  transition={{ duration: 1, ease: [0.83, 0, 0.17, 1] }}
                  className={`absolute w-full h-px transition-colors ${heroChrome && !isMenuOpen ? '' : 'bg-text-color'}`}
                  style={heroChrome && !isMenuOpen ? { backgroundColor: heroChromeAccent } : undefined}
                />
                <Motion.span
                  animate={isMenuOpen ? { rotate: -45, y: 0.5, width: "100%" } : { rotate: 0, y: 3, width: "66.6%" }}
                  transition={{ duration: 1, ease: [0.83, 0, 0.17, 1] }}
                  className={`absolute right-0 h-px transition-colors ${heroChrome && !isMenuOpen ? '' : 'bg-text-color'}`}
                  style={heroChrome && !isMenuOpen ? { backgroundColor: heroChromeAccent } : undefined}
                />
              </Motion.div>
            </button>
          </div>
        </div>
      </Motion.nav>

      <AnimatePresence>
        {isMenuOpen && (
          <Motion.div 
            key="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35, ease: 'easeInOut' }}
            data-lenis-prevent
            className="fixed inset-0 z-[90] bg-bg-color flex flex-col md:flex-row overflow-y-auto md:overflow-hidden overscroll-none"
            style={{ pointerEvents: 'auto' }}
          >
            {/* Background Texture Overlay */}
            <div className="absolute inset-0 opacity-[0.03] pointer-events-none bg-[url('https://grainy-gradients.vercel.app/noise.svg')] blend-overlay" />
            
            <div 
              className="flex-1 relative z-10 h-auto md:h-full md:overflow-y-auto pointer-events-auto"
              style={{ WebkitOverflowScrolling: 'touch' }}
            >
              <div className="flex flex-col justify-center min-h-full px-[clamp(1rem,4vw,4rem)] pl-10 md:pl-[clamp(1rem,4vw,4rem)] md:ml-[clamp(3rem,12vw,140px)] pt-24 md:pt-32 pb-4 md:py-32">
                <nav className="flex flex-col">
                {menuItems.map((item, i) => (
                  <Motion.a
                    key={item.label}
                    href={item.href}
                    onClick={(e) => {
                      if (item.onClick) {
                        item.onClick(e);
                        return;
                      }
                      e.preventDefault();
                      setIsMenuOpen(false);
                      setActiveSection(item.href);
                      if (window.__lenis) {
                        window.__lenis.start();
                      }
                      updateSectionParam(item.href);
                      
                      const target = document.querySelector(item.href);
                      if (target) {
                        setTimeout(() => {
                          smoothScrollIntoView(target, 80, 1500);
                        }, 50);
                      }
                    }}
                    initial={{ x: -100, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: 0.1 + i * 0.08, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                    onMouseEnter={() => setHoveredItem(i)}
                    onMouseLeave={() => setHoveredItem(null)}
                    className="group relative py-[clamp(0.15rem,0.6vh,0.5rem)] flex items-baseline overflow-visible"
                  >
                    <span 
                      className="text-[10px] md:text-base font-mono absolute right-full mr-6 md:mr-12 hidden md:block transition-all duration-500" 
                      style={{ 
                        color: (hoveredItem === i || activeSection === item.href) ? color : 'var(--text-color)',
                        opacity: (hoveredItem === i || activeSection === item.href) ? 1 : 0.3
                      }}
                    >
                      0{i + 1}
                    </span>
                    <span 
                      className="text-[10px] font-mono md:hidden mr-4 transition-all duration-500" 
                      style={{ 
                        color: (hoveredItem === i || activeSection === item.href) ? color : 'var(--text-color)',
                        opacity: (hoveredItem === i || activeSection === item.href) ? 1 : 0.3
                      }}
                    >
                      0{i + 1}
                    </span>
                    <h3 
                      className={`font-normal uppercase leading-none tracking-normal text-text-muted/20 transition-all duration-700`}
                      style={{ color: (hoveredItem === i || activeSection === item.href) ? color : '' }}
                    >
                      {item.label}
                    </h3>
                  </Motion.a>
                ))}
              </nav>
            </div>
          </div>
            
            <div 
              className="w-full md:w-1/3 md:min-w-[370px] relative z-10 bg-transparent md:bg-surface-color/50 border-t md:border-t-0 md:border-l border-border-color h-auto md:h-full md:overflow-y-auto pointer-events-auto"
              style={{ WebkitOverflowScrolling: 'touch' }}
            >
              <div className="flex flex-col justify-center min-h-full px-[clamp(1rem,4vw,4rem)] pl-12 md:pl-[clamp(1rem,4vw,4rem)] pt-12 md:pt-24 pb-20 md:py-32">
                <Motion.div 
                  initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="space-y-8 md:space-y-12"
              >
                <div>
                  <h6 className="text-text-muted mb-4 md:mb-6">{lang === 'de' ? 'CRE8NOW WISSEN' : 'CRE8NOW KNOWLEDGE'}</h6>
                  <div className="flex flex-col items-start gap-4">
                    <a
                      href={TAB_PATHS.cre8guide[lang]}
                      onClick={(e) => {
                        e.preventDefault();
                        trackEvent('tab_click', { tab: 'cre8guide' });
                        setActiveTab('cre8guide');
                        smoothScrollTo(0);
                        setIsMenuOpen(false);
                      }}
                      className={`px-4 py-3.5 rounded-[4px] text-xs font-black uppercase tracking-[0.2em] transition-all duration-300 border inline-flex cursor-pointer ${
                        activeTab === 'cre8guide'
                          ? ''
                          : 'text-text-muted border-border-color hover:border-text-color hover:text-text-color'
                      }`}
                      style={getActiveBtnStyles(activeTab === 'cre8guide')}
                    >
                      CRE8GUIDE
                    </a>
                  </div>
                </div>

                <div>
                  <h6 className="text-text-muted mb-6 md:mb-8">{lang === 'de' ? 'Sprache' : 'Language'}</h6>
                  <div className="flex gap-4">
                    {['de', 'en'].map((l) => (
                      <button
                        key={l}
                        onClick={() => {
                          trackEvent('change_language', { language: l });
                          setLang(l);
                        }}
                        className={`px-4 py-2 rounded-[4px] text-xs font-black uppercase tracking-[0.2em] transition-all duration-300 border ${lang === l ? '' : 'text-text-muted border-border-color hover:border-text-color hover:text-text-color'}`}
                        style={getActiveBtnStyles(lang === l)}
                      >
                        <span className="inline-block">{l === 'de' ? 'DE' : 'EN'}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <h6 className="text-text-muted mb-6 md:mb-8">{lang === 'de' ? 'Erscheinungsbild' : 'Appearance'}</h6>
                  <div className="flex gap-4">
                    <button
                      onClick={() => {
                        trackEvent('change_theme', { theme: 'light' });
                        setTheme('light');
                      }}
                      className={`px-4 py-2 rounded-[4px] text-xs font-black uppercase tracking-[0.2em] transition-all duration-300 flex items-center gap-2 border ${theme === 'light' ? '' : 'text-text-muted border-border-color hover:border-text-color hover:text-text-color'}`}
                      style={getActiveBtnStyles(theme === 'light')}
                    >
                      <Sun size={14} />
                      <span className="inline-block">Light</span>
                    </button>
                    <button
                      onClick={() => {
                        trackEvent('change_theme', { theme: 'dark' });
                        setTheme('dark');
                      }}
                      className={`px-4 py-2 rounded-[4px] text-xs font-black uppercase tracking-[0.2em] transition-all duration-300 flex items-center gap-2 border ${theme === 'dark' ? '' : 'text-text-muted border-border-color hover:border-text-color hover:text-text-color'}`}
                      style={getActiveBtnStyles(theme === 'dark')}
                    >
                      <Moon size={14} />
                      <span className="inline-block">Dark</span>
                    </button>
                  </div>
                </div>

                <div>
                  <h6 className="text-text-muted mb-4 md:mb-6">{t.nav.connect}</h6>
                  <Motion.button
                    onClick={() => {
                      trackEvent('orientation_call_click', { location: 'flyout_menu' });
                      setIsMenuOpen(false);
                      onOpenBookingModal();
                    }}
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.98 }}
                    className="mb-5 inline-flex items-center justify-center rounded-[4px] px-5 py-3 text-[10px] font-black uppercase tracking-[0.2em] transition-all duration-300 border"
                    style={getActiveBtnStyles(true)}
                  >
                    {activeTab === 'concierge' ? (lang === 'de' ? 'Live-Demo buchen' : 'Book Live Demo Call') : activeTab === 'prototyping' ? (lang === 'de' ? 'Scope-Call buchen' : 'Book a Scope Call') : t.nav.bookTermin}
                  </Motion.button>
                </div>
                
                <div>
                  <h6 className="text-text-muted mb-4 md:mb-6">{t.nav.location}</h6>
                  <p className="text-lg md:text-xl font-light">Berlin, Germany<br/><span className="text-[10px] md:text-xs opacity-40 uppercase tracking-widest mt-2 block">{t.nav.remote}</span></p>
                </div>



                <div className="pt-8 block">
                  <div className="flex items-center gap-3">
                    <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: color }} />
                    <h6 className="text-text-muted">{t.nav.openForCollab}</h6>
                  </div>
                </div>
              </Motion.div>
            </div>
          </div>
          </Motion.div>
        )}
      </AnimatePresence>
    </>
  );
};


/* --------------------------------------------------------------------------------
   BOOKING MODAL COMPONENTS
-------------------------------------------------------------------------------- */

const LabelWithTooltip = ({ label, icon: Icon, color, tooltip }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="flex items-center justify-between gap-2">
      <label className="text-[10px] font-black uppercase tracking-widest text-text-muted flex items-center gap-2">
        {Icon && <Icon size={14} style={{ color }} />} {label}
      </label>
      <div className="relative">
        <button 
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          onMouseEnter={() => setIsOpen(true)}
          onMouseLeave={() => setIsOpen(false)}
          className="p-1 -m-1 transition-all duration-300"
        >
          <Info size={14} className={`text-text-muted transition-opacity ${isOpen ? 'opacity-100' : 'opacity-40'}`} />
        </button>
        <AnimatePresence>
          {isOpen && tooltip && (
            <Motion.div 
              initial={{ opacity: 0, y: -10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.95 }}
              className="absolute top-full right-0 mt-2 w-48 p-3 bg-surface-color border border-border-color rounded-lg text-[10px] font-medium leading-relaxed text-text-muted z-[110] shadow-2xl pointer-events-none"
            >
              {tooltip}
            </Motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

const FloatingInput = ({ label, icon: Icon, value = '', name, type = 'text', onChange, placeholder, required, color, isTextArea = false }) => {
  const [isFocused, setIsFocused] = useState(false);
  const hasValue = value && value.length > 0;

  const inputClasses = `w-full bg-surface-color/50 border border-border-color rounded-lg px-4 ${isTextArea ? 'pt-7 pb-3' : 'pt-6 md:pt-7 pb-1.5 md:pb-2'} !text-[16px] focus:outline-none focus:border-text-color/20 transition-all duration-300`;

  return (
    <div className="relative group">
      <div className={`absolute left-4 pointer-events-none transition-all duration-300 group-focus-within:opacity-100 opacity-40 ${isTextArea ? 'top-5' : 'top-1/2 -translate-y-1/2'}`}>
        {Icon && <Icon size={16} style={{ color: isFocused ? color : 'currentColor' }} />}
      </div>
      <div 
        className={`absolute left-11 transition-all duration-300 pointer-events-none uppercase tracking-widest font-black ${isFocused || hasValue ? 'top-1.5 md:top-2 text-[8px] opacity-100' : (isTextArea ? 'top-5 text-[10px] opacity-40' : 'top-1/2 -translate-y-1/2 text-[10px] opacity-40')}`} 
        style={{ color: (isFocused || hasValue) ? color : 'inherit' }}
      >
        {label}
      </div>
      {isTextArea ? (
        <textarea
          name={name}
          value={value}
          onChange={onChange}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          placeholder={isFocused ? placeholder : ''}
          required={required}
          className={`${inputClasses} pl-11 h-32 resize-none`}
        />
      ) : (
        <input
          type={type}
          name={name}
          value={value}
          onChange={onChange}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          placeholder={isFocused ? placeholder : ''}
          required={required}
          className={`${inputClasses} pl-11`}
        />
      )}
    </div>
  );
};

export const BookingModal = ({ isOpen, onClose, lang, color, activeTab, initialReasons }) => {
  const copy = BOOKING_CONTENT[lang] || BOOKING_CONTENT.en;
  const [stepIndex, setStepIndex] = useState(0);
  const [form, setForm] = useState({
    name: '',
    email: '',
    company: '',
    website: '',
    focus: getDefaultBookingFocus(activeTab),
    industry: '',
    reasons: initialReasons || [],
    painpoints: [],
    timeline: '',
    budget: '',
    details: ''
  });
  const [errors, setErrors] = useState({});
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    if (!isOpen) return undefined;

    setStepIndex(0);
    setErrors({});
    setIsConfirmed(false);
    setIsSubmitting(false);
    setSubmitError('');
    setForm({
      name: '',
      email: '',
      company: '',
      website: '',
      focus: getDefaultBookingFocus(activeTab),
      industry: '',
      reasons: initialReasons || [],
      painpoints: [],
      timeline: '',
      budget: '',
      details: ''
    });

    document.body.style.overflow = 'hidden';

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleEscape);

    return () => {
      document.body.style.overflow = 'unset';
      window.removeEventListener('keydown', handleEscape);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, activeTab]);

  const updateField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: '' }));
  };

  const toggleField = (field, id) => {
    setForm((current) => ({
      ...current,
      [field]: current[field].includes(id)
        ? current[field].filter((item) => item !== id)
        : [...current[field], id]
    }));
    setErrors((current) => ({ ...current, [field]: '' }));
  };

  const validateCurrentStep = () => {
    const nextErrors = {};

    if (stepIndex === 0) {
      if (form.focus.length === 0) nextErrors.focus = copy.validation.focus;
      if (form.reasons.length === 0) nextErrors.reasons = copy.validation.goals;
    }

    if (stepIndex === 1) {
      if (form.painpoints.length === 0) nextErrors.painpoints = copy.validation.painpoints;
    }

    if (stepIndex === 2) {
      if (!form.timeline) nextErrors.timeline = copy.validation.timeline;
      if (!form.budget) nextErrors.budget = copy.validation.budget;
    }

    if (stepIndex === 3) {
      if (!form.name.trim()) nextErrors.name = copy.validation.name;
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) nextErrors.email = copy.validation.email;
      if (!form.company.trim()) nextErrors.company = copy.validation.company;
      if (!form.website.trim()) nextErrors.website = copy.validation.website;
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const isCurrentStepInvalid = () => {
    if (stepIndex === 0) {
      return form.focus.length === 0 || form.reasons.length === 0;
    }
    if (stepIndex === 1) {
      return form.painpoints.length === 0;
    }
    if (stepIndex === 2) {
      return !form.timeline || !form.budget;
    }
    if (stepIndex === 3) {
      return !form.name.trim() || !form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()) || !form.company.trim() || !form.website.trim();
    }
    return false;
  };

  const handleNext = () => {
    if (validateCurrentStep()) {
      setStepIndex((current) => Math.min(current + 1, 3));
    }
  };

  const getConditionalOptions = (type) => {
    const options = new Set();
    form.focus.forEach(f => {
      if (copy[type] && copy[type][f]) {
        copy[type][f].forEach(opt => options.add(opt));
      }
    });
    return Array.from(options);
  };

  const getRelevantPainpoints = () => {
    if (form.focus.length === 0) return {};
    const painpointsByFocus = {};
    form.focus.forEach(f => {
      if (copy.painpoints && copy.painpoints[f]) {
        painpointsByFocus[f] = copy.painpoints[f];
      }
    });
    return painpointsByFocus;
  };

  const getFilteredPainpointsForFocus = (focusId) => {
    if (!copy.painpoints || !copy.painpoints[focusId]) return [];
    const allPainpoints = copy.painpoints[focusId];

    if (form.reasons.length === 0) return allPainpoints;

    const reasonToPainpointMap = lang === 'de' ? {
      design: {
        'Landingpage': ['Wenig Conversions', 'Hoher Drop-off', 'Slow Handoff', 'Mobile UX', 'Inkonsistenz'],
        'App Design': ['Inkonsistenz', 'Mobile UX', 'Hoher Drop-off', 'Wenig Conversions', 'Veralteter Look'],
        'Design System': ['Inkonsistenz', 'Slow Handoff', 'Hoher Drop-off', 'Veralteter Look'],
        'Redesign': ['Veralteter Look', 'Wenig Conversions', 'Hoher Drop-off', 'Inkonsistenz', 'Mobile UX'],
        'Rapid Prototype': ['Hoher Drop-off', 'Slow Handoff', 'Wenig Conversions', 'Inkonsistenz'],
        'Web-App': ['Mobile UX', 'Hoher Drop-off', 'Inkonsistenz', 'Slow Handoff', 'Wenig Conversions'],
        'Native App': ['Mobile UX', 'Inkonsistenz', 'Hoher Drop-off', 'Wenig Conversions', 'Veralteter Look'],
        'Web Development': ['Slow Handoff', 'Hoher Drop-off', 'Inkonsistenz', 'Mobile UX'],
        'Social Media Design': ['Wenig Conversions', 'Inkonsistenz', 'Hoher Drop-off', 'Veralteter Look'],
        'Motion Graphics': ['Inkonsistenz', 'Slow Handoff', 'Wenig Conversions', 'Hoher Drop-off']
      },
      ai: {
        'KI-Strategie': ['Teamüberlastung', 'Kundenzufriedenheit', 'Verpasste Chancen', 'Erreichbarkeit', 'Antwortzeit Leads'],
        'AI-Agenten': ['Teamüberlastung', 'Support Team Entlastung', 'Antwortzeit Leads', 'Kundenzufriedenheit', 'Verpasste Chancen'],
        'Voice AI': ['Erreichbarkeit', 'Support Team Entlastung', 'Antwortzeit Leads', 'Teamüberlastung', 'Kundenzufriedenheit'],
        'Automation': ['Teamüberlastung', 'Verpasste Chancen', 'Kundenzufriedenheit', 'Support Team Entlastung', 'Erreichbarkeit'],
        'Gen-AI Content': ['Teamüberlastung', 'Kundenzufriedenheit', 'Antwortzeit Leads', 'Support Team Entlastung'],
        'LLM Training': ['Support Team Entlastung', 'Kundenzufriedenheit', 'Teamüberlastung', 'Antwortzeit Leads']
      },
      consulting: {
        'UX/UI Audit': ['Hohe Fehlerraten', 'Falsche Tools / Tech', 'Ineffiziente Workflows', 'Budget Verschwendung'],
        'Design Audit': ['Hohe Fehlerraten', 'Ineffiziente Workflows', 'Falsche Tools / Tech', 'Budget Verschwendung'],
        'Performance Audit': ['Hohe Fehlerraten', 'Bottlenecks', 'Falsche Tools / Tech', 'Ineffiziente Workflows'],
        'Infrastruktur Audit': ['Legacy Code / System', 'Falsche Tools / Tech', 'Hohe Fehlerraten', 'Budget Verschwendung', 'Bottlenecks'],
        'KI Roadmaps': ['Fehlendes KI-Wissen', 'Hohe Kosten', 'Budget Verschwendung', 'Skalierungs-Angst'],
        'Prozess-Analyse': ['Ineffiziente Workflows', 'Hohe Fehlerraten', 'Falsche Tools / Tech', 'Budget Verschwendung'],
        'Scaling Strategie': ['Skalierungs-Angst', 'Budget Verschwendung', 'Hohe Kosten', 'Bottlenecks', 'Ineffiziente Workflows']
      },
      workshops: {
        'Team Onboarding': ['Mitarbeiter-Vorbehalte', 'Technologie-Überforderung', 'Fehlender Standard', 'Kein Praxisbezug'],
        'Prompt Engineering': ['Technologie-Überforderung', 'Kein Praxisbezug', 'Fehlender Standard', 'Mitarbeiter-Vorbehalte'],
        'Figma Training': ['Fehlender Standard', 'Kein Praxisbezug', 'Technologie-Überforderung', 'Mitarbeiter-Vorbehalte'],
        'Webflow Training': ['Fehlender Standard', 'Kein Praxisbezug', 'Technologie-Überforderung', 'Mitarbeiter-Vorbehalte'],
        'AI Workflow Setup': ['Ineffiziente Workflows', 'Technologie-Überforderung', 'Fehlender Standard', 'Kein Praxisbezug'],
        'Design Thinking': ['Kein Praxisbezug', 'Fehlender Standard', 'Mitarbeiter-Vorbehalte', 'Technologie-Überforderung'],
        'Best-Practice-Training': ['Mitarbeiter-Vorbehalte', 'Fehlender Standard', 'Kein Praxisbezug', 'Technologie-Überforderung']
      }
    } : {
      design: {
        'Landingpage': ['Low Conversions', 'High Drop-off', 'Slow Handoff', 'Mobile UX', 'Inconsistency'],
        'App Design': ['Inconsistency', 'Mobile UX', 'High Drop-off', 'Low Conversions', 'Outdated Look'],
        'Design System': ['Inconsistency', 'Slow Handoff', 'High Drop-off', 'Outdated Look'],
        'Redesign': ['Outdated Look', 'Low Conversions', 'High Drop-off', 'Inconsistency', 'Mobile UX'],
        'Rapid Prototype': ['High Drop-off', 'Slow Handoff', 'Low Conversions', 'Inconsistency'],
        'Web-App': ['Mobile UX', 'High Drop-off', 'Inconsistency', 'Slow Handoff', 'Low Conversions'],
        'Native App': ['Mobile UX', 'Inconsistency', 'High Drop-off', 'Low Conversions', 'Outdated Look'],
        'Web Development': ['Slow Handoff', 'High Drop-off', 'Inconsistency', 'Mobile UX'],
        'Social Media Design': ['Low Conversions', 'Inconsistency', 'High Drop-off', 'Outdated Look'],
        'Motion Graphics': ['Inconsistency', 'Slow Handoff', 'Low Conversions', 'High Drop-off']
      },
      ai: {
        'AI Strategy': ['Team Overload', 'Customer Satisfaction', 'Missed Opportunities', 'Reachability', 'Lead Response Time'],
        'AI Agents': ['Team Overload', 'Support Team Relief', 'Lead Response Time', 'Customer Satisfaction', 'Missed Opportunities'],
        'Voice AI': ['Reachability', 'Support Team Relief', 'Lead Response Time', 'Team Overload', 'Customer Satisfaction'],
        'Automation': ['Team Overload', 'Missed Opportunities', 'Customer Satisfaction', 'Support Team Relief', 'Reachability'],
        'Gen-AI Content': ['Team Overload', 'Customer Satisfaction', 'Lead Response Time', 'Support Team Relief'],
        'LLM Training': ['Support Team Relief', 'Customer Satisfaction', 'Team Overload', 'Lead Response Time']
      },
      consulting: {
        'UX/UI Audit': ['High Error Rates', 'Wrong Tools / Stack', 'Inefficient Workflows', 'Wasted Budgets'],
        'Design Audit': ['High Error Rates', 'Inefficient Workflows', 'Wrong Tools / Stack', 'Wasted Budgets'],
        'Performance Audit': ['High Error Rates', 'Bottlenecks', 'Wrong Tools / Stack', 'Inefficient Workflows'],
        'Infrastructure Audit': ['Legacy Code / Systems', 'Wrong Tools / Stack', 'High Error Rates', 'Wasted Budgets', 'Bottlenecks'],
        'AI Roadmaps': ['No Internal AI Expertise', 'High Costs', 'Wasted Budgets', 'Fear of Scaling'],
        'Process Analysis': ['Inefficient Workflows', 'High Error Rates', 'Wrong Tools / Stack', 'Wasted Budgets'],
        'Scaling Strategy': ['Fear of Scaling', 'Wasted Budgets', 'High Costs', 'Bottlenecks', 'Inefficient Workflows']
      },
      workshops: {
        'Team Onboarding': ['Staff AI Hesitancy', 'Tech Overload', 'No Setup Standard', 'No Practical Relevance'],
        'Prompt Engineering': ['Tech Overload', 'No Practical Relevance', 'No Setup Standard', 'Staff AI Hesitancy'],
        'Figma Training': ['No Setup Standard', 'No Practical Relevance', 'Tech Overload', 'Staff AI Hesitancy'],
        'Webflow Training': ['No Setup Standard', 'No Practical Relevance', 'Tech Overload', 'Staff AI Hesitancy'],
        'AI Workflow Setup': ['Inefficient Workflows', 'Tech Overload', 'No Setup Standard', 'No Practical Relevance'],
        'Design Thinking': ['No Practical Relevance', 'No Setup Standard', 'Staff AI Hesitancy', 'Tech Overload'],
        'Best Practices Training': ['Staff AI Hesitancy', 'No Setup Standard', 'No Practical Relevance', 'Tech Overload']
      }
    };

    const relevantPainpoints = new Set();
    form.reasons.forEach(reason => {
      if (reasonToPainpointMap[focusId] && reasonToPainpointMap[focusId][reason]) {
        reasonToPainpointMap[focusId][reason].forEach(p => relevantPainpoints.add(p));
      }
    });

    return relevantPainpoints.size > 0 ? Array.from(relevantPainpoints) : allPainpoints;
  };

  const handleSubmit = () => {
    if (!validateCurrentStep()) return;
    
    setIsSubmitting(true);
    setSubmitError('');

    // Strictly relative for PHP + .htaccess routing
    const targetUrl = '/api/booking';

    fetch(targetUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({
        ...buildBookingSubmission({ lang, form, copy }),
        type: 'general_inquiry'
      })
    })
      .then(async (response) => {
        const data = await response.json().catch(() => ({}));
        if (!response.ok || data.success === 'false') {
          throw new Error(data.message || 'Request failed');
        }
        setIsConfirmed(true);
      })
      .catch((error) => {
        if (window.location.protocol === 'file:') {
          setIsConfirmed(true);
          return;
        }
        setSubmitError(error.message || 'Error');
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <Motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[220] flex items-center justify-center bg-black/80 backdrop-blur-md p-4 overflow-hidden"
        onClick={onClose}
        style={{ pointerEvents: 'auto' }}
      >
        <Motion.div
          initial={{ y: 50, opacity: 0, scale: 0.95 }}
          animate={{ y: 0, opacity: 1, scale: 1 }}
          exit={{ y: 50, opacity: 0, scale: 0.95 }}
          className="w-full max-w-2xl max-h-[90vh] bg-bg-color border border-border-color rounded-2xl overflow-hidden shadow-2xl flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          {!isConfirmed && (
            <div className="relative p-6 md:p-8 pb-0 md:pb-0">
              <button onClick={onClose} className="absolute top-4 right-4 p-2 hover:bg-text-color/5 rounded-full transition-colors z-10">
                <X size={20} />
              </button>
              
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-[10px] font-black uppercase tracking-[0.3em]" style={{ color }}>Step {stepIndex + 1} / 4</span>
                  <div className="flex gap-1">
                    {[0, 1, 2, 3].map(i => (
                      <div key={i} className={`h-0.5 w-5 rounded-full transition-all duration-500 ${stepIndex >= i ? '' : 'bg-border-color'}`} style={{ backgroundColor: stepIndex >= i ? color : '' }} />
                    ))}
                  </div>
                </div>
                <h4 className="text-xl md:text-2xl font-black tracking-tight mb-1">
                  {copy.title}
                </h4>
              </div>
            </div>
          )}

          {/* Body */}
          <div
            className={`${isConfirmed ? 'p-10' : 'p-6 md:p-8 pt-6 md:pt-8'} overflow-y-auto custom-scrollbar flex-1 scroll-smooth`}
            style={{ overscrollBehavior: 'contain' }}
            onWheel={(e) => e.stopPropagation()}
            onTouchMove={(e) => e.stopPropagation()}
          >
            <AnimatePresence mode="wait">
              {isConfirmed ? (
                <Motion.div
                  key="success"
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="flex flex-col items-center text-center py-8 relative"
                >
                  <button onClick={onClose} className="absolute -top-4 -right-4 p-2 hover:bg-text-color/5 rounded-full transition-colors z-10">
                    <X size={20} />
                  </button>
                  <div 
                    className="w-20 h-20 rounded-full aspect-square flex-shrink-0 flex items-center justify-center mb-8" 
                    style={{ backgroundColor: color.startsWith('var') ? `color-mix(in srgb, ${color} 20%, transparent)` : `${color}20` }}
                  >
                    <Check size={48} style={{ color }} />
                  </div>
                  <h4 className="text-3xl md:text-5xl font-black mb-6 leading-none">{copy.confirmation.title}</h4>
                  <p className="text-text-muted max-w-md mx-auto leading-relaxed text-sm md:text-base font-light">{copy.confirmation.description}</p>
                </Motion.div>
              ) : (
                <>
                  {stepIndex === 0 && (
                    <Motion.div key="step1" initial={{ x: 20, opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={{ x: -20, opacity: 0 }} className="space-y-8">
                      <div className="space-y-4">
                        <LabelWithTooltip label={copy.labels.focus} icon={Zap} color={color} tooltip={copy.tooltips.focus} />
                        <div className="flex flex-wrap gap-2">
                          {['design', 'ai', 'consulting', 'workshops'].map(id => (
                            <button
                              key={id}
                              type="button"
                              onClick={() => toggleField('focus', id)}
                              className={`px-4 py-2 md:px-5 md:py-2.5 rounded-full text-[10px] md:text-xs font-black border transition-all duration-300 ${form.focus.includes(id) ? 'text-white' : 'border-border-color text-text-muted hover:border-text-color/30'}`}
                              style={{ backgroundColor: form.focus.includes(id) ? color : 'transparent', borderColor: form.focus.includes(id) ? color : '' }}
                            >
                              {id === 'design' ? 'Design Solutions' : 
                               id === 'ai' ? 'AI Solutions' : 
                               id === 'consulting' ? 'Consulting' : 'Workshops'}
                            </button>
                          ))}
                        </div>
                        {errors.focus && <p className="text-[10px] font-bold uppercase tracking-widest text-red-500">{errors.focus}</p>}
                      </div>

                      <div className="space-y-4">
                        <LabelWithTooltip label={copy.labels.reasons} icon={MessageSquare} color={color} tooltip={copy.tooltips.goals} />
                        <div className="flex flex-wrap gap-2">
                          {getConditionalOptions('goals').length > 0 ? (
                            getConditionalOptions('goals').map(opt => (
                              <button
                                key={opt}
                                type="button"
                                onClick={() => toggleField('reasons', opt)}
                                className={`px-4 py-2 md:px-5 md:py-2.5 rounded-full text-[10px] md:text-xs font-black border transition-all duration-300 ${form.reasons.includes(opt) ? 'text-white' : 'border-border-color text-text-muted hover:border-text-color/30'}`}
                                style={{ backgroundColor: form.reasons.includes(opt) ? color : 'transparent', borderColor: form.reasons.includes(opt) ? color : '' }}
                              >
                                {opt}
                              </button>
                            ))
                          ) : (
                            <p className="text-[10px] uppercase font-black tracking-widest text-text-muted/60 italic">Please select a solution area first</p>
                          )}
                        </div>
                        {errors.reasons && <p className="text-[10px] font-bold uppercase tracking-widest text-red-500">{errors.reasons}</p>}
                      </div>
                    </Motion.div>
                  )}

                  {stepIndex === 1 && (
                    <Motion.div key="step2" initial={{ x: 20, opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={{ x: -20, opacity: 0 }} className="space-y-6">
                      <div className="space-y-4">
                        <LabelWithTooltip label={copy.labels.painpoints} icon={AlertCircle} color={color} tooltip={copy.tooltips.painpoints} />
                        {form.focus.length > 0 ? (
                          <div className="space-y-5">
                            {form.focus.map(focusId => {
                              let focusPainpoints = copy.painpoints[focusId] || [];

                              // Filter problems based on selected achievements
                              if (form.reasons.length > 0) {
                                const reasonToPainpointMap = lang === 'de' ? {
                                  design: {
                                    'Landingpage': ['Wenig Conversions', 'Hoher Drop-off', 'Slow Handoff', 'Mobile UX', 'Inkonsistenz'],
                                    'App Design': ['Inkonsistenz', 'Mobile UX', 'Hoher Drop-off', 'Wenig Conversions', 'Veralteter Look'],
                                    'Design System': ['Inkonsistenz', 'Slow Handoff', 'Hoher Drop-off', 'Veralteter Look'],
                                    'Redesign': ['Veralteter Look', 'Wenig Conversions', 'Hoher Drop-off', 'Inkonsistenz', 'Mobile UX'],
                                    'Rapid Prototype': ['Hoher Drop-off', 'Slow Handoff', 'Wenig Conversions', 'Inkonsistenz'],
                                    'Web-App': ['Mobile UX', 'Hoher Drop-off', 'Inkonsistenz', 'Slow Handoff', 'Wenig Conversions'],
                                    'Native App': ['Mobile UX', 'Inkonsistenz', 'Hoher Drop-off', 'Wenig Conversions', 'Veralteter Look'],
                                    'Web Development': ['Slow Handoff', 'Hoher Drop-off', 'Inkonsistenz', 'Mobile UX'],
                                    'Social Media Design': ['Wenig Conversions', 'Inkonsistenz', 'Hoher Drop-off', 'Veralteter Look'],
                                    'Motion Graphics': ['Inkonsistenz', 'Slow Handoff', 'Wenig Conversions', 'Hoher Drop-off']
                                  },
                                  ai: {
                                    'KI-Strategie': ['Teamüberlastung', 'Kundenzufriedenheit', 'Verpasste Chancen', 'Erreichbarkeit', 'Antwortzeit Leads'],
                                    'AI-Agenten': ['Teamüberlastung', 'Support Team Entlastung', 'Antwortzeit Leads', 'Kundenzufriedenheit', 'Verpasste Chancen'],
                                    'Voice AI': ['Erreichbarkeit', 'Support Team Entlastung', 'Antwortzeit Leads', 'Teamüberlastung', 'Kundenzufriedenheit'],
                                    'Automation': ['Teamüberlastung', 'Verpasste Chancen', 'Kundenzufriedenheit', 'Support Team Entlastung', 'Erreichbarkeit'],
                                    'Gen-AI Content': ['Teamüberlastung', 'Kundenzufriedenheit', 'Antwortzeit Leads', 'Support Team Entlastung'],
                                    'LLM Training': ['Support Team Entlastung', 'Kundenzufriedenheit', 'Teamüberlastung', 'Antwortzeit Leads']
                                  },
                                  consulting: {
                                    'UX/UI Audit': ['Hohe Fehlerraten', 'Falsche Tools / Tech', 'Ineffiziente Workflows', 'Budget Verschwendung'],
                                    'Design Audit': ['Hohe Fehlerraten', 'Ineffiziente Workflows', 'Falsche Tools / Tech', 'Budget Verschwendung'],
                                    'Performance Audit': ['Hohe Fehlerraten', 'Bottlenecks', 'Falsche Tools / Tech', 'Ineffiziente Workflows'],
                                    'Infrastruktur Audit': ['Legacy Code / System', 'Falsche Tools / Tech', 'Hohe Fehlerraten', 'Budget Verschwendung', 'Bottlenecks'],
                                    'KI Roadmaps': ['Fehlendes KI-Wissen', 'Hohe Kosten', 'Budget Verschwendung', 'Skalierungs-Angst'],
                                    'Prozess-Analyse': ['Ineffiziente Workflows', 'Hohe Fehlerraten', 'Falsche Tools / Tech', 'Budget Verschwendung'],
                                    'Scaling Strategie': ['Skalierungs-Angst', 'Budget Verschwendung', 'Hohe Kosten', 'Bottlenecks', 'Ineffiziente Workflows']
                                  },
                                  workshops: {
                                    'Team Onboarding': ['Mitarbeiter-Vorbehalte', 'Technologie-Überforderung', 'Fehlender Standard', 'Kein Praxisbezug'],
                                    'Prompt Engineering': ['Technologie-Überforderung', 'Kein Praxisbezug', 'Fehlender Standard', 'Mitarbeiter-Vorbehalte'],
                                    'Figma Training': ['Fehlender Standard', 'Kein Praxisbezug', 'Technologie-Überforderung', 'Mitarbeiter-Vorbehalte'],
                                    'Webflow Training': ['Fehlender Standard', 'Kein Praxisbezug', 'Technologie-Überforderung', 'Mitarbeiter-Vorbehalte'],
                                    'AI Workflow Setup': ['Ineffiziente Workflows', 'Technologie-Überforderung', 'Fehlender Standard', 'Kein Praxisbezug'],
                                    'Design Thinking': ['Kein Praxisbezug', 'Fehlender Standard', 'Mitarbeiter-Vorbehalte', 'Technologie-Überforderung'],
                                    'Best-Practice-Training': ['Mitarbeiter-Vorbehalte', 'Fehlender Standard', 'Kein Praxisbezug', 'Technologie-Überforderung']
                                  }
                                } : {
                                  design: {
                                    'Landingpage': ['Low Conversions', 'High Drop-off', 'Slow Handoff', 'Mobile UX', 'Inconsistency'],
                                    'App Design': ['Inconsistency', 'Mobile UX', 'High Drop-off', 'Low Conversions', 'Outdated Look'],
                                    'Design System': ['Inconsistency', 'Slow Handoff', 'High Drop-off', 'Outdated Look'],
                                    'Redesign': ['Outdated Look', 'Low Conversions', 'High Drop-off', 'Inconsistency', 'Mobile UX'],
                                    'Rapid Prototype': ['High Drop-off', 'Slow Handoff', 'Low Conversions', 'Inconsistency'],
                                    'Web-App': ['Mobile UX', 'High Drop-off', 'Inconsistency', 'Slow Handoff', 'Low Conversions'],
                                    'Native App': ['Mobile UX', 'Inconsistency', 'High Drop-off', 'Low Conversions', 'Outdated Look'],
                                    'Web Development': ['Slow Handoff', 'High Drop-off', 'Inconsistency', 'Mobile UX'],
                                    'Social Media Design': ['Low Conversions', 'Inconsistency', 'High Drop-off', 'Outdated Look'],
                                    'Motion Graphics': ['Inconsistency', 'Slow Handoff', 'Low Conversions', 'High Drop-off']
                                  },
                                  ai: {
                                    'AI Strategy': ['Team Overload', 'Customer Satisfaction', 'Missed Opportunities', 'Reachability', 'Lead Response Time'],
                                    'AI Agents': ['Team Overload', 'Support Team Relief', 'Lead Response Time', 'Customer Satisfaction', 'Missed Opportunities'],
                                    'Voice AI': ['Reachability', 'Support Team Relief', 'Lead Response Time', 'Team Overload', 'Customer Satisfaction'],
                                    'Automation': ['Team Overload', 'Missed Opportunities', 'Customer Satisfaction', 'Support Team Relief', 'Reachability'],
                                    'Gen-AI Content': ['Team Overload', 'Customer Satisfaction', 'Lead Response Time', 'Support Team Relief'],
                                    'LLM Training': ['Support Team Relief', 'Customer Satisfaction', 'Team Overload', 'Lead Response Time']
                                  },
                                  consulting: {
                                    'UX/UI Audit': ['High Error Rates', 'Wrong Tools / Stack', 'Inefficient Workflows', 'Wasted Budgets'],
                                    'Design Audit': ['High Error Rates', 'Inefficient Workflows', 'Wrong Tools / Stack', 'Wasted Budgets'],
                                    'Performance Audit': ['High Error Rates', 'Bottlenecks', 'Wrong Tools / Stack', 'Inefficient Workflows'],
                                    'Infrastructure Audit': ['Legacy Code / Systems', 'Wrong Tools / Stack', 'High Error Rates', 'Wasted Budgets', 'Bottlenecks'],
                                    'AI Roadmaps': ['No Internal AI Expertise', 'High Costs', 'Wasted Budgets', 'Fear of Scaling'],
                                    'Process Analysis': ['Inefficient Workflows', 'High Error Rates', 'Wrong Tools / Stack', 'Wasted Budgets'],
                                    'Scaling Strategy': ['Fear of Scaling', 'Wasted Budgets', 'High Costs', 'Bottlenecks', 'Inefficient Workflows']
                                  },
                                  workshops: {
                                    'Team Onboarding': ['Staff AI Hesitancy', 'Tech Overload', 'No Setup Standard', 'No Practical Relevance'],
                                    'Prompt Engineering': ['Tech Overload', 'No Practical Relevance', 'No Setup Standard', 'Staff AI Hesitancy'],
                                    'Figma Training': ['No Setup Standard', 'No Practical Relevance', 'Tech Overload', 'Staff AI Hesitancy'],
                                    'Webflow Training': ['No Setup Standard', 'No Practical Relevance', 'Tech Overload', 'Staff AI Hesitancy'],
                                    'AI Workflow Setup': ['Inefficient Workflows', 'Tech Overload', 'No Setup Standard', 'No Practical Relevance'],
                                    'Design Thinking': ['No Practical Relevance', 'No Setup Standard', 'Staff AI Hesitancy', 'Tech Overload'],
                                    'Best Practices Training': ['Staff AI Hesitancy', 'No Setup Standard', 'No Practical Relevance', 'Tech Overload']
                                  }
                                };

                                const filteredSet = new Set();
                                form.reasons.forEach(reason => {
                                  if (reasonToPainpointMap[focusId] && reasonToPainpointMap[focusId][reason]) {
                                    reasonToPainpointMap[focusId][reason].forEach(p => filteredSet.add(p));
                                  }
                                });
                                if (filteredSet.size > 0) {
                                  focusPainpoints = Array.from(filteredSet);
                                }
                              }

                              const focusLabel = focusId === 'design' ? (lang === 'de' ? 'Design Lösungen' : 'Design Solutions') :
                                               focusId === 'ai' ? (lang === 'de' ? 'KI Lösungen' : 'AI Solutions') :
                                               focusId === 'consulting' ? (lang === 'de' ? 'Beratung' : 'Consulting') : (lang === 'de' ? 'Workshops' : 'Workshops');
                              return (
                                <div key={`${focusId}-${form.reasons.join(',')}`} className="space-y-2">
                                  <p className="text-[9px] font-bold uppercase tracking-widest text-text-muted/60">{focusLabel}</p>
                                  <div className="flex flex-wrap gap-2">
                                    {focusPainpoints.map(opt => (
                                      <button
                                        key={opt}
                                        type="button"
                                        onClick={() => toggleField('painpoints', opt)}
                                        className={`px-3 py-2 md:px-4 md:py-2 rounded-full text-[9px] md:text-xs font-bold border transition-all duration-300 ${form.painpoints.includes(opt) ? 'text-white' : 'border-border-color text-text-muted hover:border-text-color/30'}`}
                                        style={{ backgroundColor: form.painpoints.includes(opt) ? color : 'transparent', borderColor: form.painpoints.includes(opt) ? color : '' }}
                                      >
                                        {opt}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <p className="text-[10px] uppercase font-black tracking-widest text-text-muted/60 italic">Please select a solution area first</p>
                        )}
                        {errors.painpoints && <p className="text-[10px] font-bold uppercase tracking-widest text-red-500">{errors.painpoints}</p>}
                      </div>
                    </Motion.div>
                  )}

                  {stepIndex === 2 && (
                    <Motion.div key="step3" initial={{ x: 20, opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={{ x: -20, opacity: 0 }} className="space-y-6">
                      <div className="space-y-4">
                        <LabelWithTooltip label={copy.labels.timeline} icon={Calendar} color={color} tooltip={copy.tooltips.timeline} />
                        <div className="flex flex-wrap gap-2">
                          {copy.timelineOptions.map(opt => (
                            <button
                              key={opt.id}
                              type="button"
                              onClick={() => updateField('timeline', opt.id)}
                              className={`px-4 py-2 md:px-5 md:py-2.5 rounded-full text-[10px] md:text-xs font-black border transition-all duration-300 ${form.timeline === opt.id ? 'text-white' : 'border-border-color text-text-muted hover:border-text-color/30'}`}
                              style={{ backgroundColor: form.timeline === opt.id ? color : 'transparent', borderColor: form.timeline === opt.id ? color : '' }}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                        {errors.timeline && <p className="text-[10px] font-bold uppercase tracking-widest text-red-500">{errors.timeline}</p>}
                      </div>

                      <div className="space-y-4">
                        <LabelWithTooltip label={copy.labels.budget} icon={BarChart || Zap} color={color} tooltip={copy.tooltips.budget} />
                        <div className="flex flex-wrap gap-2">
                          {copy.budgetOptions.map(opt => (
                            <button
                              key={opt.id}
                              type="button"
                              onClick={() => updateField('budget', opt.id)}
                              className={`px-4 py-2 md:px-5 md:py-2.5 rounded-full text-[10px] md:text-xs font-black border transition-all duration-300 ${form.budget === opt.id ? 'text-white' : 'border-border-color text-text-muted hover:border-text-color/30'}`}
                              style={{ backgroundColor: form.budget === opt.id ? color : 'transparent', borderColor: form.budget === opt.id ? color : '' }}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                        {errors.budget && <p className="text-[10px] font-bold uppercase tracking-widest text-red-500">{errors.budget}</p>}
                      </div>
                    </Motion.div>
                  )}

                  {stepIndex === 3 && (
                    <Motion.div key="step4" initial={{ x: 20, opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={{ x: -20, opacity: 0 }} className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mt-0">
                        <FloatingInput label={copy.labels.name} icon={User} name="name" required value={form.name} onChange={(e) => updateField('name', e.target.value)} color={color} />
                        <FloatingInput label={copy.labels.email} icon={Mail} type="email" name="email" required value={form.email} onChange={(e) => updateField('email', e.target.value)} color={color} />
                        <FloatingInput label={copy.labels.company} icon={Building} name="company" required value={form.company} onChange={(e) => updateField('company', e.target.value)} color={color} />
                        <FloatingInput label={copy.labels.website} icon={Globe} name="website" required value={form.website} onChange={(e) => updateField('website', e.target.value)} color={color} />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 -mt-4">
                        {errors.name && <p className="text-[10px] font-bold uppercase tracking-widest text-red-500">{errors.name}</p>}
                        {errors.email && <p className="text-[10px] font-bold uppercase tracking-widest text-red-500">{errors.email}</p>}
                        {errors.company && <p className="text-[10px] font-bold uppercase tracking-widest text-red-500">{errors.company}</p>}
                        {errors.website && <p className="text-[10px] font-bold uppercase tracking-widest text-red-500">{errors.website}</p>}
                      </div>

                      <div className="space-y-4 pt-2">
                        <FloatingInput label={copy.labels.details} icon={MessageSquare} name="details" isTextArea placeholder={copy.placeholders.details} value={form.details} onChange={(e) => updateField('details', e.target.value)} color={color} />
                      </div>
                    </Motion.div>
                  )}
                </>
              )}
            </AnimatePresence>
          </div>

          {/* Footer Buttons */}
          <div className="p-4 md:p-8 pt-4 md:pt-4 border-t border-border-color flex justify-between items-center gap-3 bg-bg-color">
            {!isConfirmed ? (
              <>
                <button
                  onClick={stepIndex === 0 ? onClose : () => setStepIndex(prev => prev - 1)}
                  className="px-4 md:px-8 py-2.5 md:py-3 text-[10px] md:text-xs font-black uppercase tracking-widest border border-border-color rounded-lg hover:bg-text-color/5 transition-colors text-text-muted hover:text-text-color"
                >
                  {stepIndex === 0 ? copy.buttons.close : copy.buttons.back}
                </button>
                
                <div className="flex flex-col items-end gap-2">
                  {submitError && <span className="text-[10px] text-red-500 uppercase font-black">{submitError}</span>}
                  <button
                    onClick={stepIndex === 3 ? handleSubmit : handleNext}
                    disabled={isSubmitting || isCurrentStepInvalid()}
                    className="group relative px-4 md:px-10 py-2.5 md:py-3 text-[10px] md:text-xs font-black uppercase tracking-widest text-white rounded-lg overflow-hidden disabled:opacity-50 flex items-center gap-2"
                    style={{ backgroundColor: color }}
                  >
                    <span className="relative z-10 flex items-center gap-2">
                      {stepIndex === 3 ? (isSubmitting ? copy.buttons.sending : copy.buttons.send) : copy.buttons.next}
                      {stepIndex < 3 && <ArrowRight size={14} />}
                    </span>
                  </button>
                </div>
              </>
            ) : (
              <button
                onClick={onClose}
                className="w-full py-4 text-xs font-black uppercase tracking-widest text-white rounded-lg transition-colors"
                style={{ backgroundColor: color }}
              >
                {copy.buttons.close}
              </button>
            )}
          </div>
        </Motion.div>
      </Motion.div>
    </AnimatePresence>
  );
};


const ProjectItem = ({ project, staticProject, i, theme, color, setExpandedVideo }) => {
  const isLight = theme === 'light';
  const [isHovered, setIsHovered] = useState(false);

  return (
    <Motion.div 
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      viewport={{ once: true, amount: 0.1 }}
      className="relative overflow-hidden bg-bg-color group flex flex-col"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div 
        className="w-full relative overflow-hidden aspect-video cursor-zoom-in rounded-[4px]"
        onClick={() => staticProject.video && setExpandedVideo(staticProject.video)}
      >
        <Motion.div 
          whileHover={{ scale: 1.05 }}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          className="w-full h-full"
        >
          {staticProject.video ? (
            <video 
              src={staticProject.video}
              autoPlay
              loop
              muted
              playsInline
              className="w-full h-full object-cover"
            />
          ) : (
            <img 
              src={staticProject.image} 
              alt={`CRE8NOW: ${project.title}, ${project.category} Projekt`}
              width={800}
              height={450}
              loading="lazy"
              className="w-full h-full object-cover"
            />
          )}
        </Motion.div>
        
        {/* Hover Overlay with Zoom Icon */}
        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-all duration-500 flex items-center justify-center pointer-events-none">
          <div className="w-16 h-16 rounded-full bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center text-white">
            <Maximize2 size={24} />
          </div>
        </div>
      </div>

      <div className="px-8 md:px-0 py-12 md:py-14 flex flex-col bg-bg-color flex-1">
        <div className="mb-4">
          <h6 className={`mb-4 ${isLight ? 'text-text-color/70' : 'text-text-muted/60'}`}>
            {project.category}
          </h6>
          <h4 className={`font-black leading-[0.8] tracking-tighter w-full ${isLight ? 'text-text-color' : 'text-white'}`}>
            {project.title}
          </h4>
        </div>

        <div className="mt-2 pt-4 border-t border-border-color/10">
          <div className="flex flex-wrap gap-2 w-full">
            {project.tags && project.tags.map((tag, idx) => (
              <span
                key={idx}
                className={`text-[10px] font-bold uppercase tracking-[0.2em] px-3 py-1.5 border rounded-[4px] ${isLight ? 'text-text-muted border-border-color' : 'text-white/70 border-white/10'}`}
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      </div>
    </Motion.div>
  );
};


// The site footer, including the full link columns.
//
// Was inline in LandingPage, which meant the solution pages and CRE8APPS each
// carried a stripped-down footer of their own — three links instead of the
// twenty-five that make up the site's internal link graph. Every page now ships
// the same one, so the 19 solution pages link to each other and to every
// section, on every page rather than only from the home page.
//
// setActiveTab/setView are optional. LandingPage passes them and keeps its
// client-side tab switching; pages that are not LandingPage leave them out and
// the anchors simply navigate to their href, which is already the real URL.
export const SiteFooter = ({ theme, activeTab, lang, color, t, setActiveTab, setView }) => {
  const tr = t || TRANSLATIONS[lang] || TRANSLATIONS.de;
  const navTab = (e, type, tab) => {
    trackEvent('footer_link_click', { type });
    if (!setActiveTab) return; // real navigation
    e.preventDefault();
    setActiveTab(tab);
    smoothScrollTo(0);
  };
  const navView = (e, type, view) => {
    trackEvent('footer_link_click', { type });
    if (!setView) return; // real navigation
    e.preventDefault();
    setView(view);
  };
  return (
      <footer className="py-16 md:py-20 border-t border-border-color bg-bg-color">
          <div className="w-full px-[8vw]">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[1.3fr_1fr_1fr_1fr] gap-x-8 gap-y-12 lg:gap-x-10">
              {/* Brand — logo, tagline, address, LinkedIn */}
              <div className="sm:col-span-2 lg:col-span-1 flex flex-col items-start gap-5">
                {/* The logo links home. Without it nothing on the site linked
                    to "/" at all — the strongest page had zero inbound internal
                    links, since the solution pages' old "Zur Hauptseite" entry
                    disappeared when their footers were unified. */}
                <a
                  href="/"
                  aria-label={lang === 'de' ? 'Zur Startseite' : 'To the homepage'}
                  onClick={() => trackEvent('footer_link_click', { type: 'home' })}
                  className="inline-flex"
                >
                  <Logo theme={theme} activeTab={activeTab} forceFull className="h-7 w-auto" />
                </a>
                <p className="text-sm font-light opacity-60 max-w-[26ch]">
                  {lang === 'de' ? 'Design & AI Agentur aus Berlin.' : 'Design & AI Agency from Berlin.'}
                </p>
                <address className="not-italic text-sm font-light opacity-60 leading-relaxed">
                  CRE8NOW<br />
                  Bänschstrasse 60<br />
                  10247 Berlin
                </address>
                <a
                  href="https://www.linkedin.com/company/92680415"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackEvent('footer_link_click', { type: 'linkedin' })}
                  style={{ '--footer-hover-color': color }}
                  className="text-[10px] font-bold tracking-widest uppercase opacity-70 hover:opacity-100 hover:text-[var(--footer-hover-color)] transition-all duration-300"
                >
                  LinkedIn
                </a>
              </div>
  
              {/* Design — the 10 SEO solution subpages, not in main nav,
                  only reachable from the expertise list and here. */}
              <div className="flex flex-col items-start gap-3.5">
                <h6 className="text-text-muted text-[10px] font-bold tracking-widest uppercase mb-1">{tr.nav.design}</h6>
                {[
                  { type: 'ux-design', label: 'UX Design', slug: 'ux-design' },
                  { type: 'consulting', label: 'Consulting', slug: 'consulting' },
                  { type: 'ui-design', label: 'UI Design', slug: 'ui-design' },
                  { type: 'ix-design', label: 'IX Design', slug: 'ix-design' },
                  { type: 'strategy', label: 'Strategy', slug: 'strategy' },
                  { type: 'prototyping', label: 'Prototyping', slug: 'prototyping' },
                  { type: 'seo', label: 'SEO / GEO', slug: 'seo' },
                  { type: 'development', label: lang === 'de' ? 'Entwicklung' : 'Development', slug: 'development' },
                  { type: 'social-ads', label: 'Social Ads', slug: 'social-ads' },
                  { type: 'trainings', label: 'Trainings', slug: 'trainings' },
                ].map((item) => (
                  <a
                    key={item.type}
                    href={`/${lang}/design/${item.slug}`}
                    onClick={() => trackEvent('footer_link_click', { type: item.type })}
                    style={{ '--footer-hover-color': COLORS.design }}
                    className="text-[10px] font-bold tracking-widest uppercase opacity-70 hover:opacity-100 hover:translate-x-0.5 hover:text-[var(--footer-hover-color)] transition-all duration-300"
                  >
                    {item.label}
                  </a>
                ))}
              </div>
  
              {/* AI — the 9 SEO solution subpages, same convention as Design. */}
              <div className="flex flex-col items-start gap-3.5">
                <h6 className="text-text-muted text-[10px] font-bold tracking-widest uppercase mb-1">{tr.nav.ai}</h6>
                {[
                  { type: 'ai-consulting', label: lang === 'de' ? 'KI-Beratung' : 'AI Consulting', slug: 'ai-consulting' },
                  { type: 'ai-agents', label: lang === 'de' ? 'KI-Agenten' : 'AI Agents', slug: 'ai-agents' },
                  { type: 'digital-concierge', label: 'Digital Concierge', slug: 'digital-concierge' },
                  { type: 'ai-pipelines', label: lang === 'de' ? 'KI-Pipelines' : 'AI Pipelines', slug: 'ai-pipelines' },
                  { type: 'ai-content', label: 'AI Content', slug: 'ai-content' },
                  { type: 'ai-workshops', label: lang === 'de' ? 'KI-Workshops' : 'AI Workshops', slug: 'ai-workshops' },
                  { type: 'ai-film-production', label: lang === 'de' ? 'KI-Film Produktion' : 'AI Film Production', slug: 'ai-film-production' },
                  { type: 'custom-ai-development', label: lang === 'de' ? 'Custom AI-Entwicklung' : 'Custom AI Development', slug: 'custom-ai-development' },
                  { type: 'ai-voice-cloning', label: 'AI Voice & Image Cloning', slug: 'ai-voice-cloning' },
                ].map((item) => (
                  <a
                    key={item.type}
                    href={`/${lang}/ai/${item.slug}`}
                    onClick={() => trackEvent('footer_link_click', { type: item.type })}
                    style={{ '--footer-hover-color': COLORS.ai }}
                    className="text-[10px] font-bold tracking-widest uppercase opacity-70 hover:opacity-100 hover:translate-x-0.5 hover:text-[var(--footer-hover-color)] transition-all duration-300"
                  >
                    {item.label}
                  </a>
                ))}
              </div>
  
              {/* Company — the other real sections/pages, plus legal. */}
              <div className="flex flex-col items-start gap-3.5">
                <h6 className="text-text-muted text-[10px] font-bold tracking-widest uppercase mb-1">{lang === 'de' ? 'Unternehmen' : 'Company'}</h6>
                <a
                  href={TAB_PATHS.cre8guide[lang]}
                  onClick={(e) => navTab(e, 'cre8guide', 'cre8guide')}
                  style={{ '--footer-hover-color': color }}
                  className="text-[10px] font-bold tracking-widest uppercase opacity-70 hover:opacity-100 hover:translate-x-0.5 hover:text-[var(--footer-hover-color)] transition-all duration-300"
                >
                  CRE8GUIDE
                </a>
                <a
                  href={lang === 'de' ? '/de/cre8apps' : '/en/cre8apps'}
                  onClick={(e) => navView(e, 'cre8apps', 'cre8apps')}
                  style={{ '--footer-hover-color': color }}
                  className="text-[10px] font-bold tracking-widest uppercase opacity-70 hover:opacity-100 hover:translate-x-0.5 hover:text-[var(--footer-hover-color)] transition-all duration-300"
                >
                  CRE8APPS
                </a>
                <a
                  href={TAB_PATHS.concierge[lang]}
                  onClick={(e) => navTab(e, 'concierge', 'concierge')}
                  style={{ '--footer-hover-color': COLORS.concierge }}
                  className="text-[10px] font-bold tracking-widest uppercase opacity-70 hover:opacity-100 hover:translate-x-0.5 hover:text-[var(--footer-hover-color)] transition-all duration-300"
                >
                  Concierge Demo
                </a>
                <a
                  href={TAB_PATHS.prototyping[lang]}
                  onClick={(e) => navTab(e, 'prototyping', 'prototyping')}
                  style={{ '--footer-hover-color': COLORS.prototyping }}
                  className="text-[10px] font-bold tracking-widest uppercase opacity-70 hover:opacity-100 hover:translate-x-0.5 hover:text-[var(--footer-hover-color)] transition-all duration-300"
                >
                  Rapid Prototyping
                </a>
                <a
                  href={lang === 'de' ? '/de/impressum' : '/en/impressum'}
                  onClick={(e) => navView(e, 'impressum', 'impressum')}
                  style={{ '--footer-hover-color': color }}
                  className="text-[10px] font-bold tracking-widest uppercase opacity-70 hover:opacity-100 hover:translate-x-0.5 hover:text-[var(--footer-hover-color)] transition-all duration-300"
                >
                  {lang === 'de' ? 'Impressum' : 'Imprint'}
                </a>
              </div>
            </div>
  
            <div className="mt-14 md:mt-16 pt-6 border-t border-border-color flex flex-col-reverse sm:flex-row items-center justify-between gap-4">
              <h6 className="text-[10px] font-bold tracking-widest uppercase opacity-50 sm:flex-1">© 2026 CRE8NOW</h6>
              {/* Centered. A real anchor to this page's other-language URL — the
                  nav menu's language switcher does the same thing, but that menu
                  is never rendered until it is opened, so it contributes no
                  crawlable link, which left every German page with no inbound
                  links at all. This one is always in the markup. */}
              <LangAlternateLink lang={lang} color={color} />
              <h6 className="text-[10px] font-bold tracking-widest uppercase opacity-50 sm:flex-1 sm:text-right">{lang === 'de' ? 'Berlin, Deutschland' : 'Berlin, Germany'}</h6>
            </div>
          </div>
        </footer>
  );
};

const LandingPage = ({ setView, theme, setTheme, lang, setLang, initialTab }) => {
  const [activeTab, setShownTab] = useState(() => {
    if (typeof window !== 'undefined') {
      // Real path (e.g. /de/design, /en/ai, /de/cre8guide, /concierge) wins
      // first — this is what makes these tabs independently crawlable/
      // indexable pages instead of query-string variants of "/".
      const path = window.location.pathname.replace(/\/$/, '');
      const pathTab = Object.keys(TAB_PATHS).find(
        (tab) => TAB_PATHS[tab].de === path || TAB_PATHS[tab].en === path
      );
      if (pathTab) return pathTab;
      if (path === '/concierge') return 'concierge'; // legacy bare alias, kept alive
      if (path === '/cre8guide') return 'cre8guide'; // legacy bare alias, kept alive
      if (['ai', 'design', 'cre8guide', 'concierge', 'prototyping'].includes(initialTab)) return initialTab;
      const params = new URLSearchParams(window.location.search);
      const tab = params.get('tab');
      if (['ai', 'design', 'cre8guide', 'concierge', 'prototyping'].includes(tab)) return tab;
      const hostname = window.location.hostname;
      if (hostname.includes('cre8now.ai')) return 'ai';
      if (hostname.includes('cre8now.design')) return 'design';
      const saved = localStorage.getItem('cre8now-active-tab');
      return ['ai', 'design', 'cre8guide', 'concierge', 'prototyping'].includes(saved) ? saved : 'design';
    }
    return 'design';
  });

  // Tab switches fade the page out, swap the rendered tab (and scroll to the
  // top) while it's invisible, then fade back in. The nav follows the click
  // immediately through navTab.
  const [navTab, setNavTab] = useState(activeTab);
  const [isTabFading, setIsTabFading] = useState(false);
  const shownTabRef = useRef(activeTab);
  const tabFadeTimer = useRef(0);
  const setActiveTab = useCallback((tab) => {
    setNavTab(tab);
    clearTimeout(tabFadeTimer.current);
    if (tab === shownTabRef.current) {
      setIsTabFading(false);
      return;
    }
    setIsTabFading(true);
    tabFadeTimer.current = setTimeout(() => {
      shownTabRef.current = tab;
      setShownTab(tab);
      setIsTabFading(false);
    }, TAB_FADE_MS);
  }, []);
  useEffect(() => () => clearTimeout(tabFadeTimer.current), []);
  const tabFadeStyle = { opacity: isTabFading ? 0 : 1, transition: `opacity ${TAB_FADE_MS}ms ease` };

  // Dynamic Meta Descriptions per Tab
  useEffect(() => {
    // CRE8APPS / Impressum bring their own title and description (PAGE_META in
    // src/seo/routes.js, applied by App.jsx) — leave them alone.
    if (ownsOwnMeta()) return;

    const metaDescriptions = {
      de: {
        design: "CRE8NOW gestaltet UX/UI und digitale Produkte für mittelständische Unternehmen, Technologieanbieter und Produktteams. Konversionsstarke Web-Erlebnisse, mobile Apps und High-End-Seiten in Webflow und Framer. 25+ Jahre Erfahrung, 450+ Projekte, 60+ internationale Kunden.",
        ai: "CRE8NOW automatisiert Geschäftsprozesse mit KI-Agenten, Workflows und Gen-AI-Content. Wiederkehrende Abläufe laufen autonom, das Team wird frei für die Arbeit, die Umsatz bringt. 25+ Jahre Erfahrung, 450+ Projekte, 60+ internationale Kunden.",
        concierge: "CRE8NOW baut KI-Voice-Agenten, die rund um die Uhr abnehmen, Termine buchen und Leads qualifizieren. Kein verpasster Anruf, keine Warteschleife, keine zusätzliche Personalstelle. 25+ Jahre Erfahrung, 450+ Projekte, 60+ internationale Kunden.",
        cre8guide: "Mit 25+ Jahren Erfahrung, 450+ Projekten & 60+ internationalen Kunden bietet die CRE8NOW Academy aus Berlin 24 exklusive Lektionen über modernes Digital Design und zukunftsweisende KI-Methoden. Lerne praxisnah von führenden Branchenexperten.",
        prototyping: "CRE8NOW baut coded Product Prototypes in 7 Werktagen: klickbar im Browser, echter React-Code, Git-Repo inklusive. Festpreis ab 2.500 Euro, Scope live konfigurierbar. 25+ Jahre Erfahrung, 450+ Projekte, 60+ internationale Kunden."
      },
      en: {
        design: "CRE8NOW designs UX/UI and digital products for mid-sized companies, technology providers and product teams. High-converting web experiences, mobile apps and high-end Webflow and Framer sites. 25+ years of experience, 450+ projects, 60+ international clients.",
        ai: "CRE8NOW automates business processes with AI agents, workflows and gen-AI content. Recurring work runs on its own and the team gets its hours back for the work that earns. 25+ years of experience, 450+ projects, 60+ international clients.",
        concierge: "CRE8NOW builds AI voice agents that answer around the clock, book appointments and qualify leads. No missed call, no hold music, no extra headcount. 25+ years of experience, 450+ projects, 60+ international clients.",
        cre8guide: "With 25+ years of experience, 450+ projects & 60+ international clients, the Berlin-based CRE8NOW Academy offers 24 exclusive lessons on modern digital design and next-generation AI methodologies. Learn real-world practices from industry experts.",
        prototyping: "CRE8NOW builds coded product prototypes in 7 business days: clickable in the browser, real React code, Git repository included. Fixed price from €2,500, scope configurable live. 25+ years of experience, 450+ projects, 60+ international clients."
      }
    };

    const description = metaDescriptions[lang]?.[activeTab] || metaDescriptions.de.design;
    
    // Update document title dynamically based on tab
    const titles = {
      de: {
        design: "CRE8NOW | UX/UI Design Agentur Berlin für digitale Produkte & Web-Erlebnisse",
        ai: "CRE8NOW | KI-Agentur Berlin für AI Automation, Agenten & Gen-AI Content",
        concierge: "CRE8NOW | KI Voice-Agent Agentur Berlin für Telefonie rund um die Uhr",
        cre8guide: "CRE8NOW | CRE8GUIDE – Design & AI Knowledge Hub mit Experten-Wissen aus Berlin",
        prototyping: "CRE8NOW | Rapid Prototyping Agentur Berlin für Coded Product Prototypes in 7 Tagen"
      },
      en: {
        design: "CRE8NOW | UX/UI Design Agency Berlin for Digital Products & Web Experiences",
        ai: "CRE8NOW | AI Agency Berlin for AI Automation, Agents & Gen-AI Content",
        concierge: "CRE8NOW | AI Voice Agent Agency Berlin for Round-the-Clock Phone Support",
        cre8guide: "CRE8NOW | CRE8GUIDE – Design & AI Knowledge Hub with Expert Insights from Berlin",
        prototyping: "CRE8NOW | Rapid Prototyping Agency Berlin for Coded Product Prototypes in 7 Days"
      }
    };
    
    const activeTitle = titles[lang]?.[activeTab] || titles.de.design;
    document.title = activeTitle;

    // og:title / twitter:title track the tab too. Without this they kept
    // index.html's generic homepage title, so every share of /de|en/ai,
    // /prototyping, /concierge or /cre8guide — WhatsApp, LinkedIn, Slack,
    // iMessage — showed "CRE8NOW – Design & AI Solutions | UX/UI Design Agency
    // Berlin" above this tab's own description. The description was already
    // per-tab, which made the mismatch look like a bug in the preview rather
    // than in the page.
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) ogTitle.setAttribute('content', activeTitle);
    const twitterTitle = document.querySelector('meta[name="twitter:title"]');
    if (twitterTitle) twitterTitle.setAttribute('content', activeTitle);

    // Update standard meta description
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute('content', description);

    // Update OG description
    let ogDesc = document.querySelector('meta[property="og:description"]');
    if (!ogDesc) {
      ogDesc = document.createElement('meta');
      ogDesc.setAttribute('property', 'og:description');
      document.head.appendChild(ogDesc);
    }
    ogDesc.setAttribute('content', description);

    // Update Twitter description (if exists)
    let twitterDesc = document.querySelector('meta[name="twitter:description"]');
    if (twitterDesc) {
      twitterDesc.setAttribute('content', description);
    }

    // Update OG/Twitter image. Each main tab carries its own card, generated
    // from OgCard via /og-preview.html (see src/og-preview-main.jsx) so the
    // poster colour and big word match the tab's own hero — Design and AI used
    // to share one generic social image with Concierge, which made three
    // different pages look like one link in a chat. Switching tabs in the SPA
    // must actively reset the tag rather than leave the previous tab's image.
    const SOCIAL_ORIGIN = 'https://cre8now.design';
    const DEFAULT_SOCIAL_IMAGE = `${SOCIAL_ORIGIN}/assets/Cre8Now_Social.jpg`;
    const TAB_OG_IMAGES = {
      design: {
        de: `${SOCIAL_ORIGIN}/assets/og/cre8now_main-design-de.jpg`,
        en: `${SOCIAL_ORIGIN}/assets/og/cre8now_main-design-en.jpg`,
      },
      ai: {
        de: `${SOCIAL_ORIGIN}/assets/og/cre8now_main-ai-de.jpg`,
        en: `${SOCIAL_ORIGIN}/assets/og/cre8now_main-ai-en.jpg`,
      },
      concierge: {
        de: `${SOCIAL_ORIGIN}/assets/og/cre8now_main-concierge-de.jpg`,
        en: `${SOCIAL_ORIGIN}/assets/og/cre8now_main-concierge-en.jpg`,
      },
      prototyping: {
        de: `${SOCIAL_ORIGIN}/assets/og/cre8now_prototyping-campaign-de.jpg`,
        en: `${SOCIAL_ORIGIN}/assets/og/cre8now_prototyping-campaign-en.jpg`,
      },
    };
    const ogImageUrl = TAB_OG_IMAGES[activeTab]?.[lang] || DEFAULT_SOCIAL_IMAGE;
    const ogImage = document.querySelector('meta[property="og:image"]');
    if (ogImage) ogImage.setAttribute('content', ogImageUrl);
    const twitterImage = document.querySelector('meta[name="twitter:image"]');
    if (twitterImage) twitterImage.setAttribute('content', ogImageUrl);

    // Optional: Update title suffix if needed, but App.jsx might handle this too

    // Canonical + hreflang — same technique SolutionPageTemplate.jsx uses
    // for the 19 SEO subpages, applied here for the main Design/AI/CRE8GUIDE/
    // Concierge tabs so each gets its own indexable URL instead of
    // canonicalizing down to "/" via App.jsx's generic (query-param-based)
    // logic.
    // The bare homepage "/" renders this component with the design tab active,
    // but it is NOT /de/design — it is that cluster's hreflang x-default, and
    // App.jsx already gave it a self-referencing canonical. Writing a tab
    // canonical here too would have the two effects fight over the tag and,
    // whenever this one ran last, canonicalize the strongest URL on the site
    // away onto a subpage. So "/" is left to App.jsx entirely.
    const isBareHomepage = typeof window !== 'undefined'
      && window.location.pathname.replace(/\/$/, '') === '';

    // ownsOwnMeta() covers Impressum AND CRE8APPS. Previously only Impressum
    // was excluded, so on /de/cre8apps this block still wrote the active tab's
    // canonical (/de/design) and only App.jsx's later effect put it right —
    // correct purely by effect ordering. Excluding both makes it deterministic.
    if (TAB_PATHS[activeTab] && !ownsOwnMeta() && !isBareHomepage) {
      const ORIGIN = 'https://cre8now.design';
      const tabPath = TAB_PATHS[activeTab][lang] || TAB_PATHS[activeTab].de;
      const canonicalTag = document.getElementById('canonical-tag');
      if (canonicalTag) canonicalTag.setAttribute('href', ORIGIN + tabPath);
      // og:url and og:locale follow the canonical — a share of /de/ai must not
      // report the homepage URL or an English locale.
      const ogUrl = document.querySelector('meta[property="og:url"]');
      if (ogUrl) ogUrl.setAttribute('content', ORIGIN + tabPath);
      const ogLocale = document.querySelector('meta[property="og:locale"]');
      if (ogLocale) ogLocale.setAttribute('content', lang === 'de' ? 'de_DE' : 'en_US');
      const hreflangDe = document.querySelector('link[rel="alternate"][hreflang="de"]');
      const hreflangEn = document.querySelector('link[rel="alternate"][hreflang="en"]');
      const hreflangDefault = document.querySelector('link[rel="alternate"][hreflang="x-default"]');
      if (hreflangDe) hreflangDe.setAttribute('href', ORIGIN + TAB_PATHS[activeTab].de);
      if (hreflangEn) hreflangEn.setAttribute('href', ORIGIN + TAB_PATHS[activeTab].en);
      // /de/design and /en/design are the pinned-language members of the
      // homepage's cluster, so their x-default must point back at "/" — the
      // same value index.html and App.jsx declare. hreflang sets are discarded
      // wholesale unless every member names the same alternates.
      const xDefault = activeTab === 'design' ? `${ORIGIN}/` : ORIGIN + TAB_PATHS[activeTab].de;
      if (hreflangDefault) hreflangDefault.setAttribute('href', xDefault);
    }
  }, [activeTab, lang]);

  // Dispatch tab change event for CookieBanner and other components
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('cre8now-active-tab', activeTab);
      window.dispatchEvent(new CustomEvent('set_tab', { detail: activeTab }));
    }
  }, [activeTab]);

  // The splash is the entrance to the four main pages. Cre8Guide rides on this
  // same component, but it is a subpage — opening it from a link should just
  // fade in, the way every solution page now does, rather than holding a logo
  // for 1.8s in front of content the visitor asked for by name.
  const [isLoading, setIsLoading] = useState(() => activeTab !== 'cre8guide');
  const [isMobile, setIsMobile] = useState(false);
  const [wordIndex, setWordIndex] = useState(0);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [activeCampaignSlide, setActiveCampaignSlide] = useState(0);
  const campaignSliderRef = useRef(null);

  const openModal = (index) => setSelectedIndex(index);
  const closeModal = () => setSelectedIndex(null);
  const nextShort = (e) => {
    if (e) e.stopPropagation();
    setSelectedIndex((prev) => (prev + 1) % YOUTUBE_SHORTS.length);
  };
  const prevShort = (e) => {
    if (e) e.stopPropagation();
    setSelectedIndex((prev) => (prev - 1 + YOUTUBE_SHORTS.length) % YOUTUBE_SHORTS.length);
  };

  useEffect(() => {
    const handleMessage = (event) => {
      if (!event.origin.match(/youtube(?:-nocookie)?\.com/)) return;
      try {
        let data = typeof event.data === "string" ? JSON.parse(event.data) : event.data;
        if (!data) return;
        const playerState = data.info?.playerState ?? (data.event === 'onStateChange' ? data.info : null);
        if (playerState === 0 || playerState === "0") {
          nextShort();
        }
      } catch (err) {}
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  useEffect(() => {
    if (!isLoading) return undefined;
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1800); // Reduced from 3s to 1.8s for better LCP
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [expandedVideo, setExpandedVideo] = useState(null);
  const [expandedAppImage, setExpandedAppImage] = useState(null);

  const handleAppInquiry = (item) => {
    const isInProgress = item.status === 'in-progress';
    const subject = encodeURIComponent(
      isInProgress ? `Demo Request for ${item.title}` : `Project Inquiry for ${item.title}`
    );
    const body = encodeURIComponent(
      isInProgress
        ? `Hi CRE8NOW,\r\n\r\nI'd like to request a demo for ${item.title}.\r\n\r\nPlease get back to me.\r\n\r\nBest regards`
        : `Hi CRE8NOW,\r\n\r\nI'd like to learn more about ${item.title}.\r\n\r\nPlease get back to me.\r\n\r\nBest regards`
    );
    window.location.href = `mailto:solutions@cre8now.design?subject=${subject}&body=${body}`;
  };

  const appCardCopy = {
    de: { inProgress: 'In Arbeit', cta: 'Open Project', demo: 'Demo anfordern' },
    en: { inProgress: 'In Progress', cta: 'Open Project', demo: 'Request Demo' },
  };
  useEffect(() => {
    const handleSetTab = (e) => {
      if (e.detail) {
        setActiveTab(e.detail);
        smoothScrollTo(0);
      }
    };
    window.addEventListener('set-landing-tab', handleSetTab);
    window.addEventListener('set_tab', handleSetTab);
    return () => {
      window.removeEventListener('set-landing-tab', handleSetTab);
      window.removeEventListener('set_tab', handleSetTab);
    };
  }, []);

  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [isGlobalMuted, setIsGlobalMuted] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('cre8now-video-muted') !== 'false';
    }
    return true;
  });
  const videoRef = useRef(null);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('cre8now-video-muted', isGlobalMuted);
    }
  }, [isGlobalMuted]);

  useEffect(() => {
    if (expandedVideo && videoRef.current && !isGlobalMuted) {
      const video = videoRef.current;
      video.volume = 0;
      video.muted = false;
      
      let vol = 0;
      const interval = setInterval(() => {
        if (vol < 0.6) {
          vol += 0.05;
          video.volume = Math.min(vol, 0.6);
        } else {
          clearInterval(interval);
        }
      }, 100);

      return () => clearInterval(interval);
    } else if (videoRef.current) {
      videoRef.current.muted = isGlobalMuted;
    }
  }, [expandedVideo, isGlobalMuted]);

  // Keep state in sync with URL — writes a real, crawlable path
  // (/de|en/design, /de|en/ai, /de|en/cre8guide, /de|en/concierge) instead
  // of stacking ?tab=/&lang= query params onto whatever path happened to be
  // current. Fixes both the GSC-indexing problem (these tabs used to
  // canonicalize down to "/") and a self-clobbering bug where landing
  // directly on a clean real path (e.g. /de/concierge, /de/cre8guide) got
  // ?tab=... appended right back onto it after mount.
  useEffect(() => {
    if (typeof window !== 'undefined') {
      if (isImpressumPath()) return;
      const tabPath = TAB_PATHS[activeTab]?.[lang];
      const url = new URL(window.location.origin + (tabPath || window.location.pathname));
      if (tabPath) {
        // Keep ?theme= on real paths too — a shared link should reopen in
        // the exact theme it was shared in. Harmless for SEO: the
        // canonical tag (set right below, in the effect above) always
        // strips it back to the bare path regardless of what's in the URL.
        url.searchParams.set('theme', theme);
      } else {
        // Unknown tab value — fall back to the old query-param behavior.
        url.searchParams.set('tab', activeTab);
        url.searchParams.set('lang', lang);
        url.searchParams.set('theme', theme);
      }
      window.history.replaceState({}, '', url.toString());
      localStorage.setItem('cre8now-active-tab', activeTab);
      localStorage.setItem('cre8now-lang', lang);
      // Use setTimeout to defer state update and avoid cascading render warning
      setTimeout(() => {
        setWordIndex(0);
      }, 0); // Reset word index on tab or language switch
    }
  }, [activeTab, lang, theme]);

  // Handle section hash on mount
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const hash = window.location.hash.replace('#', '');
      if (hash) {
        setTimeout(() => {
          const element = document.getElementById(hash);
          if (element) {
            smoothScrollIntoView(element, 80);
          }
        }, 1200); // Allow time for full initialization
      }
    }
  }, []);

  const color = COLORS[activeTab];
  const contactFgColor = (activeTab === 'design' && theme === 'dark') ? '#1a1a1a' : '#ffffff';

  // Hero identity — the dark-mode background behind the HeroVideoWall, white ink, and the
  // tab's primary as the accent:
  //   AI              → magenta accent
  //   Design + dark   → yellow accent
  //   Design + light  → magenta accent (same as AI)
  const isHeroAI = activeTab === 'ai';
  const isHeroMagenta = isHeroAI || (activeTab === 'design' && theme === 'light');
  const heroBg = 'var(--cre8-night)';
  const heroInk = '#ffffff';
  const heroAccent = isHeroMagenta ? 'var(--cre8-magenta)' : 'var(--cre8-yellow)';
  const heroAccentText = isHeroMagenta ? '#ffffff' : 'var(--cre8-ink)';
  const heroPanelBg = 'rgba(255,255,255,0.08)';
  // The CTA hover-fill reads magenta only in the yellow-bg case — keep its
  // arrow yellow there instead of the usual white/ink hover-text color.
  const heroArrowHoverColor = undefined;

  const t = TRANSLATIONS[lang];
  // Concierge uses the AI content structure for standard elements but swaps the hero view
  const content = t[(activeTab === 'concierge' || activeTab === 'prototyping') ? 'ai' : activeTab] || t.design;

  useEffect(() => {
    if (!content?.hero?.words) return;
    const timer = setInterval(() => {
      setWordIndex((prev) => (prev + 1) % (content.hero.words?.length || 1));
    }, 3500); // Increased interval from 2500 for a calmer feel
    return () => clearInterval(timer);
  }, [content?.hero?.words]);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const handleOpenConciergeModal = () => setIsBookingModalOpen(true);
    window.addEventListener('open_concierge_modal', handleOpenConciergeModal);
    return () => window.removeEventListener('open_concierge_modal', handleOpenConciergeModal);
  }, []);

  // Scroll to top and fade in new hero when tab changes
  useEffect(() => {
    if (window.__lenis) {
      window.__lenis.scrollTo(0, { immediate: true });
    } else {
      window.scrollTo({ top: 0, behavior: 'auto' });
    }
  }, [activeTab]);

  // GSAP scroll reveal — sections fade + slide in on enter, fade out on leave
  useEffect(() => {
    if (isLoading) return;

    const ctx = gsap.context(() => {
      const isMobileDevice = window.innerWidth < 768;
      
      // Small timeout to allow Lenis and layout to settle
      setTimeout(() => {
        // Main section containers: slide up + fade in/out via scrub
        gsap.utils.toArray('[data-gsap-section]').forEach((section) => {
          gsap.fromTo(
            section,
            { opacity: 0, y: 30 },
            {
              opacity: 1,
              y: 0,
              ease: isMobileDevice ? 'power2.out' : 'none',
              duration: isMobileDevice ? 1 : undefined,
              scrollTrigger: {
                trigger: section,
                start: isMobileDevice ? 'top 105%' : 'top 95%',
                end: isMobileDevice ? 'top 40%' : 'top 40%', 
                scrub: isMobileDevice ? false : 1,
                once: isMobileDevice
              },
            }
          );
        });

        // Individual content blocks inside sections
        gsap.utils.toArray('[data-gsap-reveal]').forEach((el) => {
          gsap.fromTo(
            el,
            { opacity: 0, y: 40 },
            {
              opacity: 1,
              y: 0,
              ease: isMobileDevice ? 'power2.out' : 'none',
              duration: isMobileDevice ? 1 : undefined,
              scrollTrigger: {
                trigger: el,
                start: isMobileDevice ? 'top 105%' : 'top 95%',
                end: isMobileDevice ? 'top 40%' : 'top 40%',
                scrub: isMobileDevice ? false : 1,
                once: isMobileDevice
              },
            }
          );
        });
      }, 100);
    });

    return () => ctx.revert();
  }, [isLoading, activeTab]);

  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"]
  });

  const heroSectionRef = useRef(null);
  // Mobile: each rotating hero word grows into the free width (shared hook).
  const { measureBox: heroWordBox, ...heroWordFit } = useHeroWordFit(content.hero.words || [content.hero.headline[1]], HERO_WORD_CLASS);
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [50, 550], [0, -150]);
  const heroOpacity = useTransform(scrollY, [50, 400], [1, 0]);
  const heroScale = useTransform(scrollY, [50, 550], [1, 0.9]);
  const heroFilter = useTransform(scrollY, [50, 400], ['blur(0px)', 'blur(0px)']);

  // Staggered child elements scroll transforms - each moves different distances for parallax effect
  const h4Y = useTransform(scrollY, [70, 570], [0, -150]);
  const h4Opacity = useTransform(scrollY, [70, 420], [1, 0]);
  const headlineY = useTransform(scrollY, [90, 590], [0, -120]);
  const headlineOpacity = useTransform(scrollY, [90, 440], [1, 0]);
  const descY = useTransform(scrollY, [110, 610], [0, -90]);
  const descOpacity = useTransform(scrollY, [110, 460], [1, 0]);
  const buttonY = useTransform(scrollY, [130, 630], [0, -60]);
  const buttonOpacity = useTransform(scrollY, [130, 480], [1, 0]);

  const aiIcons = [ICONS.Hexagon, ICONS.Cpu, ICONS.Mic, ICONS.Database, ICONS.Video, ICONS.Users, ICONS.Film, ICONS.Code, ICONS.Smartphone];
  const designIcons = [ICONS.Hexagon, ICONS.Users, ICONS.Layout, ICONS.MousePointer2, ICONS.Smartphone, ICONS.Layers, ICONS.Globe, ICONS.Box, ICONS.PenTool];
  const currentIcons = activeTab === 'ai' ? aiIcons : designIcons;
  const landingApps = ['tinycomp', 'keycut', 'audiobuster']
    .map((appId) => PLAYGROUND.find((app) => app.id === appId))
    .filter(Boolean);

  return (
    <AnimatePresence mode="wait">
      {isLoading ? (
        <SplashScreen key="splash" color={color} theme={theme} activeTab={activeTab} />
      ) : (
        <Motion.div 
          key="main"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.2, ease: "easeOut" }}
          ref={containerRef} 
          className="bg-bg-color text-text-color selection:bg-text-color selection:text-bg-color relative overflow-x-clip"
        >
          {/* Sitewide print-grain texture — viewport-locked so it stays cheap
              regardless of page length, sits above content but below chrome. */}
          <GrainOverlay fixed opacity={0.03} className="z-40" />

          <Nav
            videoHero
            activeTab={navTab} 
            setActiveTab={setActiveTab} 
            setView={setView}
            color={color} 
            lang={lang} 
            setLang={setLang} 
            t={t} 
            theme={theme}
            setTheme={setTheme}
            onOpenBookingModal={() => setIsBookingModalOpen(true)}
          />

          <main id="main-content" style={tabFadeStyle}>
            {/* HERO */}
            {activeTab !== 'concierge' && activeTab !== 'prototyping' && (
              // Keyed by tab so Design <-> AI remounts the hero the same way a
              // switch to Prototyping does: a fresh intro while the page fades
              // back in, not the old headline's exit after the swap.
              <section
                key={`hero-${activeTab}`}
                ref={heroSectionRef}
                className="relative h-[100dvh] md:min-h-[100dvh] md:h-auto w-full flex items-center justify-center gpu-accelerated hero-section-container overflow-hidden"
                style={{ backgroundColor: heroBg }}
              >
              {/* Tilted wall of the tab's own work clips under a dark wash. */}
              <HeroVideoWall mode={isHeroAI ? 'ai' : 'design'} tint={heroBg} ink={heroInk} />

              {/* Swiss micrographic frame: registration marks, index tags, coordinates, flow chain.
                  Desktop only — mobile keeps the hero minimal (headline, CTA, marquee). */}
              <div className="hidden md:block absolute inset-0 z-10 pointer-events-none" style={{ color: heroInk }}>
                {/* Top corners only, flush at the true edge (clears the nav's own padding). */}
                <CropMarks insetTop={8} insetX={8} showBottom={false} color={heroAccent} />

                <div className="absolute" style={{ top: '38%', left: 24 }}>
                  <Crosshair size={12} color={heroAccent} />
                </div>

                <div className="absolute bottom-20 md:bottom-24 left-6">
                  <FlowChain
                    steps={isHeroAI ? ['CRE8NOW', 'AI', 'SOLUTIONS'] : ['CRE8NOW', 'DESIGN', 'STUDIO']}
                    color={heroAccent}
                  />
                </div>

                <div className="flex absolute bottom-20 md:bottom-24 right-6 items-center gap-3">
                  <SystemMarker label="CRE8NOW / LIVE" color={heroAccent} />
                  <SectionCounter current={isHeroAI ? 2 : 1} total={2} />
                </div>

                <VerticalLabel text="CRE8NOW / SYSTEM" side="left" offset={24} />
                <VerticalLabel text={isHeroAI ? 'MODE / AI' : 'MODE / DESIGN'} side="right" offset={24} />
              </div>
              <Motion.div
                style={{ y: heroY, opacity: heroOpacity, scale: heroScale, filter: heroFilter, color: heroInk }}
                className="hero-stack relative z-20 text-center px-6"
              >
                <div className="mb-0">
                  <AnimatePresence mode="wait">
                    <Motion.div
                      key={`${activeTab}-${lang}`}
                      className="w-full"
                    >
                      <Motion.h4
                        initial={{ scale: 1.1, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.8, opacity: 0 }}
                        transition={{ duration: 0.8, ease: [0.83, 0, 0.17, 1] }}
                        className="hero-h4 block mb-1 leading-[0.95] uppercase tracking-tight [text-shadow:none]"
                        style={{ y: h4Y }}
                      >
                        {content.hero.headline[0]}
                      </Motion.h4>
                      <Motion.h1
                        initial={{ scale: 1.1, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.8, opacity: 0 }}
                        transition={{ delay: 0.1, duration: 0.8, ease: [0.83, 0, 0.17, 1] }}
                        className="font-black leading-[0.85] tracking-tight uppercase w-full mt-4"
                        style={{ y: headlineY }}
                      >
                        <Motion.span
                          ref={heroWordBox}
                          className="relative inline-block h-[1.1em] md:h-[1em] w-screen max-w-full align-top"
                        style={{ perspective: '1000px', ...(heroWordFit.active && { height: heroWordFit.boxHeight }) }}
                      >
                        <AnimatePresence initial={false}>
                          <Motion.span 
                            key={wordIndex}
                            initial={{ scale: 1.2, opacity: 0, filter: 'blur(10px)' }}
                            animate={{ 
                              scale: 1, 
                              opacity: 1, 
                              filter: 'blur(0px)',
                              transition: { duration: 2, ease: [0.22, 1, 0.36, 1], delay: 1.7 } 
                            }}
                            exit={{ 
                              scale: 0.8, 
                              opacity: 0, 
                              filter: 'blur(10px)',
                              transition: { duration: 2, ease: [0.64, 0, 0.78, 0] } 
                            }}
                            className={HERO_WORD_CLASS}
                            style={{ color: heroAccent, ...(heroWordFit.active && { fontSize: `${1.08 * heroWordFit.fitFor(content.hero.words?.[wordIndex] || content.hero.headline[1])}em`, top: '50%', translate: '-50% -50%' }) }}
                          >
                            {content.hero.words?.[wordIndex] || content.hero.headline[1]}
                          </Motion.span>
                        </AnimatePresence>
                        {/* Spacer to maintain layout dimensions for centering */}
                        <span className="max-md:hidden invisible opacity-0 pointer-events-none whitespace-nowrap px-4" aria-hidden="true">
                          {content.hero.words?.[wordIndex] || content.hero.headline[1]}
                        </span>
                      </Motion.span>
                      </Motion.h1>
                    </Motion.div>
                  </AnimatePresence>
                </div>

                <AnimatePresence mode="wait">
                    <Motion.p
                    key={`${activeTab}-${lang}`}
                    initial={{ opacity: 0, scale: 1.1 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    transition={{ delay: 0.25, duration: 0.8, ease: [0.83, 0, 0.17, 1] }}
                    className="max-w-[1200px] w-[90vw] hero-copy mx-auto leading-relaxed font-light mb-8 -mt-1 md:mt-5 gpu-accelerated"
                    style={{ y: descY, color: heroInk, opacity: 0.75 }}
                  >
                    {highlightStatPhrases(content.hero.description)}
                  </Motion.p>
                </AnimatePresence>

                <AnimatePresence mode="wait">
                  <Motion.div
                    key={`${activeTab}-${lang}-button`}
                    initial={{ opacity: 0, scale: 1.2 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ scale: 0.8, opacity: 0 }}
                    transition={{ delay: 0.8 }}
                    style={{ y: buttonY }}
                  >
                    <button
                      onClick={() => {
                        trackEvent('orientation_call_click', { location: 'hero' });
                        setIsBookingModalOpen(true);
                      }}
                      style={{ backgroundColor: heroInk, color: heroBg, borderColor: heroInk, '--hero-accent-text': heroAccentText }}
                      className="hero-cta group relative px-[2.4em] py-[1.1em] font-black uppercase ui-text transition-all duration-500 overflow-hidden rounded-none border"
                    >
                      <div
                        className="absolute inset-0 translate-y-full group-hover:translate-y-0 transition-transform duration-500"
                        style={{ backgroundColor: heroAccent }}
                      />
                      <span className="relative z-10 flex items-center transition-colors duration-500 group-hover:[color:var(--hero-accent-text)]">
                        {content.hero.cta}
                        <span className="w-0 opacity-0 group-hover:w-[1.25em] group-hover:ml-[0.75em] group-hover:opacity-100 transition-all duration-500 overflow-hidden flex items-center">
                          <ArrowRight size="1.1em" style={heroArrowHoverColor ? { color: heroArrowHoverColor } : undefined} />
                        </span>
                      </span>
                    </button>
                  </Motion.div>
                </AnimatePresence>
              </Motion.div>

              <Motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.5, duration: 1 }}
                className="absolute bottom-0 left-0 z-20 w-full overflow-hidden hero-marquee-container"
                ref={(el) => {
                  if (el && typeof window !== 'undefined') {
                    const observer = new IntersectionObserver(([entry]) => {
                      const marquee = el.querySelector('.animate-infinite-scroll-fast');
                      if (marquee) {
                        marquee.setAttribute('data-paused', !entry.isIntersecting);
                      }
                    }, { threshold: 0 });
                    observer.observe(el);
                    return () => observer.disconnect();
                  }
                }}
              >
                <HeroBrandMarquee bg="var(--hero-marquee-bg)" />
              </Motion.div>
            </section>
          )}

          <div key={`content-${activeTab}`}>
              {activeTab === 'prototyping' ? (
                <RapidPrototyping
                  // The hero CTA opens the scope-call modal, like the nav's
                  // Scope-Call button; the section CTAs route into the
                  // configurator.
                  onBookCall={() => setIsBookingModalOpen(true)}
                  theme={theme}
                  lang={lang}
                />
              ) : activeTab === 'concierge' ? (
                <AIVoiceAgent 
                  setView={setView} 
                  theme={theme} 
                  setTheme={setTheme} 
                  lang={lang} 
                  setLang={setLang} 
                />
              ) : activeTab === 'cre8guide' ? (
                <>
                  {/* ACADEMY GRID SECTION */}
                  <section id="academy" data-gsap-section className="pt-24 pb-24 md:pt-32 md:pb-48 bg-bg-color">
                    <div className="w-full px-[8vw] mb-20">
                      <SectionHeading 
                        label={content.academy.label}
                        title={content.academy.title}
                        highlight={content.academy.highlight}
                        description={content.academy.description}
                        color={color}
                        centered={true}
                        theme={theme}
                      />
                    </div>
                    
                    <div className="w-full px-[8vw] grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-24">
                      {GUIDE_ITEMS.map((item) => {
                        const partInfo = item.title.match(/Part \d+\/\d+/);
                        const cleanTitle = item.title.split(' - ').pop();
                        return (
                          <Motion.div
                            key={item.id}
                            whileHover={{ y: -10 }}
                            onClick={() => {
                              trackEvent('guide_item_click', { guide_id: item.id, guide_title: item.title, from: 'inline' });
                              window.open(item.url, '_blank');
                            }}
                            className="group p-0 rounded-[4px] cursor-pointer transition-all duration-500"
                          >
                            <div className="aspect-video overflow-hidden rounded-[2px] mb-5">
                              <img src={item.image} alt={`CRE8NOW CRE8GUIDE: ${item.title}`} width={800} height={450} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000" />
                            </div>
                            <div className="space-y-4">
                              <h6 className="text-text-muted opacity-60 mb-5">
                                {partInfo ? partInfo[0] : ""}
                              </h6>
                              <h4 className="font-black leading-[1.1] tracking-tight text-text-color transition-colors group-hover:text-red-500">
                                {cleanTitle}
                              </h4>
                              <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-text-muted mt-6 transition-all group-hover:text-red-500">
                                Explore Now <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                              </div>
                            </div>
                          </Motion.div>
                        );
                      })}
                    </div>
                  </section>

                  {/* ON AIR SECTION (Motivation) */}
                  <section data-gsap-section className="py-32 md:py-48 bg-surface-color border-y border-border-color overflow-hidden">
                    <div className="w-full px-[8vw] mb-20">
                      <SectionHeading 
                        label={content.onAir.label}
                        title={content.onAir.title}
                        highlight={content.onAir.highlight}
                        description={content.onAir.description}
                        color={color}
                        centered={true}
                        theme={theme}
                      />
                    </div>

                    <div className="w-full px-[8vw] grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-8">
                      {YOUTUBE_SHORTS.map((short, idx) => (
                        <Motion.div 
                          key={short.id} 
                          whileHover={{ y: -5 }}
                          className="flex flex-col gap-4 cursor-pointer"
                          onClick={() => {
                            trackEvent('youtube_short_click', { short_id: short.id, short_title: short.title, from: 'inline' });
                            openModal(idx);
                          }}
                        >
                          <div className="aspect-[9/16] rounded-[4px] overflow-hidden bg-bg-color border border-border-color group relative">
                            <div className="absolute inset-0 bg-black/20 group-hover:bg-transparent transition-colors z-10" />
                            <img
                              src={`https://img.youtube.com/vi/${short.id}/hqdefault.jpg`}
                              alt={short.title}
                              width={480}
                              height={360}
                              className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700"
                            />
                            <div className="absolute inset-0 flex items-center justify-center z-20 opacity-0 group-hover:opacity-100 transition-opacity">
                              <div className="p-4 rounded-full text-bg-color shadow-2xl" style={{ backgroundColor: color }}>
                                <Play size={24} fill="currentColor" />
                              </div>
                            </div>
                          </div>
                          <h5 className="font-black leading-[1.1] tracking-tight text-text-color opacity-80 group-hover:opacity-100 transition-opacity">
                            {short.title}
                          </h5>
                        </Motion.div>
                      ))}
                    </div>
                  </section>


                </>
              ) : (
                <>
                  {/* TEASER VIDEO */}
                  <div className="w-full px-[8vw] mb-20 md:mb-32 mt-[20vw] md:mt-[6vw] flex flex-col items-center">
                    <div
                      className="relative w-full rounded-lg md:rounded-2xl overflow-hidden cursor-pointer group mb-12 md:mb-20"
                      onClick={() => {
                        setIsGlobalMuted(false);
                        setExpandedVideo(activeTab === 'ai' ? '/assets/cre8now-videos/cre8now-digital-concierge.mp4' : '/assets/cre8now-videos/cre8now-teaser.mp4');
                      }}
                    >
                      <video
                        key={activeTab}
                        src={activeTab === 'ai' ? '/assets/cre8now-videos/cre8now-digital-concierge.mp4' : '/assets/cre8now-videos/cre8now-teaser.mp4'}
                        autoPlay
                        loop
                        muted
                        playsInline
                        preload="metadata"
                        className="w-full object-cover"
                      />
                      {/* Play icon overlay */}
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-black/50 backdrop-blur-md border border-white/20 flex items-center justify-center text-white group-hover:scale-110 transition-transform duration-300">
                          <Play size={28} fill="white" />
                        </div>
                      </div>
                    </div>

                    <Motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => {
                        trackEvent('orientation_call_click', { location: 'teaser_video' });
                        setIsBookingModalOpen(true);
                      }}
                      className="group relative px-8 py-4 md:px-12 md:py-5 font-black uppercase ui-text transition-all duration-500 overflow-hidden bg-text-color text-bg-color rounded-[4px]"
                    >
                      <div 
                        className="absolute inset-0 translate-y-full group-hover:translate-y-0 transition-transform duration-500" 
                        style={{ backgroundColor: color }}
                      />
                      <span className={`relative z-10 flex items-center transition-colors duration-500 ${activeTab === 'ai' ? 'group-hover:text-white' : ''} ${activeTab === 'cre8guide' ? 'group-hover:text-white' : ''}`}>
                        {lang === 'de' ? 'Erstgespräch vereinbaren' : 'Book your orientation call'}
                        <span className="w-0 opacity-0 group-hover:w-5 group-hover:ml-3 group-hover:opacity-100 transition-all duration-500 overflow-hidden flex items-center">
                          <ArrowRight size={isMobile ? 16 : 20} />
                        </span>
                      </span>
                    </Motion.button>
                  </div>

                  {/* USP tapes under the teaser video (shared HeroTapes). */}
                  <HeroTapes items={content.hero.marquee || []} accent={heroAccent} accentInk={heroAccentText} />

{/* PROJECTS SECTION — replaced with Masonry Gallery for Design tab */}
              {activeTab === 'design' && (
                <>
                  <section id="projects" data-gsap-section className="bg-bg-color">
                    <MasonryGallery 
                      key="gallery-design"
                      type="design" 
                      theme={theme} 
                      color={color} 
                      lang={lang} 
                      title={t.common.portfolio.title}
                      highlight={t.common.portfolio.highlight}
                      label={t.common.portfolio.label}
                      description={t.common.portfolio.description}
                    />
                  </section>

                  {/* SERVICES GRID FOR DESIGN */}
                  <section id="services" data-gsap-section className="relative w-full border-t border-border-color bg-bg-color pt-24 pb-12 md:pt-32 md:pb-16">
                    <SectionFrame code="D" num="003" color={color} verticalLabelRight="MODE / SERVICES" showVerticalLabels={false} />
                    <div className="w-full px-[8vw] pb-12 md:pb-16">
                      <SectionHeading
                        title={t.design.services.title}
                        highlight={t.design.services.highlight}
                        color={color} 
                        label={t.design.services.label}
                        description={t.design.services.description}
                        centered={true}
                        theme={theme}
                      />
                    </div>

                    <div className="flex flex-col border-t border-border-color w-full">
                      {t.design.services.items.map((service, i) => (
                        <ServiceItem
                          key={i}
                          service={service}
                          i={i}
                          total={t.design.services.items.length}
                          color={color}
                          isMobile={isMobile}
                          currentIcons={currentIcons}
                          theme={theme}
                          hoverTextColor={contactFgColor}
                        />
                      ))}
                    </div>

                    <div className="w-full flex justify-center py-16 md:py-24 border-t border-border-color bg-bg-color">
                      <Motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => {
                          trackEvent('orientation_call_click', { location: 'services_design_bottom' });
                          setIsBookingModalOpen(true);
                        }}
                        className="group relative px-8 py-4 md:px-12 md:py-5 font-black uppercase ui-text transition-all duration-500 overflow-hidden bg-text-color text-bg-color rounded-[4px]"
                      >
                        <div 
                          className="absolute inset-0 translate-y-full group-hover:translate-y-0 transition-transform duration-500" 
                          style={{ backgroundColor: color }}
                        />
                        <span className={`relative z-10 flex items-center transition-colors duration-500 ${activeTab === 'ai' ? 'group-hover:text-white' : ''} ${activeTab === 'cre8guide' ? 'group-hover:text-white' : ''}`}>
                          {lang === 'de' ? 'Erstgespräch vereinbaren' : 'Book your orientation call'}
                          <span className="w-0 opacity-0 group-hover:w-5 group-hover:ml-3 group-hover:opacity-100 transition-all duration-500 overflow-hidden flex items-center">
                            <ArrowRight size={isMobile ? 16 : 20} />
                          </span>
                        </span>
                      </Motion.button>
                    </div>
                  </section>
                </>
              )}

              {/* PROCESS / METHOD */}
              {activeTab === 'ai' && (
                <>
                  <section id="ai-showcase" data-gsap-section className="bg-bg-color">
                    <MasonryGallery 
                      key="gallery-ai"
                      type="ai" 
                      theme={theme} 
                      color={color} 
                      lang={lang} 
                      title={lang === 'de' ? 'Die kreative Pipeline für' : 'The creative Pipeline for'}
                      highlight={lang === 'de' ? 'Content Generation' : 'Content Generation'}
                      label={lang === 'de' ? 'Creative AI Labs' : 'Creative AI Labs'}
                      description={lang === 'de' 
                        ? 'Deine automatisierte Pipeline für High-Performance Visuals, strategische Workshops und exzellente Markenprozesse. CRE8NOW verschmilzt State-of-the-Art Generative KI mit kompromissloser Qualität für deine Brand.'
                        : 'Your dedicated pipeline for high-performing visuals, strategic workshops, and world-class brand operations. CRE8NOW merges state-of-the-art generative AI with uncompromising quality standards built for your brand.'}
                      marqueeLogos={TOOL_LOGOS}
                    />
                  </section>

                  {/* SERVICES GRID FOR AI */}
                  <section id="services" data-gsap-section className="relative w-full border-t border-border-color bg-bg-color pt-24 pb-12 md:pt-32 md:pb-16">
                    <SectionFrame code="AI" num="004" color={color} verticalLabelRight="MODE / SERVICES" showVerticalLabels={false} />
                    <div className="w-full px-[8vw] pb-12 md:pb-16">
                      <SectionHeading
                        title={t.ai.services.title}
                        highlight={t.ai.services.highlight}
                        color={color} 
                        label={t.ai.services.label}
                        description={t.ai.services.description}
                        centered={true}
                        theme={theme}
                      />
                    </div>

                    <div className="flex flex-col border-t border-border-color w-full">
                      {t.ai.services.items.map((service, i) => (
                        <ServiceItem
                          key={i}
                          service={service}
                          i={i}
                          total={t.ai.services.items.length}
                          color={color}
                          isMobile={isMobile}
                          currentIcons={currentIcons}
                          theme={theme}
                          hoverTextColor={contactFgColor}
                        />
                      ))}
                    </div>

                    <div className="w-full flex justify-center py-16 md:py-24 border-t border-border-color bg-bg-color">
                      <Motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => {
                          trackEvent('orientation_call_click', { location: 'services_ai_bottom' });
                          setIsBookingModalOpen(true);
                        }}
                        className="group relative px-8 py-4 md:px-12 md:py-5 font-black uppercase ui-text transition-all duration-500 overflow-hidden bg-text-color text-bg-color rounded-[4px]"
                      >
                        <div 
                          className="absolute inset-0 translate-y-full group-hover:translate-y-0 transition-transform duration-500" 
                          style={{ backgroundColor: color }}
                        />
                        <span className={`relative z-10 flex items-center transition-colors duration-500 ${activeTab === 'ai' ? 'group-hover:text-white' : ''} ${activeTab === 'cre8guide' ? 'group-hover:text-white' : ''}`}>
                          {lang === 'de' ? 'Erstgespräch vereinbaren' : 'Book your orientation call'}
                          <span className="w-0 opacity-0 group-hover:w-5 group-hover:ml-3 group-hover:opacity-100 transition-all duration-500 overflow-hidden flex items-center">
                            <ArrowRight size={isMobile ? 16 : 20} />
                          </span>
                        </span>
                      </Motion.button>
                    </div>
                  </section>
                </>
              )}

      {/* CLIENT LOGO WALL — moved ahead of the campaign section per request */}
      {(activeTab === 'design' || activeTab === 'ai') && (
        <LogoWallSection color={color} lang={lang} theme={theme} />
      )}

              <section id="process" data-gsap-section className="relative bg-bg-color pt-24 pb-24 md:pt-32 md:pb-24">
        <div className="w-full px-[8vw] mb-16 md:mb-24">
          <SectionHeading 
            title={content.process.title} 
            highlight={content.process.highlight} 
            color={color} 
            label={content.process.label} 
            description={content.process.description}
            centered={true}
            theme={theme}
          />
        </div>

        <ProcessPinnedSection key={`${activeTab}-${lang}`} steps={content.process.steps} color={color} t={t} />

        <div className="w-full flex justify-center py-16 md:py-24 border-t border-border-color bg-bg-color">
          <Motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => {
              trackEvent('orientation_call_click', { location: 'process_bottom' });
              setIsBookingModalOpen(true);
            }}
            className="group relative px-8 py-4 md:px-12 md:py-5 font-black uppercase ui-text transition-all duration-500 overflow-hidden bg-text-color text-bg-color rounded-[4px]"
          >
            <div 
              className="absolute inset-0 translate-y-full group-hover:translate-y-0 transition-transform duration-500" 
              style={{ backgroundColor: color }}
            />
            <span className={`relative z-10 flex items-center transition-colors duration-500 ${activeTab === 'ai' ? 'group-hover:text-white' : ''} ${activeTab === 'cre8guide' ? 'group-hover:text-white' : ''}`}>
              {lang === 'de' ? 'Erstgespräch vereinbaren' : 'Book your orientation call'}
              <span className="w-0 opacity-0 group-hover:w-5 group-hover:ml-3 group-hover:opacity-100 transition-all duration-500 overflow-hidden flex items-center">
                <ArrowRight size={isMobile ? 16 : 20} />
              </span>
            </span>
          </Motion.button>
        </div>
      </section>

      {/* TESTIMONIALS SECTION */}
      <TestimonialSection color={color} t={t} lang={lang} theme={theme} />

              {/* ACCELERATOR SECTION — Concierge campaign banner, hidden while the
                  Concierge tab is (SHOW_CAMPAIGN_BANNER). */}
              {SHOW_CAMPAIGN_BANNER && (
              <section id="accelerator" className="py-24 md:py-32 px-[8vw] border-b border-border-color bg-surface-color/5">
                    <div className="w-full">
                      <div className="mb-16 md:mb-24">
                        <SectionHeading 
                          title={lang === 'de' ? 'Deine Voice-Agenten für' : 'Your Voice Agent for'}
                          highlight={lang === 'de' ? 'Mehr Leads' : 'More Leads'}
                          color={color} 
                          label={lang === 'de' ? 'AI Concierge Kampagne' : 'AI Concierge Campaign'}
                          description={lang === 'de' 
                            ? "Automatisierte Telefonie & Support mit intelligenten Voice-Agenten rund um die Uhr. Steigern Sie Ihre Erreichbarkeit und Effizienz mit unserer neuesten AI-Technologie."
                            : "Automated telephony & support with intelligent voice agents around the clock. Increase your availability and efficiency with our latest AI technology."}
                          centered={true}
                          theme={theme}
                        />
                      </div>

                      <div data-gsap-reveal className="w-full mt-16 md:mt-24">
                        <Motion.div 
                          initial={{ opacity: 0.001, y: 40 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          transition={{ 
                            duration: 1.2, 
                            ease: [0.16, 1, 0.3, 1],
                            opacity: { duration: 1.5, ease: "easeOut" }
                          }}
                          viewport={{ once: true, margin: "-20px" }}
                          onClick={() => { 
                            trackEvent('campaign_banner_click', { campaign: 'ai-voice-agent' });
                            setView('campaign-ai-voice-agent'); 
                            setActiveTab('concierge');
                            if (window.__lenis) {
                              window.__lenis.scrollTo(0, { immediate: true });
                            } else {
                              window.scrollTo(0, 0); 
                            }
                          }}
                          className="group cursor-pointer relative overflow-hidden rounded-[4px] transition-all duration-700 ease-out hover:scale-[1.01] hover:shadow-[0_20px_50px_-12px_rgba(0,0,0,0.5)] cursor-zoom"
                          style={{ backgroundColor: (activeTab === 'design' && theme === 'dark') ? '#111111' : color }}
                        >
                          {/* BG NOISE / TEXTURE GLOW */}
                          <div className="absolute inset-0 pointer-events-none">
                            <div className="absolute -top-1/2 -right-1/4 w-[60vw] h-[60vw] rounded-full blur-[120px] bg-white/10" />
                            <div className="absolute -bottom-1/4 -left-1/4 w-[40vw] h-[40vw] rounded-full blur-[100px] bg-black/10" />
                          </div>

                          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-0">
                            {/* LEFT SIDE — COPY */}
                            <div className="p-10 md:p-16 lg:p-20 flex flex-col justify-center gap-10 lg:border-r border-white/10">
                              <div className="w-full">
                                <div className="flex items-center gap-3 mb-6">
                                  <Mic size={16} className="text-white/50" />
                                  <span className="text-[8.5px] font-black uppercase tracking-[0.4em] text-white/50">
                                    {lang === 'de' ? 'Aktuelle Kampagne' : 'Current Campaign'}
                                  </span>
                                </div>

                                <h4 className="text-5xl md:text-6xl lg:text-7xl font-black leading-[0.88] tracking-tighter text-white mb-8">
                                  {lang === 'de' ? 'Dein digitaler Concierge' : 'Your Digital Concierge'}
                                </h4>

                                <p className="text-base md:text-lg text-white/90 font-light leading-relaxed mb-10 w-full">
                                  {lang === 'de'
                                    ? 'Nie wieder einen Call verpassen. Dein intelligenter Concierge übernimmt Support und Vertrieb rund um die Uhr, vollautomatisiert und hochprofessionell.'
                                    : 'Never miss a call again. Your intelligent concierge handles support and sales around the clock, fully automated and highly professional.'}
                                </p>

                                <div className="grid grid-cols-2 lg:flex lg:flex-wrap gap-2 lg:gap-3 mb-10">
                                  {[
                                    { label: lang === 'de' ? 'Mehr Leads' : 'More Leads', icon: Target },
                                    { label: lang === 'de' ? 'Mehr Umsatz' : 'More Revenue', icon: TrendingUp },
                                    { label: lang === 'de' ? 'Weniger Stress' : 'Less Stress', icon: ShieldCheck },
                                    { label: lang === 'de' ? 'Mehr Zeit' : 'More Time', icon: Clock }
                                  ].map((win, i) => (
                                    <div key={i} className="flex items-center justify-center lg:justify-start gap-2 px-3 py-1.5 rounded-full border border-white/15 bg-white/5 backdrop-blur-md transition-all duration-300">
                                      <win.icon size={11} className="text-white opacity-60" />
                                      <span className="text-[8.5px] font-black uppercase tracking-[0.2em] text-white whitespace-nowrap">{win.label}</span>
                                    </div>
                                  ))}
                                </div>

                                <div className="hidden lg:flex">
                                  <div className={`group/btn relative inline-flex items-center px-8 py-4 md:px-12 md:py-5 font-black uppercase ui-text transition-all duration-500 overflow-hidden rounded-[4px] ${
                                    (activeTab === 'design' && theme === 'dark') 
                                      ? 'bg-black text-white border border-white/10' 
                                      : 'bg-white text-[#1a1a1a]'
                                  }`}>
                                    <div 
                                      className={`absolute inset-0 translate-y-full group-hover:translate-y-0 transition-transform duration-500 ${
                                        (activeTab === 'design' && theme === 'dark') ? 'bg-white' : 'bg-[#1a1a1a]'
                                      }`} 
                                    />
                                    <span className={`relative z-10 flex items-center transition-colors duration-500 ${
                                      (activeTab === 'design' && theme === 'dark') ? 'group-hover:text-black' : 'group-hover:text-white'
                                    }`}>
                                      {lang === 'de' ? 'Concierge sichern' : 'Get your Concierge'}
                                      <span className="w-0 opacity-0 group-hover:w-5 group-hover:ml-3 group-hover:opacity-100 transition-all duration-500 overflow-hidden flex items-center">
                                        <ArrowRight size={isMobile ? 16 : 20} />
                                      </span>
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* RIGHT SIDE — TARGET GROUP GRID */}
                            <div className="p-10 md:p-12 lg:p-16 flex flex-col justify-center bg-black/5">
                              <div className="flex items-center gap-3 mb-6 opacity-40">
                                <span className="text-[8.5px] font-black uppercase tracking-[0.4em] text-white">
                                  {lang === 'de' ? 'Ideal für' : 'Made for'}
                                </span>
                                <div className="h-px flex-1 bg-white/10" />
                              </div>
                              
                              <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                                {[
                                  { icon: Dumbbell,      label: lang === 'de' ? 'Fitness' : 'Fitness' },
                                  { icon: UtensilsCrossed, label: lang === 'de' ? 'Restaurants' : 'Restaurants' },
                                  { icon: Bed,           label: lang === 'de' ? 'Hotels' : 'Hotels' },
                                  { icon: Flower2,       label: lang === 'de' ? 'Yoga' : 'Yoga' },
                                  { icon: Car,           label: lang === 'de' ? 'Autohäuser' : 'Dealerships' },
                                  { icon: Stethoscope,   label: lang === 'de' ? 'Arztpraxen' : 'Medical' },
                                  { icon: Scale,         label: lang === 'de' ? 'Anwälte' : 'Legal' },
                                  { icon: Scissors,      label: lang === 'de' ? 'Friseure' : 'Salons' },
                                  { icon: Home,          label: lang === 'de' ? 'Makler' : 'Real Estate' },
                                ].map(({ icon: Icon, label }, i) => (
                                  <div
                                    key={i}
                                    className={`flex flex-col items-center justify-center gap-2 py-5 px-2 rounded-[4px] text-center transition-all duration-500 ${
                                      (activeTab === 'design' && theme === 'dark') 
                                        ? 'bg-white/5 border border-white/10' 
                                        : 'bg-white border border-white'
                                    }`}
                                  >
                                    <Icon 
                                      size={20} 
                                      className="transition-colors duration-500" 
                                      style={{ 
                                        color: (activeTab === 'design' && theme === 'dark') 
                                          ? '#ffffff' 
                                          : '#F41465' 
                                      }} 
                                      strokeWidth={2} 
                                    />
                                    <span className={`text-[8.5px] font-black uppercase tracking-[0.2em] leading-tight transition-colors duration-500 ${
                                      (activeTab === 'design' && theme === 'dark') ? 'text-white' : 'text-[#1a1a1a]'
                                    }`}>
                                      {label}
                                    </span>
                                  </div>
                                ))}
                              </div>

                              {/* MOBILE BUTTON — APPEARS ONLY UNDER THE GRID */}
                              <div className="flex lg:hidden mt-10 w-full justify-center">
                                <div className={`group/btn relative inline-flex items-center justify-center px-6 py-4 font-black uppercase tracking-widest text-[11px] transition-all duration-500 overflow-hidden rounded-[4px] whitespace-nowrap ${
                                  (activeTab === 'design' && theme === 'dark') 
                                    ? 'bg-black text-white border border-white/10' 
                                    : 'bg-white text-[#1a1a1a]'
                                }`}>
                                  <div 
                                    className={`absolute inset-0 translate-y-full group-hover:translate-y-0 transition-transform duration-500 ${
                                      (activeTab === 'design' && theme === 'dark') ? 'bg-white' : 'bg-[#1a1a1a]'
                                    }`} 
                                  />
                                  <span className={`relative z-10 flex items-center transition-colors duration-500 ${
                                    (activeTab === 'design' && theme === 'dark') ? 'group-hover:text-black' : 'group-hover:text-white'
                                  }`}>
                                    {lang === 'de' ? 'Concierge sichern' : 'Get your Concierge'}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </Motion.div>
                      </div>
                    </div>
                  </section>
              )}

      {/* ABOUT / FOUNDER HIDDEN FOR NOW
      <section id="about" className="relative h-screen min-h-[700px] overflow-hidden">
        <AboutSectionContent color={color} t={t} theme={theme} />
      </section>
      */}

      {/* CRE8APPS SECTION */}
      <section id="playground" data-gsap-section className="pt-24 pb-24 md:pt-32 md:pb-24 bg-bg-color">
        <div className="w-full px-[8vw] mb-16 md:mb-24">
          <SectionHeading 
            title={t.common.playground.title}
            highlight={t.common.playground.highlight}
            color={color} 
            label={t.common.playground.label}
            description={t.common.playground.description}
            wins={t.common.playground.wins}
            centered={true}
            theme={theme}
          />
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 w-full gap-8 px-6 md:px-12 lg:px-20">
          {landingApps.map((app, i) => (
            <div key={app.id} className="relative">
              <div className="absolute -top-2 left-0 z-10">
                <IndexTag code="APP" num={String(i + 1).padStart(2, '0')} color={color} />
              </div>
              <AppCard item={app} lang={lang} color={color} onPreview={setExpandedAppImage} onAction={handleAppInquiry} copy={appCardCopy[lang] || appCardCopy.de} hoverTextDark={activeTab === 'design' && theme === 'dark'} statusTextColor={activeTab === 'ai' ? '#ffffff' : 'var(--bg-color)'} freeRibbonBgColor={activeTab === 'design' && theme === 'dark' ? 'var(--design-primary)' : '#f41465'} freeRibbonTextColor={activeTab === 'design' && theme === 'dark' ? '#1a1a1a' : '#ffffff'} />
            </div>
          ))}
        </div>

        <div className="flex justify-center mt-16 md:mt-20 px-6">
          <button
            onClick={() => {
              trackEvent('all_apps_click', { tab: activeTab });
              setView('cre8apps');
            }}
            className="cta-m group relative px-10 py-4 font-black uppercase tracking-widest text-[11px] transition-all duration-500 overflow-hidden border border-text-color text-text-color rounded-[4px] inline-flex items-center gap-3"
          >
            <div className="absolute inset-0 translate-y-full group-hover:translate-y-0 transition-transform duration-500 bg-text-color" />
            <span className="relative z-10 flex items-center gap-3 group-hover:text-bg-color transition-colors duration-500">
              {lang === 'de' ? `Alle ${PLAYGROUND.length + CRE8_EXTENSIONS.length} Apps & Extensions` : `All ${PLAYGROUND.length + CRE8_EXTENSIONS.length} Apps & Extensions`}
              <ArrowRight size={14} />
            </span>
          </button>
        </div>
      </section>
    </>
  )}
</div>

{/* CONTACT / FOOTER */}
<section id="contact" style={{ backgroundColor: color, color: contactFgColor }} className="min-h-screen pt-24 pb-24 md:pt-32 md:pb-24 flex items-center justify-center text-center px-10 relative overflow-hidden">
        <div className="hidden md:block absolute inset-0 pointer-events-none z-10" style={{ color: contactFgColor }}>
          <CropMarks insetTop={8} insetX={24} color={contactFgColor} />
          <div className="absolute top-24 left-6">
            <IndexTag code="CRE8NOW" num="CONTACT" color={contactFgColor} />
          </div>
          <div className="absolute top-24 right-6">
            <FlowChain steps={activeTab === 'ai' ? ['DISCOVER', 'AUTOMATE', 'SCALE'] : ['STRATEGY', 'DESIGN', 'RESULT']} color={contactFgColor} className="opacity-70" />
          </div>
          <VerticalLabel text="CRE8NOW / SYSTEM" side="left" offset={24} color={contactFgColor} />
        </div>

        <Motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, amount: 0.1 }}
          className="max-w-[90vw] mx-auto relative z-10"
        >
          <div className="overflow-visible md:overflow-hidden mb-12 mt-3">
            <Motion.h4
              variants={fadeInLeft}
              style={{ color: contactFgColor }}
              className="block mb-1 font-black uppercase leading-[0.95] tracking-tight"
            >
              {t.common.contact.title}
            </Motion.h4>
            <Motion.h2
              variants={fadeInLeft}
              style={{ color: contactFgColor }}
              className="audiowide-regular block font-black uppercase leading-[0.95] md:leading-[0.8] tracking-tight"
            >
              {t.common.contact.highlight}
            </Motion.h2>
          </div>
          
          <Motion.p 
            variants={fadeInLeft}
            style={{ color: contactFgColor, opacity: 0.7 }}
            className="font-light mb-20 max-w-[90vw] mx-auto tracking-tight leading-tight"
          >
            {t.common.contact.description}
          </Motion.p>
          
          <Motion.div
            variants={fadeInLeft}
            className="mb-20 flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <button
              onClick={() => {
                trackEvent('orientation_call_click', { location: 'contact_section' });
                setIsBookingModalOpen(true);
              }}
              style={{ backgroundColor: contactFgColor, color: (activeTab === 'design' && theme === 'dark') ? '#ffffff' : '#1a1a1a' }}
              className="group relative px-8 py-4 md:px-12 md:py-5 font-black uppercase ui-text transition-all duration-500 overflow-hidden rounded-[4px] inline-flex items-center"
            >
              <div className="absolute inset-0 translate-y-full group-hover:translate-y-0 transition-transform duration-500" style={{ backgroundColor: color === 'var(--design-primary)' ? 'rgba(0,0,0,0.15)' : 'rgba(255,255,255,0.15)' }} />
              <span className="relative z-10 flex items-center gap-3 transition-colors duration-500">
                {lang === 'de' ? 'Strategiegespräch' : 'Orientation Call'}
                <span className="w-0 opacity-0 group-hover:w-5 group-hover:opacity-100 transition-all duration-500 overflow-hidden flex items-center">
                  <ArrowRight size={isMobile ? 16 : 20} />
                </span>
              </span>
            </button>
            <a
              href="mailto:solutions@cre8now.design"
              onClick={() => trackEvent('email_click', { location: 'contact_section' })}
              style={{ borderColor: `${contactFgColor}40`, color: contactFgColor }}
              className="group relative px-8 py-4 md:px-12 md:py-5 font-black uppercase ui-text transition-all duration-500 overflow-hidden border rounded-[4px] inline-flex items-center"
            >
              <div className="absolute inset-0 translate-y-full group-hover:translate-y-0 transition-transform duration-500" style={{ backgroundColor: contactFgColor }} />
              <span style={{ '--hover-color': (activeTab === 'design' && theme === 'dark') ? '#ffffff' : '#1a1a1a' }} className="relative z-10 flex items-center gap-3 group-hover:text-[var(--hover-color)] transition-colors duration-500">
                {lang === 'de' ? 'Email schicken' : 'Send Email'}
                <span className="w-0 opacity-0 group-hover:w-5 group-hover:opacity-100 transition-all duration-500 overflow-hidden flex items-center">
                  <ArrowRight size={isMobile ? 16 : 20} />
                </span>
              </span>
            </a>
          </Motion.div>
        </Motion.div>
      </section>
      </main>

      {/* FOOTER */}
      <div style={tabFadeStyle}>
      <SiteFooter
        theme={theme}
        activeTab={activeTab}
        lang={lang}
        color={color}
        t={t}
        setActiveTab={setActiveTab}
        setView={setView}
      />
      </div>

      {/* YouTube Short Modal */}
      <AnimatePresence>
        {selectedIndex !== null && (
          <Motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[200] flex items-center justify-center bg-black/95 backdrop-blur-3xl overflow-hidden p-0 md:p-10"
            onClick={closeModal}
          >
            <button 
              onClick={closeModal}
              className="absolute top-8 right-8 z-50 p-4 rounded-full bg-white/10 hover:bg-white/20 transition-colors text-white"
            >
              <X size={32} />
            </button>

            <div className="flex items-center justify-center w-full h-full gap-4 md:gap-12 px-4">
              <button 
                onClick={prevShort}
                className="p-4 rounded-full bg-white/5 hover:bg-white/10 transition-colors text-white hidden md:block"
              >
                <ChevronLeft size={48} />
              </button>

              <Motion.div 
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="relative aspect-[9/16] h-[90vh] md:h-full bg-black rounded-3xl overflow-hidden shadow-[0_0_100px_rgba(239,68,68,0.3)] border border-white/10"
                onClick={(e) => e.stopPropagation()}
              >
                <iframe
                  width="100%"
                  height="100%"
                  src={`https://www.youtube.com/embed/${YOUTUBE_SHORTS[selectedIndex].id}?autoplay=1&rel=0&modestbranding=1&enablejsapi=1`}
                  title={YOUTUBE_SHORTS[selectedIndex].title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  className="w-full h-full"
                ></iframe>
                
                {/* Auto-play instruction for users */}
                <div className="absolute bottom-10 left-0 w-full text-center pointer-events-none px-6">
                  <p className="text-white/40 text-[10px] font-black uppercase tracking-[0.3em]">Youtube Player Auto-Forward enabled</p>
                </div>
              </Motion.div>

              <button 
                onClick={nextShort}
                className="p-4 rounded-full bg-white/5 hover:bg-white/10 transition-colors text-white hidden md:block"
              >
                <ChevronRight size={48} />
              </button>
            </div>
          </Motion.div>
        )}
      </AnimatePresence>

      {/* One modal per solution area. The active tab picks the content pack,
          so the form opens on the area's own first question instead of asking
          which of four mixed areas the visitor meant. */}
      <InquiryModal
        isOpen={isBookingModalOpen}
        onClose={() => setIsBookingModalOpen(false)}
        area={activeTab === 'cre8guide' ? 'design' : activeTab}
        lang={lang}
        color={color}
        onOpenConfigurator={activeTab === 'prototyping'
          ? () => smoothScrollIntoView(document.querySelector('#configurator'))
          : undefined}
      />

      <style>{`
        @keyframes infinite-scroll {
          from { transform: translateX(0); }
          to { transform: translateX(-50%); }
        }
        .animate-infinite-scroll {
          animation: infinite-scroll 60s linear infinite;
          display: flex;
          width: max-content;
          backface-visibility: hidden;
          -webkit-backface-visibility: hidden;
        }
        .booking-input:focus {
          border-color: rgba(255, 255, 255, 0.12);
          box-shadow: none;
        }
        .booking-input:-webkit-autofill,
        .booking-input:-webkit-autofill:hover,
        .booking-input:-webkit-autofill:focus,
        .booking-input:-webkit-autofill:active {
          -webkit-text-fill-color: var(--text-color);
          -webkit-box-shadow: 0 0 0px 1000px var(--surface-color) inset;
          box-shadow: 0 0 0px 1000px var(--surface-color) inset;
          caret-color: var(--text-color);
          border-color: rgba(255, 255, 255, 0.12);
          transition: background-color 9999s ease-out 0s;
        }
        .booking-option-active {
          color: var(--bg-color) !important;
          -webkit-text-fill-color: var(--bg-color) !important;
        }
        @media (max-height: 800px), (max-width: 768px) {
          .hero-section-container {
            height: 100dvh !important;
            min-height: 100dvh !important;
            padding-top: 96px !important;
            padding-bottom: 88px !important;
            display: flex !important;
            flex-direction: column !important;
            justify-content: center !important;
            align-items: center !important;
          }
          .hero-marquee-container {
            position: absolute !important;
            bottom: 0 !important;
            left: 0 !important;
            margin-top: 0 !important;
            width: 100% !important;
            transform: none !important;
          }
          .campaign-hero-container {
            min-height: 0vh !important;
            height: auto !important;
            padding-top: 160px !important;
            padding-bottom: 40px !important;
            display: flex !important;
            flex-direction: column !important;
            justify-content: center !important;
            align-items: center !important;
          }
        }
      `}</style>

      {/* VIDEO MODAL */}
      <AnimatePresence>
        {expandedAppImage && (
          <Motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setExpandedAppImage(null)}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-bg-color/90 backdrop-blur-sm cursor-zoom-out"
          >
            <Motion.div
              initial={{ opacity: 0, scale: 0.8, filter: 'blur(10px)' }}
              animate={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
              exit={{ opacity: 0, scale: 0.8, filter: 'blur(10px)' }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="relative w-screen h-screen flex items-center justify-center"
              onClick={() => setExpandedAppImage(null)}
            >
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setExpandedAppImage(null);
                }}
                className="absolute top-8 right-8 p-3 transition-colors z-[110] bg-black/50 hover:bg-black backdrop-blur-md rounded-full text-white"
              >
                <X size={24} />
              </button>

              <img
                src={expandedAppImage.image}
                alt={`CRE8NOW: ${expandedAppImage.title}`}
                className="w-screen h-screen object-contain"
              />
            </Motion.div>
          </Motion.div>
        )}

        {expandedVideo && (
          <Motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setExpandedVideo(null)}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-bg-color/90 backdrop-blur-sm cursor-zoom-out"
          >
            <Motion.div
              initial={{ opacity: 0, scale: 0.8, filter: 'blur(10px)' }}
              animate={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
              exit={{ opacity: 0, scale: 0.8, filter: 'blur(10px)' }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="relative w-screen h-screen flex items-center justify-center"
              onClick={() => setExpandedVideo(null)}
            >
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  setExpandedVideo(null);
                }}
                className="absolute top-8 right-8 p-3 transition-colors z-[110] bg-black/50 hover:bg-black backdrop-blur-md rounded-full text-white"
              >
                <X size={24} />
              </button>
              
              <div className="w-full h-full flex items-center justify-center relative bg-black">
                <video
                  ref={videoRef}
                  src={expandedVideo}
                  autoPlay
                  loop
                  muted={isGlobalMuted}
                  playsInline
                  className="w-full h-full object-contain"
                />

                {/* Global Mute Toggle */}
                <div 
                  className="absolute bottom-12 left-1/2 -translate-x-1/2 z-[110]"
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    onClick={() => setIsGlobalMuted(!isGlobalMuted)}
                    className="w-16 h-16 rounded-full bg-black/40 backdrop-blur-xl border border-white/10 flex items-center justify-center text-white hover:bg-black/80 transition-all active:scale-95"
                  >
                    {isGlobalMuted ? <VolumeX size={28} /> : <Volume2 size={28} />}
                  </button>
                </div>
              </div>
            </Motion.div>
          </Motion.div>
        )}
      </AnimatePresence>
    </Motion.div>
  )}
</AnimatePresence>
  );
};

export default LandingPage;
